import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

type Language = 'en' | 'he';

interface TranslationContextType {
  t: (key: string, fallback?: string) => string;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    va_title: "Show as",
    va_viewing: "Viewing as",
    va_write_notice: "Preview changes what you see. Any changes you make still use your own admin access.",
    va_exit: "Back to my own access",
    va_change: "Change preview",
    va_close: "Close",
    va_retry: "Retry",
    va_loading: "Loading…",
    va_load_error: "Could not load preview choices.",
    va_by_role: "Find someone by role",
    va_role_explanation: "Opens a real person with this role, including their other roles, groups and assignments. This is not an isolated role simulation.",
    va_choose_site: "Choose a site to preview this role.",
    va_no_site_holder: "No active person holds this role on this site.",
    va_no_holder: "No active person holds this role.",
    va_site_role: "Site role",
    va_global_role: "Global role",
    va_via: "Preview through",
    va_by_person: "Choose a specific person",
    va_person_explanation: "See this person’s combined access across their sites and assignments.",
    va_search: "Search name or email…",
    va_no_people: "No matching people.",

    ug_create_a_site: "Create a site",
    ug_edit_site_details: "Edit site details",
    ug_delete_a_site: "Delete a site",
    ug_delete_a_model_file: "Delete a model file",
    ug_own_an_acc_connection: "Own an ACC connection",
    ug_unlink_retarget_a_model: "Unlink / retarget a model",
    ug_import_pois_from_csv: "Import POIs from CSV",
    ug_delete_pois: "Delete POIs",
    ug_re_file_pois_into_systems: "Re-file POIs into systems",
    ug_open_a_ticket: "Open a ticket",
    ug_assign_a_ticket: "Assign a ticket",
    ug_comment_on_a_ticket: "Comment on a ticket",
    ug_delete_a_ticket: "Delete a ticket",
    ug_reveal_a_device_ingest_token: "Reveal a device ingest token",
    ug_manage_crm_integrations: "Manage CRM integrations",
    ug_reveal_an_integration_token: "Reveal an integration token",
    ug_see_who_has_access: "See who has access",
    ug_add_a_user: "Add a user",
    ug_deactivate_reset_a_user: "Deactivate / reset a user",
    ug_delete_a_user: "Delete a user",
    ug_create_rename_delete_a_group: "Create / rename / delete a group",
    ug_run_the_onboarding_wizard: "Run the onboarding wizard",
    ug_invite_people_to_a_site: "Invite people to a site",
    ug_diagnostic_dumps: "Diagnostic dumps",
    ug_confirm_action: "Confirm action",
    ug_confirm: "Confirm",
    ug_name_activated: "{name} activated",
    ug_name_deactivated: "{name} deactivated",
    ug_administrator: "Administrator",
    ug_operator: "Operator",
    ug_editor: "Editor",
    ug_viewer: "Viewer",
    ug_site_administrator: "Site Administrator",
    ug_open_min_8: "(min 8)",
    ug_plus_add_assignment: "+ Add assignment",
    ug_plus_add_person: "+ Add person",
    ug_plus_create_group: "+ Create Group",
    ug_plus_create_user: "+ Create User",
    ug_a_temporary_password_was_emailed_to_the_account_holder_nobody_else_is_shown_it: "A temporary password was emailed to the account holder. Nobody else is shown it.",
    ug_abilities: "Abilities",
    ug_abilities_for_this_assignment: "Abilities for this assignment",
    ug_access_responsibility: "Access & responsibility",
    ug_access_inherited_from_a_group_is_managed_on_that_group_direct_assignments_can_be_changed_here: "Access inherited from a group is managed on that group. Direct assignments can be changed here.",
    ug_access_to_value0_revoked: "Access to {value0} revoked",
    ug_activate: "Activate",
    ug_add: "Add",
    ug_add_people: "Add people",
    ug_add_people_name: "Add people: {name}",
    ug_adding_a_person_gives_them_the_group_s_assignments_removing_them_removes_only_access_inherited_from_this_group: "Adding a person gives them the group’s assignments. Removing them removes only access inherited from this group.",
    ug_after_creating_the_account_add_them_to_a_group_or_assign_their_sites_systems_rooms_and_assets: "After creating the account, add them to a group or assign their sites, systems, rooms and assets.",
    ug_all_sites: "All sites",
    ug_allow: "Allow",
    ug_allow_or_withhold_specific_abilities_on_this_attachment_only: "Allow or withhold specific abilities on this attachment only",
    ug_asset: "Asset",
    ug_assets_open: "Assets (",
    ug_assign_access_and_responsibility: "Assign access and responsibility",
    ug_assign_name: "Assign {name}",
    ug_assignment_removed: "Assignment removed",
    ug_assignment_saved: "Assignment saved",
    ug_assignment_saved_review_the_access_warning_for_this_person: "Assignment saved. Review the access warning for this person.",
    ug_assignments: "Assignments",
    ug_attach: "Attach",
    ug_attached_to_no_room: "Attached to no room",
    ug_attached_to_no_system: "Attached to no system",
    ug_attached_to_nothing: "Attached to nothing",
    ug_autodesk_construction_cloud: "Autodesk Construction Cloud",
    ug_back_to_people: "Back to people",
    ug_cancel: "Cancel",
    ug_choose_a_room_ellipsis: "Choose a room…",
    ug_choose_a_site_ellipsis: "Choose a site…",
    ug_choose_a_system_ellipsis: "Choose a system…",
    ug_choose_an_asset_ellipsis: "Choose an asset…",
    ug_choose_what_to_attach: "Choose what to attach",
    ug_choose_where_this_person_or_group_belongs_then_choose_their_role_and_responsibility_check_that_they_also_have_access_to_the_site: "Choose where this person or group belongs, then choose their role and responsibility. Check that they also have access to the site.",
    ug_close: "Close",
    ug_could_not_load_abilities: "Could not load abilities",
    ug_could_not_save: "Could not save",
    ug_create_group: "Create group",
    ug_create_user: "Create user",
    ug_created_by_name: "Created by {name}",
    ug_deactivate: "Deactivate",
    ug_default: "Default",
    ug_delete: "Delete",
    ug_delete_value0_everyone_in_it_loses_what_the_group_gave_them: "Delete \"{value0}\"? Everyone in it loses what the group gave them.",
    ug_delete_value0_this_cannot_be_undone: "Delete {value0}? This cannot be undone.",
    ug_description: "Description",
    ug_diagrams: "Diagrams",
    ug_direct: "Direct",
    ug_done: "Done",
    ug_email_required: "Email *",
    ug_embed_partners: "Embed partners",
    ug_enter_a_group_name: "Enter a group name",
    ug_enter_a_name: "Enter a name",
    ug_equipment_register: "Equipment register",
    ug_failed_to_attach: "Failed to attach",
    ug_failed_to_change_group_membership: "Failed to change group membership",
    ug_failed_to_change_status: "Failed to change status",
    ug_failed_to_create_group: "Failed to create group",
    ug_failed_to_create_user: "Failed to create user",
    ug_failed_to_delete_group: "Failed to delete group",
    ug_failed_to_delete_user: "Failed to delete user",
    ug_failed_to_detach: "Failed to detach",
    ug_failed_to_grant_read: "Failed to grant read",
    ug_failed_to_load_available_sites_systems_rooms_or_assets: "Failed to load available sites, systems, rooms or assets",
    ug_failed_to_load_this_person: "Failed to load this person",
    ug_failed_to_load_users_and_groups: "Failed to load users and groups",
    ug_failed_to_remove_from_the_group: "Failed to remove from the group",
    ug_failed_to_rename_group: "Failed to rename group",
    ug_failed_to_reset_password: "Failed to reset password",
    ug_failed_to_revoke_site_access: "Failed to revoke site access",
    ug_files_models: "Files & models",
    ug_following_the_role: "Following the role",
    ug_full_access: "Full access",
    ug_full_name: "Full name",
    ug_grant_read_access_to_site: "Grant read access to {site}",
    ug_granted_by_the_group_change_it_on_the_group: "Granted by the group — change it on the group",
    ug_granted_from_the_responsibility_gap_warning: "Granted from the responsibility-gap warning",
    ug_group_created: "Group created",
    ug_group_deleted: "Group deleted",
    ug_group_name_required: "Group name *",
    ug_group_renamed: "Group renamed",
    ug_groups: "Groups",
    ug_groups_open: "Groups (",
    ug_in_no_group: "In no group",
    ug_includes_child_items: "Includes child items",
    ug_integrations: "Integrations",
    ug_iot: "IoT",
    ug_join: "Join",
    ug_leave: "Leave",
    ug_loading_rooms_ellipsis: "Loading rooms…",
    ug_loading_systems_ellipsis: "Loading systems…",
    ug_loading_users: "Loading users...",
    ug_loading_ellipsis: "Loading…",
    ug_manage_groups: "Manage groups",
    ug_manage_groups_name: "Manage groups: {name}",
    ug_manage_members_and_assignments_together_group_assignments_are_inherited_by_all_members_roles_and_responsibilities_determine_what_they_can_do: "Manage members and assignments together. Group assignments are inherited by all members; roles and responsibilities determine what they can do.",
    ug_manage_people_groups_and_their_access_across_all_sites: "Manage people, groups and their access across all sites.",
    ug_member: "member",
    ug_member_since_date: "Member since {date}",
    ug_membership_only: "Membership only",
    ug_name_required: "Name *",
    ug_no_duty: "No duty",
    ug_no_groups: "No groups",
    ug_no_groups_to_join_yet: "No groups to join yet",
    ug_no_groups_yet_create_a_group_to_manage_people_and_their_assignments_together: "No groups yet. Create a group to manage people and their assignments together.",
    ug_no_matching_groups: "No matching groups",
    ug_no_roles: "No roles",
    ug_no_rooms: "No rooms",
    ug_no_site_access: "No site access",
    ug_no_sites_assigned: "No sites assigned",
    ug_no_systems: "No systems",
    ug_no_users_found: "No users found",
    ug_nobody_yet: "Nobody yet",
    ug_onboarding: "Onboarding",
    ug_open: "Open",
    ug_optional: "Optional",
    ug_organization: "Organization",
    ug_outside_the_sites_you_administer: "Outside the sites you administer",
    ug_owner: "Owner",
    ug_password_required: "Password *",
    ug_password_reset: "Password reset",
    ug_external_apps_that_embed_the_viewer_site_grants_decide_which_sites_a_partner_can_show_allowed_origins_decide_where_their_frames_and_api_calls_may_come_from: "External apps that embed the viewer. Site grants decide which sites a partner can show; allowed origins decide where their frames and API calls may come from.",
    ug_new_partner_name: "New partner name",
    ug_create_partner: "Create partner",
    ug_no_embed_partners_yet_create_one_to_give_an_external_app_access_to_a_site: "No embed partners yet. Create one to give an external app access to a site.",
    ug_disabled: "Disabled",
    ug_disable: "Disable",
    ug_enable: "Enable",
    ug_api_key_shown_once_copy_it_now_and_send_it_to_the_partner_securely: "API key, shown once. Copy it now and send it to the partner securely.",
    ug_copy: "Copy",
    ug_dismiss: "Dismiss",
    ug_api_keys: "API keys",
    ug_no_keys: "No keys.",
    ug_created_date: "created {date}",
    ug_revoked: "Revoked",
    ug_last_used_date: "last used {date}",
    ug_never_used: "never used",
    ug_mint_new_key: "Mint new key",
    ug_site_grants: "Site grants",
    ug_no_sites_granted: "No sites granted.",
    ug_site_to_grant: "Site to grant",
    ug_choose_a_site_to_grant_ellipsis: "Choose a site to grant…",
    ug_grant: "Grant",
    ug_allowed_origins: "Allowed origins",
    ug_no_origins_yet: "No origins yet.",
    ug_allowed_origin: "Allowed origin",
    ug_failed_to_load_embed_partners: "Failed to load embed partners",
    ug_operation_failed: "Operation failed",
    ug_password_reset_name: "Password reset: {name}",
    ug_people: "People",
    ug_people_and_groups: "People and groups",
    ug_platform: "Platform",
    ug_platform_admins_only: "Platform admins only",
    ug_pick_a_role_or_a_duty: "Pick a role or a duty.",
    ug_this_person_can_t_open_this_site_yet_give_them_a_role_on_the_site_first_then_a_duty_or_an_assignment_inside_it: "This person can't open this site yet. Give them a role on the site first, then a duty or an assignment inside it.",
    ug_this_group_isn_t_on_this_site_yet_add_the_group_to_the_site_first_then_a_duty_or_an_assignment_inside_it: "This group isn't on this site yet. Add the group to the site first, then a duty or an assignment inside it.",
    ug_this_group_also_opens_sites_you_don_t_manage_only_a_platform_admin_can_change_who_is_in_it: "This group also opens sites you don't manage. Only a platform admin can change who is in it.",
    ug_same_or_higher_role_than_yours_only_a_platform_admin_can_do_this: "Same or higher role than yours. Only a platform admin can do this.",
    ug_this_person_also_uses_sites_you_don_t_manage_only_a_platform_admin_can_do_this_you_can_still_remove_their_roles_on_your_sites: "This person also uses sites you don't manage. Only a platform admin can do this. You can still remove their roles on your sites.",
    ug_this_person_s_autodesk_sign_in_opens_models_on_sites_in_mabat_connect_autodesk_with_another_account_on_those_sites_first_or_deactivate_this_person_instead: "This person's Autodesk sign-in opens models on sites in Mabat. Connect Autodesk with another account on those sites first, or deactivate this person instead.",
    ug_this_person_started_a_site_onboarding_so_they_cannot_be_deleted_deactivate_them_instead: "This person started a site onboarding, so they cannot be deleted. Deactivate them instead.",
    ug_other_records_still_point_to_this_person_so_they_cannot_be_deleted_deactivate_them_instead: "Other records still point to this person, so they cannot be deleted. Deactivate them instead.",
    ug_read_granted_on_value0: "Read granted on {value0}",
    ug_read_only: "Read only",
    ug_remove: "Remove",
    ug_remove_assignment: "Remove assignment",
    ug_remove_the_assignment: "Remove the assignment",
    ug_remove_value0_from_value1: "Remove {value0} from {value1}?",
    ug_rename: "Rename",
    ug_rename_group: "Rename group",
    ug_reset_password: "Reset password",
    ug_reset_the_password_for_value0_a_temporary_password_will_be_emailed_to_them: "Reset the password for {value0}? A temporary password will be emailed to them.",
    ug_responder: "Responder",
    ug_responsibility: "Responsibility",
    ug_retry: "Retry",
    ug_revoke: "Revoke",
    ug_revoke_access_to_value0: "Revoke access to {value0}?",
    ug_role_for_this_assignment: "Role for this assignment",
    ug_room: "Room",
    ug_rooms_open: "Rooms (",
    ug_save: "Save",
    ug_saving_changes_ellipsis: "Saving changes…",
    ug_saving_ellipsis: "Saving…",
    ug_search_assets_by_name_ellipsis: "Search assets by name…",
    ug_search_groups: "Search groups",
    ug_search_groups_by_name_or_description_ellipsis: "Search groups by name or description…",
    ug_search_name_email_group_ellipsis: "Search name, email, group…",
    ug_search_people: "Search people",
    ug_search_people_ellipsis: "Search people…",
    ug_select_a_person: "Select a person",
    ug_select_a_person_to_see_what_they_are_attached_to: "Select a person to see what they are attached to",
    ug_service_calls: "Service calls",
    ug_sign_in: "Sign-in",
    ug_site: "Site",
    ug_site_membership_managed_on_the_group_s_sites: "Site membership — managed on the group's sites",
    ug_sites_open: "Sites (",
    ug_some_people_could_not_be_added_retry_adds_only_the_remaining_people: "Some people could not be added. Retry adds only the remaining people.",
    ug_spatial_tree: "Spatial tree",
    ug_system: "System",
    ug_systems_open: "Systems (",
    ug_these_changes_apply_only_to_this_assignment_access_granted_elsewhere_is_unchanged: "These changes apply only to this assignment. Access granted elsewhere is unchanged.",
    ug_type_to_search_assets_ellipsis: "Type to search assets…",
    ug_unknown: "Unknown",
    ug_users: "Users",
    ug_via_group: "Via {group}",
    ug_watcher: "Watcher",
    ug_what: "What",
    ug_withhold: "Withhold",
    ug_you: "You",
    ug_you_manage_access_for_sites_people_outside_these_sites_are_read_only: "You manage access for: {sites}. People outside these sites are read-only.",
    ug_a_group: "a group",
    ug_admin: "Admin",
    ug_e_g_contractors: "e.g. Contractors",
    ug_inactive: "inactive",
    ug_no_access: "no access",
    ug_other_site: "other site",
    ug_not_on_your_sites: "Not on your sites",
    ug_site_required: "Site *",
    ug_no_site_yet: "No site yet",
    ug_they_start_on_this_site_as_a_user: "They start on this site as a User.",
    ug_choose_the_site_this_person_starts_on: "Choose the site this person starts on",
    ug_you_can_only_add_people_to_your_own_sites: "You can only add people to your own sites",
    ug_that_site_does_not_exist: "That site does not exist",
    ug_site_member: "site member",
    ug_count_ability_changes: "{count} ability changes",
    ug_count_ability_changes_saved: "{count} ability changes saved",
    ug_count_access_gaps: "{count} access gaps",
    ug_count_asset: "{count} asset",
    ug_count_assets: "{count} assets",
    ug_count_group: "{count} group",
    ug_count_groups: "{count} groups",
    ug_count_other_abilities_are_controlled_by_the_role_and_cannot_be_changed_here: "{count} other abilities are controlled by the role and cannot be changed here.",
    ug_count_room: "{count} room",
    ug_count_rooms: "{count} rooms",
    ug_count_site: "{count} site",
    ug_count_sites: "{count} sites",
    ug_count_system: "{count} system",
    ug_count_systems: "{count} systems",
    ug_name_is_responsible_for_place_but_cannot_open_its_site_grant_site_access_or_remove_the_assignment: "{name} is responsible for {place}, but cannot open its site. Grant site access or remove the assignment.",
    ug_people_people_assignments_assignments: "{people} people · {assignments} assignments",
    ug_people_people_groups_groups: "{people} people · {groups} groups",
    ug_value0_added_to_value1: "{value0} added to {value1}",
    ug_value0_created: "{value0} created",
    ug_value0_deleted: "{value0} deleted",
    ug_value0_for_this_assignment: "{value0} for this assignment",
    ug_value0_removed_from_value1: "{value0} removed from {value1}",
    ug_responsibility_gap: "⚠ responsibility gap",
    ug_email_already_exists: "Email already exists",
    ug_email_name_and_password_are_required: "Email, name, and password are required",
    ug_enter_a_valid_email_address: "Enter a valid email address",
    ug_password_must_contain_at_least_8_characters: "Password must contain at least 8 characters",
    ug_you_cannot_manage_a_user_with_equal_or_higher_privileges: "You cannot manage a user with equal or higher privileges",
    ug_site_admin: "Site Admin",
    ug_data_creator: "Data Creator",
    ug_user: "User",
    ug_no_people_available_to_add: "No people available to add",
    dt_assets_unassignedSystem: "Unassigned",
    dt_tickets_matching: "matching tickets",
    dt_tickets_filters: "Filters",
    dt_tickets_applyFilters: "Show tickets",
    dt_tickets_clearFilters: "Clear filters",
    dt_tickets_outsideQueue: "Outside this page",
    dt_tickets_onPage: "on this page",
    dt_tickets_closeDetails: "Close ticket details",
    dt_tickets_activity: "Activity",
    dt_tickets_connections: "Connections",
    dt_tickets_files: "Files",
    dt_tickets_moreActions: "More",
    dt_tickets_locate: "Locate in 3D",
    dt_tickets_editDetails: "Edit details",
    dt_tickets_details: "Ticket details",
    dt_tickets_noDescription: "No description provided.",
    dt_tickets_action_in_progress: "Start work",
    dt_tickets_action_resolved: "Resolve ticket",
    dt_tickets_action_closed: "Close ticket",
    dt_tickets_action_cancelled: "Cancel ticket",
    dt_tickets_action_open: "Reopen ticket",

    headerSearch: 'Search assets and rooms',
    headerSelectSite: 'Select a site to search',
    headerThisSite: 'This site',
    headerSearching: 'Searching…',
    headerNoResults: 'No results found',
    headerSearchError: 'Search failed. Change your search to try again.',
    headerLanguage: 'Language',
    headerAccount: 'Account',
    headerLogout: 'Logout',
    headerSwitchSite: 'Switch site',
    headerSites: 'Sites',
    headerFindSite: 'Find a site',
    headerManageSites: 'Manage sites',
    navHintWalk: 'Walk — drag to look · W A S D to move · wheel to step · right-drag to slide',
    navHintOrbit: 'Orbit — drag to rotate around what you grab · wheel to zoom · right-drag to pan',
    ctrlHelpTitle: 'Controls',
    ctrlHelpWalk: 'Walk',
    ctrlHelpOrbit: 'Orbit',
    ctrlHelpCurrent: 'now',
    ctrlHelpSwitch: 'Switch with Walk / Orbit in the top bar. 🏠 takes you to your Home view.',
    homeGo: 'Home',
    homeGoSaved: 'Home — your saved view',
    homeGoAuto: 'Home (automatic) — set yours with ▾',
    homeMenu: 'Home options',
    homeSet: 'Set current view as my Home',
    homeReset: 'Reset to automatic',
    homeSetDone: 'Home set for this site',
    homeResetDone: 'Home reset to automatic',
    ctrlHelp_walkDrag_gesture: 'Drag',
    ctrlHelp_walkDrag: 'Look around',
    ctrlHelp_walkKeys_gesture: 'W A S D',
    ctrlHelp_walkKeys: 'Walk',
    ctrlHelp_walkWheel_gesture: 'Wheel',
    ctrlHelp_walkWheel: 'Step forward / back',
    ctrlHelp_walkRight_gesture: 'Right-drag',
    ctrlHelp_walkRight: 'Slide sideways',
    ctrlHelp_walkQE_gesture: 'Q / E',
    ctrlHelp_walkQE: 'Down / up',
    ctrlHelp_walkShift_gesture: 'Shift',
    ctrlHelp_walkShift: 'Faster',
    ctrlHelp_orbitDrag_gesture: 'Drag',
    ctrlHelp_orbitDrag: 'Rotate around the point you press on',
    ctrlHelp_orbitWheel_gesture: 'Wheel',
    ctrlHelp_orbitWheel: 'Zoom',
    ctrlHelp_orbitRight_gesture: 'Right-drag',
    ctrlHelp_orbitRight: 'Pan',
    ctrlHelpPanorama: 'Panorama',
    ctrlHelp_panoDrag_gesture: 'Drag',
    ctrlHelp_panoDrag: 'Look around',
    ctrlHelp_panoWheel_gesture: 'Wheel',
    ctrlHelp_panoWheel: 'Zoom',
    ctrlHelp_panoClick_gesture: 'Click 🌐',
    ctrlHelp_panoClick: 'Go to that panorama',
    ctrlHelp_panoFloor_gesture: 'Floor − / +',
    ctrlHelp_panoFloor: 'Another floor',
    ctrlHelp_panoOverlay_gesture: '3D button',
    ctrlHelp_panoOverlay: 'Show the model over the photo',
    ctrlHelpPanoFoot: 'Search, Measure and Planning work on the photo too.',
    ctrlHelpPlan: '2D plan',
    ctrlHelp_planDrag_gesture: 'Drag',
    ctrlHelp_planDrag: 'Move the plan',
    ctrlHelp_planWheel_gesture: 'Wheel',
    ctrlHelp_planWheel: 'Zoom',
    ctrlHelp_planClick_gesture: 'Click 🌐',
    ctrlHelp_planClick: 'Open that panorama',
    ctrlHelp_planAsset_gesture: 'Click an asset',
    ctrlHelp_planAsset: 'Open its details',
    ctrlHelp_planFloor_gesture: 'Floor − / +',
    ctrlHelp_planFloor: 'Another floor',
    ctrlHelpPlanFoot: 'Search, Measure and Planning work on the plan too.',
    measureTitle_distance: 'Distance',
    measureTitle_angle: 'Angle',
    measureTitle_area: 'Area',
    measureHow_distance: 'Click two points.',
    measureHow_angle: 'Click three points.',
    measureHow_area: 'Click points.',
    measureDragKey: 'Drag',
    measureDrag: 'Turn the view',
    measureDelete: 'Remove the selected measurement',
    measureEsc: 'Stop measuring (clears all)',
    measureFromModel: 'Sizes come from the 3D model behind the photo.',
    measureHowPlan_area: 'Click points; click the first point again to finish.',
    measureDragPlan: 'Move the plan',
    measureDeleteLast: 'Remove the last measurement',
    sitePlanReset: 'Reset position',
    sitePlanOnFloor: 'Floor',
    sitePlanKindFloor: 'Floor plan',
    sitePlanKindCeiling: 'Ceiling plan',
    sitePlanAdjust: 'Adjust the height',
    sitePlanAdjustHint: 'up (+) or down (−) from where its floor puts it',
    sitePlanPosition: 'Position',
    sitePlanMovedByHand: 'Moved by hand',
    sitePlanBringHere: 'Bring it here',
    sitePlanAlignTwo: 'Align by 2 points',
    sitePlanRemoveConfirm: 'Remove this plan? Its file stays in the site files.',
    planningGoTo3D: 'Show it in 3D',
    sitePlanMoveHint: 'Move it on the view: drag inside its frame · the round handle turns it · a corner resizes it',
    sitePlanAlignPlan1Flat: 'Click a recognisable point on YOUR DRAWING',
    sitePlanAlignModel1Flat: 'Now click that same point on the FLOOR PLAN under it',
    sitePlanAlignPlan2Flat: 'Click a SECOND point on your drawing, far from the first',
    sitePlanAlignModel2Flat: 'And that second point on the floor plan',
    headerNoSitesFound: 'No sites found',
    headerUntitledAsset: 'Untitled asset',
    headerAssetFallback: 'Asset',
    headerRoleAdmin: 'Administrator',
    headerRoleSiteAdmin: 'Site admin',
    headerRoleDataCreator: 'Data creator',
    headerRoleUser: 'User',

    planningCm: 'cm',
    planningTool: "Planning",
    viewerBrowser: "Browser",
    planningWorkspace: "Planning workspace",
    planningMyObjects: "My planning objects",
    planningPrivate: "Private to you · planning shapes",
    planningSelected: "Selected planning object",
    planningLayerHidden: "Planning objects are hidden.",
    planningPlansHidden: "Plans are hidden.",
    planningShowLayer: "Show layer",
    planningCloseHint: "Closing this panel keeps visible layers on.",
    planningPlansVisibility: "Show plans",
    planningOpenPlans: "Open plan controls",

    dt_element_element: "Element",
    dt_element_modelElement: "Model element",
    dt_element_asset: "Asset",
    dt_element_noAsset: "No asset yet",
    dt_element_location: "Location",
    dt_element_system: "System",
    dt_element_unassigned: "Not assigned",
    dt_element_properties: "Model properties",
    dt_element_groups: "groups",
    dt_element_values: "values",
    dt_element_search: "Search names or values…",
    dt_element_noMatches: "No matching properties.",
    dt_element_noProperties: "No properties available in the current view.",
    dt_element_createHint: "Create an asset to link tickets, rooms and systems.",
    dt_element_modelOnlyHint: "This model element is not linked to an asset.",
    dt_element_more: "More actions",
    dt_element_assetActions: "Asset actions",
    dt_element_close: "Close panel",
    dt_element_drag: "Drag to reposition",
    dt_element_canvasAvailable: "Model stays interactive",
    dt_element_creating: "Creating…",
    dt_element_yes: "Yes",
    dt_element_no: "No",
    dt_element_action_focus: "Show in 3D",
    dt_element_action_copyId: "Copy element ID",
    dt_element_action_createAsset: "Create asset",
    dt_element_action_createAssetAndAddIot: "Add IoT (adds asset)",
    dt_element_action_createAssetAndTicket: "Create ticket (adds asset)",
    dt_element_action_createTicket: "Create ticket",
    dt_element_action_viewTickets: "View tickets",
    dt_element_action_addIot: "Add IoT device",
    dt_element_action_showInDiagram: "System diagram",
    dt_element_action_editAsset: "Edit asset",
    dt_element_action_deleteAsset: "Delete asset",
    dt_tickets_issueType: "Issue type",
    dt_tickets_unclassified: "Unclassified",
    dt_issue_incident: "Incident / fault",
    dt_issue_maintenance: "Maintenance",
    dt_issue_inspection: "Inspection",
    dt_issue_request: "Service request",
    dt_issue_other: "Other",
    dt_tickets_tags: "Tags",
    dt_tickets_tagsPlaceholder: "e.g. recurring, safety",
    dt_tickets_tagsHelp: "Separate tags with commas · Up to 12 tags, 40 characters each.",
    dt_tickets_existingTags: "Existing tags",
    dt_tickets_retryTags: "Could not load tag suggestions · Retry",
    dt_tickets_related: "Related tickets",
    dt_tickets_linkTicket: "Link ticket",
    dt_tickets_unlinkTicket: "Unlink",
    dt_tickets_relatedHelp: "Links appear on both tickets. Status and assignment stay independent.",
    dt_tickets_searchRelated: "Search tickets by title or ID",
    dt_tickets_relatedLoadFailed: "Could not load tickets.",
    dt_tickets_relatedFailed: "Could not update the ticket link.",
    dt_tickets_classification: "Classification",
    dt_tickets_editClassification: "Edit classification",
    dt_tickets_classificationFailed: "Could not save classification.",
    dt_tickets_removeTag: "Remove tag",
    dt_tickets_noTags: "No tags",
    dt_tickets_allTypes: "All issue types",
    dt_tickets_tagFilter: "Filter by tag",
    dt_ev_ticketLinked: "linked ticket",
    dt_ev_ticketUnlinked: "unlinked ticket",
    dt_tickets_moreFilters: 'More filters',
    dt_tickets_equipmentError: 'Could not load equipment.',
    dt_tickets_autoAssign: 'Assign automatically',
    dt_tickets_leaveUnassigned: 'Leave unassigned',
    dt_tickets_queue: 'Assigned to',
    dt_tickets_everyone: 'Everyone',
    dt_tickets_mine: 'Mine',
    dt_tickets_myTeams: 'My teams',
    dt_tickets_sort: 'Sort by',
    dt_tickets_newest: 'Newest first',
    dt_tickets_dueFirst: 'Due date',
    dt_tickets_assetId: 'Equipment',
    dt_tickets_back: 'Back to tickets',
    dt_tickets_previous: 'Previous',
    dt_tickets_next: 'Next',
    dt_tickets_retry: 'Retry',
    dt_tickets_scopeError: 'Could not load rooms and systems.',
    dt_tickets_narrowSearch: 'More matches available. Refine your search.',
    dt_tickets_contextHint: 'Equipment memberships provide context; explicitly linked rooms and systems describe the affected scope.',
    dt_tickets_siteUnavailable: 'The ticket’s site is unavailable.',
    dt_tickets_filterScope: 'Room or system',
    dt_tickets_filterEquipment: 'Search equipment…',
    dt_tickets_assignmentHint: 'Automatic assignment uses the originating equipment, otherwise the first equipment selected, otherwise the selected room or system.',
    // App - General
    mabat3dPortal: 'Mabat3D Portal',
    dashboard: 'Dashboard',
    tickets: 'Tickets',
    viewer: 'Viewer',
    assets: 'Assets',
    sites: 'Sites',
    systems: 'Systems',
    locations: 'Locations',
    users: 'Users',
    settings: 'Settings',
    onboarding: 'Onboarding',
    documentation: 'Documentation',
    search: 'Search',
    searchPlaceholder: 'Search assets, rooms, sites...',
    noAssetSelected: 'No site selected',
    noViewerFilesAvailable: 'No Viewer Files Available',
    noViewerFilesMessage: 'doesn\'t have any files that can be viewed in the 3D/2D/Panorama viewers.',
    upload: 'Upload',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',

    // Sites page chrome (F-6)
    sitesLoadingAssets: 'Loading assets...',
    fileBadgeDefault: 'Default',
    fileBadgeLocked: 'Locked',
    fileBadgeLockedTitle: 'Auto-sync disabled - locked to specific version',
    fileBadgePaused: 'Paused',
    fileBadgePausedTitle: 'Auto-sync is paused',
    noIfcModelsUploaded: 'No IFC models uploaded',
    noModelsUploaded: 'No models uploaded',

    // POI Table chrome (F-6)
    poiSearchPlaceholder: 'Search...',
    poiDeleteSelectedTitle: 'Delete selected assets',
    poiDeleteAllTitle: 'Delete all assets in this site',
    poiClosePanel: 'Close panel',
    poiSelectColumns: 'Select Columns',
    poiSearchFields: 'Search fields...',
    poiSearchColumns: 'Search columns...',
    poiValuePlaceholder: 'Value...',
    poiActions: 'Actions',
    poiDeleteAll: 'Delete All',
    poiDeleteAllConfirmTitle: 'Delete All Assets?',
    poiDeleteSelectedConfirmTitle: 'Delete Selected Assets?',
    poiDeleting: 'Deleting...',
    poiApply: 'Apply',
    poiApplying: 'Applying...',
    poiSetSystems: 'Set Systems',
    poiSetLocations: 'Set Locations',


    // Help Section
    hi: 'Hi',
    needHelp: 'Need help?',
    pleaseCheckDocs: 'Please check our docs',

    // Login Page
    loginTitle: 'Mabat3D Portal',
    loginSubtitle: 'Sign in to your account',
    emailAddress: 'Email Address',
    password: 'Password',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    signingIn: 'Signing in...',
    signIn: 'Sign In',
    pleaseEnterBoth: 'Please enter both email and password',
    loginFailed: 'Login failed. Please check your credentials.',
    // Forgot password (2026-09-28)
    forgotPassword: 'Forgot password?',
    forgotTitle: 'Reset your password',
    forgotSubtitle: "Enter your account's email and we'll send you a link to choose a new password.",
    sendResetLink: 'Send reset link',
    sendingResetLink: 'Sending...',
    forgotSent: 'If an account exists for that email, we sent a link to reset the password. The link works for 30 minutes.',
    backToSignIn: 'Back to sign in',
    enterValidEmail: 'Please enter a valid email address',
    resetUnavailable: 'Password reset by email is not available yet. Please contact your administrator.',
    tooManyRequests: 'Too many requests. Please try again in a few minutes.',
    newPasswordTitle: 'Choose a new password',
    savePassword: 'Save password',
    savingPassword: 'Saving...',
    resetLinkInvalid: 'This link is invalid or has expired. Request a new one.',
    requestNewLink: 'Request a new link',
    passwordUpdated: 'Password updated. You can sign in now.',
    resetFailed: 'Something went wrong. Please try again.',

    // Settings Panel - Profile
    profileInfo: 'Profile Information',
    editProfile: 'Edit Profile',
    saving: 'Saving...',
    saveChanges: 'Save Changes',
    profileUpdatedSuccess: 'Profile updated successfully!',
    profileUpdateFailed: 'Failed to update profile',
    name: 'Name',
    fullName: 'Full Name',
    email: 'Email',
    organization: 'Organization',
    yourOrganizationName: 'Your organization name',
    role: 'Role',

    // Settings Panel - Password
    passwordSecurity: 'Password Security',
    keepAccountSecure: 'Keep your account secure by using a strong, unique password.',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    enterNewPassword: 'Enter new password (min 8 characters)',
    enterCurrentPassword: 'Enter current password',
    reenterNewPassword: 'Re-enter new password',
    confirmPassword: 'Confirm new password',
    passwordsDoNotMatch: 'Passwords do not match',
    allFieldsRequired: 'All password fields are required',
    newPasswordsDoNotMatch: 'New passwords do not match',
    passwordMinLength: 'New password must be at least 8 characters long',
    passwordTooWeak: 'Password is too weak.',
    passwordChangedSuccess: 'Password changed successfully!',
    passwordChangeFailed: 'Failed to change password',
    changing: 'Changing...',

    // Locations Page
    noAssetSelectedMessage: 'No site selected. Please select a site from the Sites page.',
    noSiteSelectedMessage: 'No site selected. Please select a site from the Sites page.',
    failedToFetchLocations: 'Failed to fetch location tree',
    addLocation: 'Add Location',
    locationName: 'Location Name',
    locationType: 'Location Type',
    building: 'Building',
    buildingStorey: 'Building Storey',
    space: 'Space',
    searchLocations: 'Search in locations...',
    createLocation: 'Create Location',
    deleteAll: 'Delete All',
    retry: 'Retry',
    searchPOIs: 'Search assets...',

    // Systems Page - Categories
    categoryStructure: 'Structural elements: walls, beams, columns, slabs',
    categoryArchitectural: 'Architectural elements: doors, windows, stairs',
    categoryHVAC: 'HVAC systems: ducts, air terminals, boilers',
    categoryElectrical: 'Electrical systems: cables, lighting, switches',
    categoryPlumbing: 'Plumbing systems: pipes, fixtures, valves',
    categoryFireSafety: 'Fire safety: sprinklers, alarms',
    categoryMechanical: 'Mechanical equipment: sensors, controllers',
    categoryFurniture: 'Furniture and furnishing elements',
    categorySpatial: 'Spatial elements: rooms, spaces',
    categoryOther: 'Other building elements',

    // Revit Categories
    categoryAudioVisual: 'Audio Visual Devices',
    categoryCommunication: 'Communication Devices',
    categoryData: 'Data Devices',
    categoryElectricalEquipment: 'Electrical Equipment',
    categoryElectricalFixtures: 'Electrical Fixtures',
    categoryLightingDevices: 'Lighting Devices',
    categoryLightingFixtures: 'Lighting Fixtures',
    categoryNurseCall: 'Nurse Call Devices',
    categorySecurity: 'Security Devices',
    categoryTelephone: 'Telephone Devices',
    categoryFireAlarm: 'Fire Alarm Devices',
    categorySprinklers: 'Sprinklers',
    categoryPlumbingFixtures: 'Plumbing Fixtures',
    categoryAirTerminals: 'Air Terminals',
    categoryDuctAccessory: 'Duct Accessories',
    categoryDuctFitting: 'Duct Fittings',
    categoryFlexDuct: 'Flex Ducts',
    categoryMechanicalEquipment: 'Mechanical Equipment',
    categoryPipeFitting: 'Pipe Fittings',
    categoryPipeAccessory: 'Pipe Accessories',
    categoryWalls: 'Walls',
    categoryFloors: 'Floors',
    categoryCeilings: 'Ceilings',
    categoryRoofs: 'Roofs',
    categoryDoors: 'Doors',
    categoryWindows: 'Windows',
    categoryStairs: 'Stairs',
    categoryRailings: 'Railings',
    categoryColumns: 'Columns',
    categoryBeams: 'Structural Framing',
    categoryFoundations: 'Structural Foundations',
    categoryGenericModels: 'Generic Models',
    categorySpecialtyEquipment: 'Specialty Equipment',
    categoryCasework: 'Casework',
    categoryRooms: 'Rooms',

    // View modes
    view3D: '3D',
    view2D: '2D',
    viewPanorama: 'Panorama',
    map2D: '2D Map',
    closeMinimap: 'Close map',
    minimapOrbitNote: 'No minimap in Orbit mode',

    // User management
    userManagement: 'User Management',
    manageUsersRolesPermissions: 'Manage users, roles, and permissions',
    createUser: 'Create User',
    addUser: 'Add User',
    deleteUser: 'Delete User',
    userRole: 'User Role',
    admin: 'Admin',
    assetManager: 'Site Manager',
    roleViewer: 'Viewer', // the RBAC ROLE, distinct from the `viewer` nav tab above.
                      // Both were spelled `viewer`, so this one won and the nav tab
                      // read the role name. Harmless in English (identical words),
                      // a visible mislabel in Hebrew ('צפיין' a viewer vs 'צפייה' viewing).
    searchUsers: 'Search users by name or email...',
    noUsersFound: 'No users found',
    active: 'Active',
    inactive: 'Inactive',
    manageGroups: 'Manage Groups',
    resetPassword: 'Reset Password',
    deactivate: 'Deactivate',
    activate: 'Activate',
    assetGroups: 'Site Groups',
    organizeAssetsGroups: 'Organize sites into groups for easier permission management',
    createGroup: 'Create Group',
    noAssetGroupsYet: 'No site groups yet. Create one to organize sites and manage permissions efficiently.',
    manageAssets: 'Manage Sites',
    manageUsers: 'Manage Users',
    createNewUser: 'Create New User',
    passwordResetSuccessful: 'Password Reset Successful',
    passwordResetFor: 'Password has been reset for:',
    temporaryPassword: 'Temporary Password:',
    importantSharePassword: 'Please share this password securely with the user. They should change it immediately after logging in.',
    copyToClipboard: 'Copy to Clipboard',
    manageGroupsFor: 'Manage Groups:',
    loadingPermissions: 'Loading permissions...',
    assetGroupPermissions: 'Site Group Permissions',
    groupName: 'Group Name',
    permission: 'Permission',
    grantedBy: 'Granted By',
    actions: 'Actions',
    readOnly: 'Read Only',
    fullAccess: 'Full Access',
    revoke: 'Revoke',
    noGroupPermissionsYet: 'No group permissions assigned yet',
    grantNewPermission: 'Grant New Permission',
    grantPermissionTo: 'Grant Permission to',
    selectAssetGroup: 'Select Site Group',
    chooseGroup: 'Choose a group...',
    permissionLevel: 'Permission Level',
    userCanViewOnly: 'User can view but cannot modify or delete',
    userCanEdit: 'User can view, edit, and delete',
    grantPermission: 'Grant Permission',
    assetsInGroup: 'Sites in Group',
    noAssetsInGroup: 'No sites in this group yet',
    addAssetsToGroup: 'Add Sites to Group',
    allAssetsInGroup: 'All available sites are already in this group',
    remove: 'Remove',
    add: 'Add',
    createAssetGroup: 'Create Site Group',
    groupNameRequired: 'Group Name',
    noDescription: 'No description',
    usersWithAccess: 'Users with Access',
    noUsersHaveAccess: 'No users have access to this group yet',
    addUsersToGroup: 'Add Users to Group',
    allUsersHaveAccess: 'All users already have access to this group',
    minimumCharacters: 'Minimum 8 characters',
    assignToGroup: 'Assign to Group (Optional)',
    noGroupAssignment: 'No group assignment',

    // Language
    language: 'Language',
    english: 'English',
    hebrew: 'Hebrew',

    // Theme
    theme: 'Theme',
    lightTheme: 'Light',
    darkTheme: 'Dark',

    // Sites page (formerly Assets)
    selectFiles: 'Select Files',
    dragDrop: 'or drag and drop files here',
    supportedFormats: 'Supported formats: IFC, PDF, E57, RCP, RCS, JPEG',
    noFiles: 'No files selected',
    uploadedFiles: 'Uploaded Files',
    dataDirectoryFiles: 'Data Directory Files',
    loadFromData: 'Load from Data',
    sitesManagement: 'Sites Management',
    searchSites: 'Search sites...',
    createSite: 'Create Site',
    currentSite: 'Current site',
    whatsNew_title: "What's new in Digital Twin",
    whatsNew_subtitle: 'September 2026 update',
    whatsNew_look: 'A new look',
    whatsNew_lookBody: 'The whole app is redesigned, in Hebrew and English.',
    whatsNew_viewer: 'Your site, inside the viewer',
    whatsNew_viewerBody: 'Floors, systems and assets in one Browser pane.',
    whatsNew_assets: 'Assets on the model',
    whatsNew_assetsBody: 'Flags by system in 3D, on the minimap and on panoramas.',
    whatsNew_tickets: 'Tickets from the model',
    whatsNew_ticketsBody: 'Open a ticket on any element, room or system, with photos.',
    whatsNew_systems: 'Systems as diagrams',
    whatsNew_systemsBody: 'Every system opens as a diagram you can explore.',
    whatsNew_plans: 'Plans and planning',
    whatsNew_plansBody: 'Lay a 2D plan over the model and check that new items fit.',
    whatsNew_dashboard: 'Dashboard',
    whatsNew_dashboardBody: 'Activity, open tickets and device status in one place.',
    whatsNew_reopenHint: 'Open this again from your profile menu.',
    whatsNew_gotIt: 'Got it',
    whatsNew_menu: "What's new",
    noSitesYet: 'No sites yet',
    noSites: 'No sites found',
    assetsManagement: 'Assets Management',
    searchAssets: 'Search assets...',
    createAsset: 'Create Asset',
    assetsCount: 'Assets',
    noFilesUploaded: 'No files uploaded',
    created: 'Created',
    uploadFile: 'Upload File',
    description: 'Description',
    files: 'Files',
    file: 'File',
    size: 'Size',
    uploaded: 'Uploaded',
    status: 'Status',
    createNewSite: 'Create New Site',
    siteName: 'Site Name',
    siteDescription: 'Site Description',
    editSite: 'Edit Site',
    createNewAsset: 'Create New Asset',
    assetName: 'Asset Name',
    editAsset: 'Edit Asset',
    uploadFileTo: 'Upload File to',
    fileType: 'File Type',
    selectFile: 'Select File',
    deleteConfirm: 'Are you sure you want to delete this asset?',
    deleteFileConfirm: 'Are you sure you want to delete the {fileType} file?',
    enterAssetName: 'Please enter an asset name',
    uploadFailed: 'Upload failed',
    threeDModel: '3D Model (IFC)',
    twoDPlans: '2D Plans',
    panoramas: 'Panoramas (E57)',
    documents: 'Documents (PDF)',
    noFileUploaded: 'No {fileType} uploaded',
    panorama: 'Panorama',

    // Settings/Appearance
    settingsTitle: 'Settings',
    appearance: 'Appearance',
    themeMode: 'Theme Mode',
    currentlyUsing: 'Currently using',
    switchTo: 'Switch to',
    dark: 'Dark',
    light: 'Light',
    interfaceLanguage: 'Interface Language',
    current: 'Current',
    switchToLanguage: 'Switch to',

    // Documentation Page
    docSubtitle: 'Learn how to use Mabat3D Portal effectively',
    docOverview: 'Overview',
    docFeatures: 'Features',
    docFAQ: 'FAQ',
    docWelcome: 'Welcome to Mabat3D Portal',
    docWelcomeDesc: 'Mabat3D Portal is a comprehensive BIM (Building Information Modeling) viewer and management platform. It allows you to view, navigate, and manage 3D building models, 2D floor plans, panoramic imagery, and assets in an integrated environment.',
    docQuickStart3D: '3D BIM Viewer',
    docQuickStart3DDesc: 'View and navigate IFC models',
    docQuickStartPOI: 'Asset Management',
    docQuickStartPOIDesc: 'Track and manage assets',
    docQuickStartPanorama: 'Panorama Viewer',
    docQuickStartPanoramaDesc: 'Explore 360° imagery from E57 scans',
    docGettingStarted: 'Getting Started',
    docStep1: 'Go to the Sites page and select or create a site',
    docStep2: 'Upload your files (IFC for 3D, GEOTIFF for 2D, E57 for panoramas)',
    docStep3: 'Navigate to the Viewer page to explore your models',
    docStep4: 'Use the view mode buttons (3D/2D/Panorama) to switch between views',
    docStep5: 'Search for assets using the search bar in the header',

    // Documentation Features
    docFeature3DViewer: '3D BIM Viewer',
    docFeature3DViewerDesc: 'Interactive 3D viewer for IFC files with mouse and keyboard navigation. Supports multiple IFC files per site.',
    docFeature2DViewer: '2D Floor Plans',
    docFeature2DViewerDesc: 'View GEOTIFF floor plans with coordinate display. Click to sync camera position with 3D viewer.',
    docFeaturePanorama: 'Panorama Viewer',
    docFeaturePanoramaDesc: 'View 360° panoramic images extracted from E57 point cloud scans. Navigate between panorama positions.',
    docFeaturePOI: 'Assets',
    docFeaturePOIDesc: 'Import, view, and manage assets from CSV files. Search and navigate to asset locations in all viewers.',
    docFeatureAssets: 'Site Management',
    docFeatureAssetsDesc: 'Create, organize, and manage building sites with multiple file types. Track file counts and asset statistics.',
    docFeatureUsers: 'User Management',
    docFeatureUsersDesc: 'Manage users, roles (Admin, Site Manager, User), and permissions. Create site groups for access control.',
    docFeatureI18n: 'Multilingual Support',
    docFeatureI18nDesc: 'Full support for English and Hebrew with automatic RTL layout switching.',
    docFeatureTheme: 'Theme Customization',
    docFeatureThemeDesc: 'Switch between light and dark themes to match your preference.',

    // Documentation FAQ
    docFAQCategoryGeneral: 'General',
    docFAQCategoryViewer: 'Viewer',
    docFAQCategoryAssets: 'Sites',
    docFAQCategoryPOI: 'Assets',
    docFAQCategoryUsers: 'Users & Permissions',
    docFAQWhatIs: 'What is Mabat3D Portal?',
    docFAQWhatIsAnswer: 'Mabat3D Portal is a web-based BIM viewer and management platform that allows teams to view 3D building models, 2D floor plans, and panoramic imagery in one integrated environment.',
    docFAQFileFormats: 'What file formats are supported?',
    docFAQFileFormatsAnswer: 'Supported formats include: IFC (3D BIM models), GEOTIFF (2D floor plans), E57 (panoramic point clouds), CSV (asset data), DWG/DXF (CAD drawings).',
    docFAQHowToView3D: 'How do I view a 3D model?',
    docFAQHowToView3DAnswer: 'Select a site with an IFC file, go to the Viewer page, and ensure 3D mode is selected. The model will load automatically. Use mouse to rotate, scroll to zoom, and right-click to pan.',
    docFAQHowToNavigate: 'How do I navigate in the 3D viewer?',
    docFAQHowToNavigateAnswer: 'Left-click and drag to rotate the view. Scroll to zoom in/out. Right-click and drag to pan. You can also use the minimap in the corner to click on locations.',
    docFAQPanoramaSync: 'How does panorama sync work?',
    docFAQPanoramaSyncAnswer: 'When viewing panoramas, the minimap shows your position on the 2D floor plan. Click on different locations in the navigation panel or minimap to jump to that panorama position.',
    docFAQHowToUpload: 'How do I upload files to a site?',
    docFAQHowToUploadAnswer: 'Go to the Sites page, click on a site, then use the Upload button to add files. Select the file type (IFC, GEOTIFF, E57, etc.) and choose your file.',
    docFAQMultipleIFC: 'Can I have multiple IFC files in one site?',
    docFAQMultipleIFCAnswer: 'Yes! You can upload multiple IFC files to a single site. Use the "Set as Default" button to choose which IFC file loads by default in the viewer.',
    docFAQWhatArePOIs: 'What are Assets?',
    docFAQWhatArePOIsAnswer: 'Assets are specific locations in your building that you want to track, such as equipment, sensors, or maintenance points. They are imported via CSV files and can be viewed in Systems page.',
    docFAQHowToSearchPOIs: 'How do I search for assets?',
    docFAQHowToSearchPOIsAnswer: 'Use the search bar in the header to search for assets by name. Click on a result to navigate to that location in the viewer. You can also browse assets by category in the Systems page.',
    docFAQUserRoles: 'What are the different user roles?',
    docFAQUserRolesAnswer: 'Admin: Full access to all features and user management. Site Manager: Can manage sites and users within their groups. User: Can view sites they have been granted access to.',
    docNeedHelp: 'Need more help?',
    docContactSupport: 'Contact support',

    // Misc
    logout: 'Logout',
    profile: 'Profile',
    notifications: 'Notifications',
    help: 'Help',

    // ===== DT Alpha pages (diagrams / tickets / notifications / IoT / integrations) =====
    dt_loading: 'Loading…',
    app_newVersionReady: 'A new version is ready',
    app_reload: 'Reload',
    app_pageCouldNotOpen: "This page couldn't open",
    dt_dismiss: 'Dismiss',
    dt_cancel: 'Cancel',
    dt_save: 'Save',
    dt_delete: 'Delete',
    dt_edit: 'Edit',
    dt_create: 'Create',
    dt_rotateToken: 'Rotate token',
    dt_copyToken: 'Copy token',
    dt_hide: 'Hide',
    dt_reveal: 'Reveal',
    dt_status_open: 'Open',
    dt_status_assigned: 'Assigned',
    dt_status_in_progress: 'In progress',
    dt_status_resolved: 'Resolved',
    dt_status_closed: 'Closed',
    dt_status_cancelled: 'Cancelled',
    dt_priority_low: 'Low',
    dt_priority_normal: 'Normal',
    dt_priority_high: 'High',
    dt_priority_critical: 'Critical',
    dt_origin_manual: 'Manual',
    dt_origin_3d_element: '3D element',
    dt_origin_diagram: 'Diagram',
    dt_origin_iot: 'IoT',
    dt_devstatus_ok: 'OK',
    dt_devstatus_alert: 'Alert',
    dt_devstatus_offline: 'Offline',
    dt_devstatus_never: 'No data yet',
    dt_devstatus_disabled: 'Disabled',
    dt_dash_openTickets: 'Open tickets',
    dt_dash_updatedNow: 'Updated just now',
    dt_dash_unassigned: 'unassigned',
    dt_dash_needAttentionNow: 'need attention now',
    dt_dash_reportingNormally: 'reporting normally',
    dt_dash_nodataShort: 'no-data',
    dt_dash_highCritical: 'High / critical',
    dt_dash_devicesAttention: 'Devices needing attention',
    dt_dash_iotDevices: 'IoT devices',
    dt_dash_recentActivity: 'Recent activity',
    dt_dash_deviceHealth: 'Device health',
    dt_dash_allTickets: 'All tickets',
    dt_dash_allDevices: 'All devices',
    dt_dash_selectSite: 'Select a site to see its dashboard.',
    dt_dash_noActivity: 'No activity yet — ticket changes and IoT alerts on this site will show here.',
    dt_dash_nothingOpen: 'Nothing open.',
    dt_dash_noDevices: 'No devices registered.',
    dt_dash_boundLightUp: 'Bound devices light up the relationship diagram.',
    dt_dash_allReporting: 'All devices reporting normally.',
    dt_tickets_search: 'Search tickets…',
    dt_tickets_new: '+ New ticket',
    dt_tickets_activeDefault: 'Active (default)',
    dt_tickets_allStatuses: 'All statuses',
    dt_tickets_allPriorities: 'All priorities',
    dt_tickets_unassigned: 'Unassigned',
    dt_tickets_delete: 'Delete ticket',
    dt_tickets_deleteConfirm: 'Delete this ticket? Its comments, assignees and attached files go with it. This cannot be undone.',
    dt_tickets_post: 'Post',
    dt_tickets_addComment: 'Add a comment…',
    dt_tickets_showIn3d: 'Show in 3D',
    dt_tickets_timeline: 'Timeline',
    dt_tickets_loading: 'Loading tickets…',
    dt_tickets_none: 'No tickets yet — open one with “New ticket”, from a 3D element, or from a diagram simulation.',
    dt_tickets_noneMatch: 'No tickets match the filters.',
    dt_tickets_selectSite: 'Select a site to see its service calls.',
    dt_tickets_of: 'of',
    dt_tickets_active: 'Active',
    dt_tickets_createdVia: 'created via',
    dt_tickets_selectTicket: 'Select a ticket to see its details.',
    dt_tickets_emptyTitle: 'No tickets yet',
    dt_tickets_emptyHint: 'Open one with the button below, from a 3D element, or from a diagram simulation.',
    dt_tickets_touches: 'What this ticket touches',
    dt_tickets_linkAsset: '+ Link asset',
    dt_tickets_unlinkAsset: 'Unlink this asset from the ticket',
    dt_tickets_link: 'Link',
    dt_tickets_searchAssets: 'Search assets…',
    dt_tickets_noAssets: 'No assets linked to this ticket yet.',
    dt_tickets_primary: 'PRIMARY',
    dt_tickets_linked: 'LINKED',
    dt_tickets_location: 'Location',
    dt_tickets_system: 'System',
    dt_tickets_model: 'Model',
    dt_tickets_element: 'Element',
    dt_tickets_fromPrimary: 'from the primary asset',
    dt_tickets_moreSystems: 'more systems',
    dt_tickets_diagram: 'System diagram',
    dt_tickets_assetsHere: 'assets here',
    dt_tickets_openInDiagrams: 'Open in Diagrams →',
    dt_tickets_thisTicket: 'this ticket',
    dt_tickets_legendTicket: 'ticket asset',
    dt_tickets_legendLinked: 'linked',
    dt_tickets_isOn: 'is on',
    dt_tickets_assignee: 'Assignee',
    dt_tickets_aUser: 'A user',
    dt_tickets_aGroup: 'A group',
    dt_tickets_addAssignee: 'Add another assignee',
    dt_tickets_pickMember: '— pick a user or a group —',
    dt_tickets_clear: 'Clear',
    dt_tickets_group: 'group',
    dt_tickets_people: 'people',
    dt_tickets_sites: 'sites',
    dt_tickets_noAssignees: 'Nobody is on this ticket yet.',
    dt_tickets_forThisAsset: 'Tickets for this asset',
    dt_tickets_noneForAsset: 'No tickets reference this asset yet.',
    dt_ev_assetLinked: 'linked an asset',
    dt_ev_assetUnlinked: 'unlinked an asset',
    dt_ev_opened: 'opened the ticket',
    dt_ev_status: 'changed status',
    dt_ev_priority: 'changed priority',
    dt_ev_assigned: 'assigned the ticket',
    dt_ev_unassigned: 'unassigned the ticket',
    dt_ev_edited: 'edited the ticket',
    dt_ev_system: 'System',
    dt_ct_title: 'New ticket',
    dt_ct_titleField: 'Title',
    dt_ct_desc: 'Description',
    dt_ct_priority: 'Priority',
    dt_ct_assignee: 'Assignee',
    dt_ct_titlePh: 'What needs attention?',
    dt_ct_descPh: 'Details, symptoms, location…',
    dt_ct_create: 'Create ticket',
    dt_ct_creating: 'Creating…',
    dt_notif_title: 'Notifications',
    dt_notif_markAll: 'Mark all read',
    dt_notif_new: 'new',
    dt_notif_prefs: 'Notification preferences',
    dt_notif_inApp: 'In-app notifications',
    dt_notif_email: 'Email',
    dt_notif_empty: 'Nothing yet — ticket activity and alerts on your sites will appear here.',
    dt_notif_newTicket: 'New ticket',
    dt_notif_ticket: 'Ticket',
    dt_notif_commentOn: 'Comment on ticket',
    dt_notif_iotAlert: 'IoT alert',
    dt_notif_resolved: 'Ticket resolved',
    dt_notif_overdue: 'Ticket overdue',
    dt_notif_needsAttention: 'Ticket needs attention',
    dt_notif_dueAt: 'Due:',
    dt_notif_attentionSince: 'Attention needed since:',
    dt_notif_deviceSilent: 'Device stopped reporting',
    dt_notif_lastReading: 'Last reading:',
    dt_notif_activityUpdate: 'Activity update',
    dt_int_delivery_pending: 'Waiting to send',
    dt_int_delivery_delivered: 'Delivered',
    dt_int_delivery_failed: 'Delivery failed',
    dt_int_delivery_unknown: 'Delivery status unavailable',
    dt_notif_updated: 'updated',
    dt_notif_reassigned: 'reassigned',
    dt_notif_unassigned: 'unassigned',
    dt_dev_title: 'IoT Devices',
    dt_dev_registered: 'registered',
    dt_dev_register: '+ Register device',
    dt_dev_loading: 'Loading devices…',
    dt_dev_selectSite: 'Select a site to manage its IoT devices.',
    dt_dev_none: 'No devices yet. Register one, bind it to an asset (POI), and point the sensor at the ingest webhook — bound devices light up their asset node on the relationship diagram.',
    dt_dev_boundTo: 'Bound to:',
    dt_dev_unbound: '— unbound',
    dt_dev_lastReading: 'Last reading:',
    dt_dev_never: 'never',
    dt_dev_thresholds: 'Thresholds:',
    dt_dev_showToken: 'Show ingest token',
    dt_dev_ext: 'ext:',
    dt_dev_name: 'Name',
    dt_dev_extKey: 'External key (vendor/serial — optional)',
    dt_dev_boundAsset: 'Bound asset (POI)',
    dt_dev_locNote: 'Location note',
    dt_dev_alertThresholds: 'Alert thresholds',
    dt_dev_metricPh: 'metric (e.g. temperature)',
    dt_dev_min: 'min',
    dt_dev_max: 'max',
    dt_dev_addThreshold: '+ Threshold',
    dt_dev_enabled: 'Enabled (disabled devices reject ingest)',
    dt_dev_registerBtn: 'Register',
    dt_dev_editTitle: 'Edit',
    dt_dev_regTitle: 'Register device',
    dt_dev_confirmRotate: 'Rotate the ingest token for this device? The current token stops working immediately.',
    dt_dev_confirmDelete: 'Delete this device and all its readings?',
    dt_int_title: 'Integrations',
    dt_int_connectors: 'connectors',
    dt_int_connector: 'connector',
    dt_int_add: '+ Add connector',
    dt_int_intro: 'Mirror service-call activity to an external CRM (outbound webhook) and let it update calls back (inbound endpoint). Mabat3D remains the source of truth.',
    dt_int_loading: 'Loading integrations…',
    dt_int_none: 'No connectors yet.',
    dt_int_selectSite: 'Select a site to manage its integrations.',
    dt_int_enabled: 'Enabled',
    dt_int_disabled: 'Disabled',
    dt_int_outbound: 'Outbound:',
    dt_int_inbound: 'Inbound:',
    dt_int_notSet: '— not set',
    dt_int_events: 'Events:',
    dt_int_lastDelivery: 'Last delivery:',
    dt_int_showEndpoint: 'Show inbound endpoint + token',
    dt_int_addTitle: 'Add connector',
    dt_int_editTitle: 'Edit',
    dt_int_name: 'Name',
    dt_int_url: 'Outbound webhook URL',
    dt_int_secret: 'Signing secret (optional — sent as HMAC-SHA256 X-Mabat-Signature',
    dt_int_secretKeep: '; blank keeps the current secret',
    dt_int_deliveredEvents: 'Delivered events',
    dt_int_noneSelected: 'No events selected — nothing will be delivered (the connector is effectively paused).',
    dt_int_ev_all: 'All service-call events',
    dt_int_ev_created: 'Call created',
    dt_int_ev_updated: 'Call updated',
    dt_int_ev_commented: 'Call commented',
    dt_int_ev_iot: 'IoT alerts',
    dt_int_confirmRotate: 'Rotate the inbound token for this connector? The current token stops working immediately.',
    dt_int_confirmDelete: 'Delete this integration?',
    dt_int_pausedNone: 'none (paused)',
    dt_dg_addAsset: '+ Asset',
    dt_dg_addSystem: '+ System',
    dt_dg_addJunction: '+ Junction',
    dt_dg_seedFromSystem: 'Seed from system…',
    dt_dg_allSystems: 'All systems',
    dt_dg_allKinds: 'All kinds',
    dt_dg_lens: 'Lens:',
    dt_dg_only: 'Only:',
    dt_dg_fullLabels: 'Full labels',
    dt_dg_fullLabelsTip: 'Wrap long node names instead of truncating',
    dt_dg_panel: 'Panel',
    dt_dg_newConnections: 'New connections:',
    dt_dg_save: 'Save',
    dt_dg_saving: 'Saving…',
    dt_dg_saved: 'Saved',
    dt_dg_canonical: 'CANONICAL',
    dt_dg_edge_flow: 'Flow',
    dt_dg_edge_dependency: 'Dependency',
    dt_dg_edge_physical: 'Physical',
    dt_dg_edge_condition: 'Condition',
    dt_dg_kind_asset: 'Asset',
    dt_dg_kind_system: 'System',
    dt_dg_kind_logical: 'Junction',
    dt_dg_tab_simulate: 'Simulate',
    dt_dg_tab_lint: 'Validity',
    dt_dg_fail: 'Fail',
    dt_dg_disconnect: 'Disconnect',
    dt_dg_reset: 'Reset',
    dt_dg_impact: 'Impact',
    dt_dg_affected: 'affected',
    dt_dg_seeds: 'seed(s)',
    dt_dg_highlight3d: 'Highlight impact in 3D',
    dt_dg_isolated: 'Nothing else is affected — the seeds are isolated or redundancy holds.',
    dt_dg_reason_flow: 'Lost all supply',
    dt_dg_reason_physical: 'Physical spread',
    dt_dg_reason_dependency: 'Dependency down',
    dt_dg_noSim: 'No simulation running.',
    dt_dg_noIssues: '✓ No structural issues found.',
    dt_dg_checkLinks: 'Check asset links (loads asset list)',
    dt_dg_seedTitle: 'Seed from a system',
    dt_dg_seedIntro: 'Adds an asset node for every asset in the chosen system (no connections are guessed).',
    dt_dg_seedLoading: 'Loading systems…',
    dt_dg_seedNone: 'No system values found on this site\'s assets.',
    dt_dg_assets: 'assets',
    dt_dg_pickAsset: 'Link an asset',
    dt_dg_searchAssets: 'Search assets…',
    dt_dg_noMatchAssets: 'No matching assets.',
    dt_dg_loadingAssets: 'Loading assets…',
    dt_dg_loadingDiagram: 'Loading diagram…',
    dt_dg_noDiagram: 'No diagram exists for this site yet.',
    dt_dg_call: 'Ticket',
    dt_dg_callTip: 'Open a ticket for this impact',
    dt_dg_3d: '3D',
    dt_dg_3dTip: 'Show this asset in the 3D viewer',
    dt_dg_selectSite: 'Select a site to view its relationship diagram.',
    dt_dg_diagrams: 'Diagrams',
    dt_dg_chooseDiagram: 'Choose diagram',
    dt_dg_unsavedSwitch: 'You have unsaved changes — discard them and switch diagrams?',
    dt_dg_createSiteDiagram: 'Create the site diagram',
    dt_dg_firstInfo: 'The first diagram becomes the site\'s canonical relationship graph — the base for failure simulation, and later IoT and service calls.',
    dt_dg_saveChanges: 'Save changes',
    dt_dg_cannotSave: 'Cannot save',
    dt_dg_saveFailed: 'Save failed',
    dt_dg_saveConflict: 'This diagram was changed by someone else since you loaded it.\n\nOK = overwrite with your version · Cancel = keep theirs (reload)',
    dt_dg_nodeLabelPrompt: 'Node label:',
    dt_dg_markNodes: 'Select node(s) on the canvas, then mark them:',
    dt_dg_showFirst50: 'Showing the first 50 matches — refine the search.',
    dt_dg_closePanel: 'Close panel',

    // Responsibility editor (scope_grants, migration 042)
    dt_resp_title: 'Responsibility',
    dt_resp_subtitle: 'Attach a user or a group to a system or a room, with a role and a duty',
    dt_resp_selectSite: 'Select a site to manage who is responsible for what.',
    dt_resp_loading: 'Loading scopes and people\u2026',
    dt_resp_loadFailed: 'Failed to load responsibility data',
    dt_resp_attachFailed: 'Failed to attach',
    dt_resp_revokeFailed: 'Failed to revoke',
    dt_resp_newAttachment: 'New attachment',
    dt_resp_step1: 'What is it attached to?',
    dt_resp_step1Sub: 'the scope',
    dt_resp_step2: 'Who?',
    dt_resp_step2Sub: 'the member',
    dt_resp_step3: 'With what power, and what duty?',
    dt_resp_aSystem: 'A system',
    dt_resp_aRoom: 'A room',
    dt_resp_aSite: 'A site',
    dt_resp_oneAsset: 'One asset',
    dt_resp_otherScopes: 'Also legal scope types \u2014 same row, different reference',
    dt_resp_subtree: 'subtree',
    dt_resp_wholeStorey: 'whole storey',
    dt_resp_noOwner: 'no owner',
    dt_resp_noSystems: 'This site has no systems yet.',
    dt_resp_noRooms: 'This site has no rooms yet.',
    dt_resp_aUser: 'A user',
    dt_resp_aGroup: 'A group',
    dt_resp_noUsers: 'Nobody has access to this site yet.',
    dt_resp_noGroups: 'No group is on this site yet. Add one to the site on the Users page first.',
    dt_resp_here: 'here',
    dt_resp_people: 'people',
    dt_resp_sites: 'sites',
    dt_resp_alreadyOn: 'already on',
    dt_resp_group: 'group',
    dt_resp_groupNote: 'Attaching the group is one row forever \u2014 people joining or leaving inherit this immediately, and no per-person cleanup is ever needed.',
    dt_resp_roleLabel: 'Role \u2014 what they may DO',
    dt_resp_roleNone: '\u2014 no role (the duty only) \u2014',
    dt_resp_roleHint: 'Role is separate from scope. The role says what; this scope says what to.',
    dt_resp_dutyLabel: 'Duty \u2014 what they answer for',
    dt_resp_duty: 'duty',
    dt_resp_duty_owner: 'Owner',
    dt_resp_duty_responder: 'Responder',
    dt_resp_duty_watcher: 'Watcher',
    dt_resp_dutyHint: 'Owner answers for it \u00b7 Responder is routed the tickets \u00b7 Watcher is only notified.',
    dt_resp_systemSubtree: 'system subtree',
    dt_resp_roomScope: 'room',
    dt_resp_pickBoth: 'Pick a scope and a member to see what will be attached.',
    dt_resp_impliesReadA: 'Covers',
    dt_resp_impliesReadAssets: 'assets under this scope',
    dt_resp_impliesReadB: '\u2014 only people and groups already on this site can be picked, so \u201cresponsible but can\u2019t open it\u201d cannot be created here.',
    dt_resp_cancel: 'Cancel',
    dt_resp_attach: 'Attach',
    dt_resp_siteAdminOnly: 'Site administrators manage responsibility for their own site.',
    dt_resp_alreadyOnScope: 'Already on this scope',
    dt_resp_pickScopeFirst: 'Pick a scope to see who is already on it.',
    dt_resp_nobodyHere: 'Nobody is attached to this scope yet.',
    dt_resp_membership: 'membership',
    dt_resp_member_user: 'user',
    dt_resp_member_group: 'group',
    dt_resp_noRole: 'no role',
    dt_resp_by: 'by',
    dt_resp_revoke: 'Revoke',
    dt_resp_membershipNote: 'Plain membership already means can see it and is notified about it. A duty is the extra sentence: someone answers for it.',
    dt_resp_threeRules: 'The three rules',
    dt_resp_rule1: 'A group is users + things',
    dt_resp_rule1d: 'One list of people, one list of what they are attached to. No nested groups.',
    dt_resp_rule2: 'Role \u2260 scope',
    dt_resp_rule2d: 'The role says what you may do; the scope says what you may do it to. Permission is the intersection.',
    dt_resp_rule3: 'Duty implies read',
    dt_resp_rule3d: 'Being responsible for something grants read on it \u2014 so \u201cresponsible but no access\u201d becomes impossible, not just visible.',
    dt_resp_rowNote: 'Every attachment made here is one row with a typed source. Revoking removes the row; it never writes a tombstone or a note.',

    // Planning objects (source request §7) — sized shapes placed in the model to check fit.
    planningObjectsTitle: 'Planning objects',
    planningObjectsLayer: 'Planning objects',
    planningObjectAdd: '+ Add object',
    planningObjectDefaultName: 'New object',
    planningObjectName: 'Name',
    planningObjectNamePlaceholder: 'Washing machine',
    planningObjectShape: 'Shape',
    planningObjectShapeBox: 'Box',
    planningObjectShapeCylinder: 'Cylinder',
    planningObjectShapeExtrusion: 'Draw a shape',
    planningObjectDraw: 'Draw',
    planningObjectDrawHint: 'Click the corners of the shape on the floor, then click the first corner again to close it',
    planningObjectSize: 'Size (cm)',
    planningObjectWidth: 'Width',
    planningObjectDepth: 'Depth',
    planningObjectDiameter: 'Diameter',
    planningObjectHeight: 'Height',
    planningObjectRotation: 'Rotation (\u00b0)',
    planningObjectFloor: 'Floor',
    planningObjectAboveFloor: 'Height above floor (cm)',
    planningObjectPlace: 'Place',
    planningObjectPlaceHint: 'Click a floor in the model to place it',
    planningObjectsEmpty: 'Nothing placed yet. Add an object with the size of the equipment you are planning, drop it in the model, and the panel will tell you how much room is left around it.',
    planningObjectOverlaps: 'Overlaps the model \u2014 it does not fit here',
    planningObjectClearance: 'Clearance',
    planningObjectSide_right: 'Right',
    planningObjectSide_left: 'Left',
    planningObjectSide_front: 'Front',
    planningObjectSide_back: 'Back',
    planningObjectSide_above: 'Above',

    // Site plans (source request §5) — 2D drawings projected onto the model.
    sitePlansTitle: 'Plans',
    sitePlansLayer: 'Plans',
    sitePlanUpload: "Upload plan",
    sitePlanChooseExisting: "Choose an existing site file",
    sitePlanUploading: "Uploading plan…",
    sitePlanAdding: "Adding plan…",
    sitePlanRetryAdd: "Retry adding uploaded plan",
    sitePlanKeepOpen: "Keep this pane open until the plan is added.",
    sitePlanLoadError: "Could not load files. Please try again.",
    sitePlanWaitModel: "Wait for the model to load before adding an image plan.",
    sitePlanCreateError: "Could not add the plan. The file is saved in site files; try again.",
    sitePlanUnsupported: "Choose a supported drawing or image file.",
    sitePlanUploadError: "Could not finish adding the plan. Please try again.",
    sitePlanNoExistingFiles: "No suitable site files yet. Upload a plan above.",
    sitePlanQueued: "Queued",
    sitePlanStatusFailed: "Failed",
    sitePlanAdd: '+ Add plan',
    sitePlanPickFile: 'Choose a drawing',
    sitePlanNoCandidates: 'No usable drawings on this site yet. Upload a DWG, DXF, PDF or image first.',
    sitePlansEmpty: 'No plans yet. Add a drawing and it is laid over the model — a DWG with coordinates lands by itself.',
    sitePlanKind: 'Shows',
    sitePlanElevation: 'Height in the model',
    sitePlanOpacity: 'Opacity',
    sitePlanVisible: 'Show this plan',
    sitePlanRetry: 'Try again',
    sitePlanRemove: 'Remove',
    sitePlanFailed: 'The drawing could not be translated',
    sitePlanClipped: 'The drawing may be clipped to its last saved view. If part of it is missing, re-save it in CAD showing the whole plan and upload again.',
    sitePlanAlignPlan1: 'Click a recognisable point ON THE PLAN',
    sitePlanAlignModel1: 'Now click that same point ON THE MODEL',
    sitePlanAlignPlan2: 'Click a SECOND point on the plan, far from the first',
    sitePlanAlignModel2: 'And that second point on the model',
    sitePlanAlignedByPoints: 'Aligned by two points',
    sitePlanAlignedByFile: 'Using the drawing\u2019s own coordinates',
    sitePlanUseFileCoords: 'Use the drawing\u2019s own coordinates',
    dt_int_kind: 'Type',
    dt_int_kind_rest: 'CRM / ERP connector',
    dt_int_kind_lorawan: 'LoRaWAN network server',
    dt_int_kind_bms: 'BMS gateway',
    dt_int_hint_lorawan: 'Point your network server\u2019s webhook at this integration. Devices are matched by DevEUI, so each device needs its DevEUI set as its External key. Payloads must be decoded by a formatter in the network server.',
    dt_int_hint_bms: 'Point your BMS gateway at this integration. It should POST { points: [{ id, value }] }, where id matches a device\u2019s External key.',
    dt_int_deliveries: 'Deliveries',
    dt_int_noDeliveries: 'Nothing sent yet.',
    dt_int_attempts: 'attempts',
    dt_int_resend: 'Resend',
    dt_dev_adapter: 'Reports via',
    dt_dev_adapter_generic: 'Direct (own ingest token)',
    dt_dev_adapter_lorawan: 'LoRaWAN network server',
    dt_dev_adapter_bms: 'BMS gateway',
    dt_dev_devEui: 'DevEUI',
    dt_dev_pointId: 'BMS point id',
    dt_dev_extKeyRequired: 'Without this, an uplink cannot find this device \u2014 it stays on the direct adapter.',
    systemsTreeTitle: 'System tree',
    systemsTreeHint: 'Group systems under a parent \u2014 an asset in a child is also in everything above it, and whoever is responsible for a parent is responsible for what is below it.',
    systemsTreeEmpty: 'No systems yet. Add one above, or derive them from model properties below.',
    systemsNewName: 'New system name',
    systemsNoParent: 'Top level',
    systemsAdd: 'Add',
    systemsRename: 'Rename',
    systemsParent: 'Parent',
    systemsDerived: 'derived',
    systemsDerivedHint: 'Created by a property derivation \u2014 a re-run may replace it',
    systemsCountHint: 'Assets in this system or one below it',
    systemsDeleteBlocked: 'Assets are still in this system',
    systemsPreviewTitle: 'Derive from model properties',
    systemsPreview: 'Preview',
    systemsPreviewing: 'Checking\u2026',
    systemsPickPropertyFirst: 'Choose a system property first',
    systemsWouldCreate: 'Systems',
    systemsMaxDepth: 'Depth',
    systemsNoSystem: 'Assets with no system',
    systemsTooManyWarning: 'That is a lot of systems for one site. Property values like Revit family names produce one system per element type rather than real groupings \u2014 check the names below before applying.',
    systemsApplyDerivation: 'Apply this',
    dt_ct_findAsset: 'Search assets to attach\u2026',
    dt_ct_noAssetMatch: 'No matching asset',
    dt_ct_noAssetsYet: 'No asset attached. A ticket can also be about a whole room or system — add one below.',
    // Ticket scope, attachments and SLA (migrations 060/062/063)
    dt_ct_scope: 'Or a whole room / system',
    dt_ct_findScope: 'Search rooms and systems…',
    dt_ct_noScopeMatch: 'No matching room or system',
    dt_ct_removeScope: 'Remove',
    dt_tickets_aboutScope: 'This ticket is about',
    dt_tickets_detachScope: 'Detach',
    dt_ev_scopeLinked: 'Attached',
    dt_ev_scopeUnlinked: 'Detached',
    dt_tickets_attachments: 'Photos & files',
    dt_tickets_uploading: 'Uploading…',
    dt_tickets_addPhoto: '+ Add photo or file',
    dt_tickets_noAttachments: 'No photos or files yet — a picture of the fault is usually the fastest report.',
    dt_tickets_removeAttachment: 'Remove',
    dt_tickets_download: 'Download',
    dt_ev_attachmentAdded: 'Attached',
    dt_ev_attachmentRemoved: 'Attachment removed',
    dt_tickets_overdue: 'Overdue',
    dt_tickets_slaDue: 'Escalation due',
    // Tickets — DT alignment (2026-09-25): icon buttons and the queue footer say in words
    // what their icons show.
    dt_tickets_newTicket: 'New ticket',
    dt_tickets_removeFilter: 'Remove filter',
    dt_tickets_range: '{from}–{to} of {total}',
    dt_tickets_previousPage: 'Previous page',
    dt_tickets_nextPage: 'Next page',
    dt_tickets_previousTicket: 'Previous ticket',
    dt_tickets_nextTicket: 'Next ticket',
    dt_tickets_created: 'Created',
    dt_tickets_doneEditing: 'Done',
    // Coverage dashboard \u2014 never had translations at all
    dt_cov_title: 'Coverage',
    dt_cov_subtitle: 'What has nobody accountable for it',
    dt_cov_selectSite: 'Select a site to see coverage.',
    dt_cov_loadFailed: 'Could not load coverage for this site.',
    dt_cov_wholeSubtree: 'whole subtree',
    dt_cov_assets: 'assets',
    dt_cov_openTicket: 'open ticket',
    dt_cov_systemsWithOwner: 'Systems with an owner',
    dt_cov_subtreesUncovered: 'subtrees uncovered',
    dt_cov_roomsWithOwner: 'Rooms with an owner',
    dt_cov_roomsUncovered: 'rooms uncovered',
    dt_cov_uncoveredWithTickets: 'Uncovered with open tickets',
    dt_cov_nobodyOnHook: 'nobody is on the hook for these',
    dt_cov_peopleWithDuty: 'People & groups with a duty',
    dt_cov_users: 'users',
    dt_cov_groups: 'groups',
    dt_cov_systemsNoOwner: 'Systems with no owner',
    dt_cov_allSystemsCovered: 'Every system has an owner',
    dt_cov_roomsNoOwner: 'Rooms with no owner',
    dt_cov_allRoomsCovered: 'Every room has an owner',
    dt_cov_byTopLevelSystem: 'Coverage by top-level system',
    dt_cov_owner: 'owner',
    dt_cov_noOwner: 'no owner',
    dt_cov_howComputed: 'How this is computed',
    dt_cov_howComputedBody: 'One query over',
    dt_cov_fullyCovered: 'Fully covered',
    dt_cov_everythingCovered: 'Every system and room has an owner',
    dt_cov_systems: 'systems',
    dt_cov_rooms: 'rooms',
    dt_cov_nothingUncovered: 'nothing uncovered. Check back after the next model sync \\u2014 new rooms arrive uncovered.',
    dt_cov_notFullyCovered: 'Not fully covered yet \\u2014 see the lists above.',
    // Missing since before this session
    assignLocations: 'Assign Locations',
    assetNotAssigned: 'Not assigned',
    assetLocate: 'Locate in 3D',
    assetLoadModel: 'Load model',
    assetManage: 'Manage',
    assetSelectResults: 'Select all results',
    assetFind: 'Find assets',
    assetFindHint: 'Choose where and what you’re looking for. You can select more than one.',
    assetWhere: 'Where?',
    assetWhat: 'What?',
    assetSearchPlaces: 'Search buildings, floors or rooms',
    assetSearchEquipment: 'Search equipment or systems',
    assetChooseRooms: 'Choose rooms',
    assetMoreOptions: 'More options',
    assetChooseProperty: 'Choose a property',
    assetCustomRules: 'Custom rules',
    assetCustomRulesHint: 'For detailed property searches. Rules also narrow the choices above.',
    assetNoChoices: 'No matching choices',
    assetMatches: 'matching assets',
    assetAll: 'All assets',
    assetOr: 'or',
    assetResetChoices: 'Reset choices',
    assetShowResults: 'Show results',
    assetFacet_building: 'Building',
    assetFacet_level: 'Floor',
    assetFacet_room_name: 'Room',
    assetFacet_category: 'Equipment type',
    assetFacet_system_category: 'System',
    assetFacet_source_file: 'Model',
    assetExport: 'Export',
    assetExportResults: 'Matching assets',
    assetExportSelected: 'Selected assets',
    assetExpand: 'Expand',
    assetRestore: 'Restore',
    assetWithoutRoom: 'assets without an assigned room',
    assetInScene: 'In scene selection',
    assetFollowScene: 'Follow scene selection',
    assetNotAssetYet: 'not an asset yet',
    assetSceneElement: 'Element',
    assetCreating: 'Creating…',
    assetSceneMore: '+{count} more',
    assetSceneFewer: 'Show fewer',
    assetClearFilters: 'Clear filters',
    assetRemoveFilter: 'Remove filter',
    assetOutsideResults: 'selected outside these results',
    assetClearSelection: 'Clear selection',
    assetSelectPage: 'Select this page',
    assetSelect: 'Select asset',
    assetDetails: 'Asset details',
    assetNoResults: 'No assets match these choices.',
    assetNoAssets: 'No assets in this site yet.',
    assetResetSearch: 'Reset search',
    assetPage: 'Page',
    assetResize: 'Resize assets panel',
    assetDevices: 'Connected devices',
    assetNoDevices: 'No connected devices',
    assetDevicesError: 'Could not load connected devices',
    assetAddDevice: 'Add device',
    assetNewTicket: 'New ticket',
    assetViewTickets: 'View tickets',
    poiAssetsTitle: 'Assets',
    poiElements: 'elements',
    poiColumns: 'Columns',
    poiRoomGapWarning: 'assets are assigned to storeys but not to rooms \u2014 room highlight, filters and counts won\'t cover them.',
    noLocationsHint: 'Locations are added when you import assets from a model in the Viewer.',
    noSystemAssetsHint: 'Assets are added when you import them from a model in the Viewer.',
    poiRoomAssignmentWarning: '{count} of {total} assets are not assigned to rooms \u2014 room features (highlight, filters, counts) won\'t cover them. Run "Reassign locations" from the Locations page.',
    dt_cov_howComputedBody2: 'where duty = \'owner\', walking the system and location parent chains the same way accessSql.js \u00b7 RESPONSIBLE_GRANTS_SQL already does for a single POI. Nothing is cached and nothing is fanned out \u2014 a new grant changes this page on the next load.',
  },
  he: {
    va_title: "הצג בתור",
    va_viewing: "מוצג בתור",
    va_write_notice: "התצוגה משנה את מה שרואים. שינויים שתבצעו עדיין יתבצעו בהרשאות המנהל שלכם.",
    va_exit: "חזרה להרשאות שלי",
    va_change: "שינוי התצוגה",
    va_close: "סגירה",
    va_retry: "ניסיון חוזר",
    va_loading: "טוען…",
    va_load_error: "לא ניתן לטעון את אפשרויות התצוגה.",
    va_by_role: "בחירת אדם לפי תפקיד",
    va_role_explanation: "מציג אדם אמיתי בעל התפקיד, כולל שאר התפקידים, הקבוצות והשיוכים שלו. זו אינה הדמיה של תפקיד בודד.",
    va_choose_site: "בחרו אתר כדי לצפות בתפקיד זה.",
    va_no_site_holder: "אין אדם פעיל בעל תפקיד זה באתר הנבחר.",
    va_no_holder: "אין אדם פעיל בעל תפקיד זה.",
    va_site_role: "תפקיד באתר",
    va_global_role: "תפקיד כלל־מערכתי",
    va_via: "תצוגה דרך",
    va_by_person: "בחירת אדם מסוים",
    va_person_explanation: "צפו בגישה המשולבת של אדם זה לאתרים ולשיוכים שלו.",
    va_search: "חיפוש שם או דוא״ל…",
    va_no_people: "לא נמצאו אנשים מתאימים.",

    ug_create_a_site: "יצירת אתר",
    ug_edit_site_details: "עריכת פרטי אתר",
    ug_delete_a_site: "מחיקת אתר",
    ug_delete_a_model_file: "מחיקת קובץ מודל",
    ug_own_an_acc_connection: "ניהול חיבור Autodesk",
    ug_unlink_retarget_a_model: "ניתוק או שינוי שיוך מודל",
    ug_import_pois_from_csv: "ייבוא נכסים מקובץ CSV",
    ug_delete_pois: "מחיקת נכסים",
    ug_re_file_pois_into_systems: "שינוי שיוך נכסים למערכות",
    ug_open_a_ticket: "פתיחת קריאה",
    ug_assign_a_ticket: "הקצאת קריאה",
    ug_comment_on_a_ticket: "הוספת תגובה לקריאה",
    ug_delete_a_ticket: "מחיקת קריאה",
    ug_reveal_a_device_ingest_token: "הצגת מפתח חיבור של חיישן",
    ug_manage_crm_integrations: "ניהול חיבורי CRM",
    ug_reveal_an_integration_token: "הצגת מפתח חיבור של אינטגרציה",
    ug_see_who_has_access: "הצגת בעלי גישה",
    ug_add_a_user: "הוספת משתמש",
    ug_deactivate_reset_a_user: "השבתת משתמש או איפוס סיסמה",
    ug_delete_a_user: "מחיקת משתמש",
    ug_create_rename_delete_a_group: "יצירה, שינוי שם או מחיקת קבוצה",
    ug_run_the_onboarding_wizard: "הפעלת אשף הקמת אתר",
    ug_invite_people_to_a_site: "הזמנת אנשים לאתר",
    ug_diagnostic_dumps: "הורדת נתוני אבחון",
    ug_confirm_action: "אישור פעולה",
    ug_confirm: "אישור",
    ug_name_activated: "החשבון של {name} הופעל",
    ug_name_deactivated: "החשבון של {name} הושבת",
    ug_administrator: "מנהל",
    ug_operator: "מפעיל",
    ug_editor: "עורך",
    ug_viewer: "צופה",
    ug_site_administrator: "מנהל אתר",
    ug_open_min_8: "(לפחות 8 תווים)",
    ug_plus_add_assignment: "+ הוספת שיוך",
    ug_plus_add_person: "+ הוספת אדם",
    ug_plus_create_group: "+ יצירת קבוצה",
    ug_plus_create_user: "+ יצירת משתמש",
    ug_a_temporary_password_was_emailed_to_the_account_holder_nobody_else_is_shown_it: "סיסמה זמנית נשלחה בדוא״ל לבעל החשבון בלבד.",
    ug_abilities: "יכולות",
    ug_abilities_for_this_assignment: "יכולות עבור שיוך זה",
    ug_access_responsibility: "גישה ואחריות",
    ug_access_inherited_from_a_group_is_managed_on_that_group_direct_assignments_can_be_changed_here: "גישה שמתקבלת מקבוצה מנוהלת בקבוצה עצמה. ניתן לשנות כאן שיוכים ישירים.",
    ug_access_to_value0_revoked: "הגישה אל {value0} בוטלה",
    ug_activate: "הפעלה",
    ug_add: "הוספה",
    ug_add_people: "הוספת אנשים",
    ug_add_people_name: "הוספת אנשים: {name}",
    ug_adding_a_person_gives_them_the_group_s_assignments_removing_them_removes_only_access_inherited_from_this_group: "הוספת אדם מקנה לו את שיוכי הקבוצה. הסרתו מבטלת רק גישה שהתקבלה מקבוצה זו.",
    ug_after_creating_the_account_add_them_to_a_group_or_assign_their_sites_systems_rooms_and_assets: "לאחר יצירת החשבון, הוסיפו את האדם לקבוצה או שייכו אליו אתרים, מערכות, חדרים ונכסים.",
    ug_all_sites: "כל האתרים",
    ug_allow: "לאפשר",
    ug_allow_or_withhold_specific_abilities_on_this_attachment_only: "שינוי יכולות עבור שיוך זה בלבד",
    ug_asset: "נכס",
    ug_assets_open: "נכסים (",
    ug_assign_access_and_responsibility: "שיוך גישה ואחריות",
    ug_assign_name: "שיוך עבור {name}",
    ug_assignment_removed: "השיוך הוסר",
    ug_assignment_saved: "השיוך נשמר",
    ug_assignment_saved_review_the_access_warning_for_this_person: "השיוך נשמר. יש לבדוק את אזהרת הגישה עבור אדם זה.",
    ug_assignments: "שיוכים",
    ug_attach: "שמירת שיוך",
    ug_attached_to_no_room: "ללא שיוך לחדרים",
    ug_attached_to_no_system: "ללא שיוך למערכות",
    ug_attached_to_nothing: "אין שיוכים",
    ug_autodesk_construction_cloud: "Autodesk Construction Cloud",
    ug_back_to_people: "חזרה לאנשים",
    ug_cancel: "ביטול",
    ug_choose_a_room_ellipsis: "בחירת חדר…",
    ug_choose_a_site_ellipsis: "בחירת אתר…",
    ug_choose_a_system_ellipsis: "בחירת מערכת…",
    ug_choose_an_asset_ellipsis: "בחירת נכס…",
    ug_choose_what_to_attach: "יש לבחור אתר, מערכת, חדר או נכס לשיוך",
    ug_choose_where_this_person_or_group_belongs_then_choose_their_role_and_responsibility_check_that_they_also_have_access_to_the_site: "בחרו לאן לשייך את האדם או הקבוצה, ואז בחרו תפקיד ואחריות. יש לוודא שיש להם גם גישה לאתר.",
    ug_close: "סגירה",
    ug_could_not_load_abilities: "לא ניתן לטעון את היכולות",
    ug_could_not_save: "לא ניתן לשמור",
    ug_create_group: "יצירת קבוצה",
    ug_create_user: "יצירת משתמש",
    ug_created_by_name: "נוצרה על ידי {name}",
    ug_deactivate: "השבתה",
    ug_default: "לפי התפקיד",
    ug_delete: "מחיקה",
    ug_delete_value0_everyone_in_it_loses_what_the_group_gave_them: "למחוק את ״{value0}״? כל חברי הקבוצה יאבדו את הגישה שקיבלו ממנה.",
    ug_delete_value0_this_cannot_be_undone: "למחוק את {value0}? לא ניתן לבטל פעולה זו.",
    ug_description: "תיאור",
    ug_diagrams: "תרשימים",
    ug_direct: "ישיר",
    ug_done: "סיום",
    ug_email_required: "דוא״ל *",
    ug_embed_partners: "שותפי הטמעה",
    ug_enter_a_group_name: "יש להזין שם קבוצה",
    ug_enter_a_name: "יש להזין שם",
    ug_equipment_register: "רשימת ציוד",
    ug_failed_to_attach: "לא ניתן לשמור את השיוך",
    ug_failed_to_change_group_membership: "לא ניתן לשנות את החברות בקבוצה",
    ug_failed_to_change_status: "לא ניתן לשנות את מצב החשבון",
    ug_failed_to_create_group: "לא ניתן ליצור את הקבוצה",
    ug_failed_to_create_user: "לא ניתן ליצור את המשתמש",
    ug_failed_to_delete_group: "לא ניתן למחוק את הקבוצה",
    ug_failed_to_delete_user: "לא ניתן למחוק את המשתמש",
    ug_failed_to_detach: "לא ניתן להסיר את השיוך",
    ug_failed_to_grant_read: "לא ניתן להעניק גישת קריאה",
    ug_failed_to_load_available_sites_systems_rooms_or_assets: "לא ניתן לטעון את האתרים, המערכות, החדרים או הנכסים הזמינים",
    ug_failed_to_load_this_person: "לא ניתן לטעון את פרטי האדם",
    ug_failed_to_load_users_and_groups: "לא ניתן לטעון משתמשים וקבוצות",
    ug_failed_to_remove_from_the_group: "לא ניתן להסיר את האדם מהקבוצה",
    ug_failed_to_rename_group: "לא ניתן לשנות את שם הקבוצה",
    ug_failed_to_reset_password: "לא ניתן לאפס את הסיסמה",
    ug_failed_to_revoke_site_access: "לא ניתן לבטל את הגישה לאתר",
    ug_files_models: "קבצים ומודלים",
    ug_following_the_role: "לפי הגדרות התפקיד",
    ug_full_access: "גישה מלאה",
    ug_full_name: "שם מלא",
    ug_grant_read_access_to_site: "הענקת גישת קריאה אל {site}",
    ug_granted_by_the_group_change_it_on_the_group: "גישה שהתקבלה מקבוצה — יש לשנות אותה בקבוצה",
    ug_granted_from_the_responsibility_gap_warning: "הוענק בעקבות אזהרת פער גישה",
    ug_group_created: "הקבוצה נוצרה",
    ug_group_deleted: "הקבוצה נמחקה",
    ug_group_name_required: "שם הקבוצה *",
    ug_group_renamed: "שם הקבוצה שונה",
    ug_groups: "קבוצות",
    ug_groups_open: "קבוצות (",
    ug_in_no_group: "ללא קבוצות",
    ug_includes_child_items: "כולל פריטים משויכים מתחתיו",
    ug_integrations: "אינטגרציות",
    ug_iot: "חיישנים",
    ug_join: "הוספה לקבוצה",
    ug_leave: "הסרה מהקבוצה",
    ug_loading_rooms_ellipsis: "טוען חדרים…",
    ug_loading_systems_ellipsis: "טוען מערכות…",
    ug_loading_users: "טוען משתמשים…",
    ug_loading_ellipsis: "טוען…",
    ug_manage_groups: "ניהול קבוצות",
    ug_manage_groups_name: "ניהול קבוצות: {name}",
    ug_manage_members_and_assignments_together_group_assignments_are_inherited_by_all_members_roles_and_responsibilities_determine_what_they_can_do: "ניהול חברים ושיוכים יחד. שיוכי הקבוצה חלים על כל חבריה; התפקידים ותחומי האחריות קובעים מה הם יכולים לבצע.",
    ug_manage_people_groups_and_their_access_across_all_sites: "ניהול אנשים, קבוצות והגישה שלהם בכל האתרים.",
    ug_member: "חבר",
    ug_member_since_date: "חבר מאז {date}",
    ug_membership_only: "חברות בלבד",
    ug_name_required: "שם *",
    ug_no_duty: "ללא אחריות מוגדרת",
    ug_no_groups: "ללא קבוצות",
    ug_no_groups_to_join_yet: "אין עדיין קבוצות להצטרפות",
    ug_no_groups_yet_create_a_group_to_manage_people_and_their_assignments_together: "אין עדיין קבוצות. צרו קבוצה לניהול אנשים ושיוכים יחד.",
    ug_no_matching_groups: "לא נמצאו קבוצות מתאימות",
    ug_no_roles: "ללא תפקידים",
    ug_no_rooms: "ללא חדרים",
    ug_no_site_access: "ללא גישה לאתרים",
    ug_no_sites_assigned: "לא הוקצו אתרים",
    ug_no_systems: "ללא מערכות",
    ug_no_users_found: "לא נמצאו משתמשים",
    ug_nobody_yet: "אין עדיין חברים",
    ug_onboarding: "הקמת אתר",
    ug_open: "פתיחה",
    ug_optional: "לא חובה",
    ug_organization: "ארגון",
    ug_outside_the_sites_you_administer: "מחוץ לאתרים שבניהולך",
    ug_owner: "אחראי",
    ug_password_required: "סיסמה *",
    ug_password_reset: "איפוס סיסמה",
    ug_external_apps_that_embed_the_viewer_site_grants_decide_which_sites_a_partner_can_show_allowed_origins_decide_where_their_frames_and_api_calls_may_come_from: "אפליקציות חיצוניות שמטמיעות את הצופה. הרשאות אתר קובעות אילו אתרים שותף יכול להציג; מקורות מורשים קובעים מאיפה מותר להציג את המסגרות ולשלוח קריאות API.",
    ug_new_partner_name: "שם שותף חדש",
    ug_create_partner: "יצירת שותף",
    ug_no_embed_partners_yet_create_one_to_give_an_external_app_access_to_a_site: "אין עדיין שותפי הטמעה. צרו שותף כדי לתת לאפליקציה חיצונית גישה לאתר.",
    ug_disabled: "מושבת",
    ug_disable: "השבתה",
    ug_enable: "הפעלה",
    ug_api_key_shown_once_copy_it_now_and_send_it_to_the_partner_securely: "מפתח API – מוצג פעם אחת בלבד. העתיקו אותו עכשיו ושלחו לשותף בדרך מאובטחת.",
    ug_copy: "העתקה",
    ug_dismiss: "סגירה",
    ug_api_keys: "מפתחות API",
    ug_no_keys: "אין מפתחות.",
    ug_created_date: "נוצר {date}",
    ug_revoked: "בוטל",
    ug_last_used_date: "שימוש אחרון {date}",
    ug_never_used: "לא נעשה שימוש",
    ug_mint_new_key: "יצירת מפתח חדש",
    ug_site_grants: "הרשאות אתר",
    ug_no_sites_granted: "לא הוענקו אתרים.",
    ug_site_to_grant: "אתר להרשאה",
    ug_choose_a_site_to_grant_ellipsis: "בחרו אתר להרשאה…",
    ug_grant: "הענקה",
    ug_allowed_origins: "מקורות מורשים",
    ug_no_origins_yet: "אין עדיין מקורות.",
    ug_allowed_origin: "מקור מורשה",
    ug_failed_to_load_embed_partners: "טעינת שותפי ההטמעה נכשלה",
    ug_operation_failed: "הפעולה נכשלה",
    ug_password_reset_name: "איפוס סיסמה: {name}",
    ug_people: "אנשים",
    ug_people_and_groups: "אנשים וקבוצות",
    ug_platform: "פלטפורמה",
    ug_platform_admins_only: "למנהלי פלטפורמה בלבד",
    ug_pick_a_role_or_a_duty: "בחרו תפקיד או אחריות.",
    ug_this_person_can_t_open_this_site_yet_give_them_a_role_on_the_site_first_then_a_duty_or_an_assignment_inside_it: "האדם הזה עדיין לא יכול לפתוח את האתר. קודם תנו לו תפקיד באתר, ואחר כך אחריות או שיוך בתוכו.",
    ug_this_group_isn_t_on_this_site_yet_add_the_group_to_the_site_first_then_a_duty_or_an_assignment_inside_it: "הקבוצה הזו עדיין לא באתר. קודם הוסיפו את הקבוצה לאתר, ואחר כך אחריות או שיוך בתוכו.",
    ug_this_group_also_opens_sites_you_don_t_manage_only_a_platform_admin_can_change_who_is_in_it: "הקבוצה הזו פותחת גם אתרים שאינם בניהולך. רק מנהל פלטפורמה יכול לשנות את חבריה.",
    ug_same_or_higher_role_than_yours_only_a_platform_admin_can_do_this: "תפקיד זהה לשלך או גבוה ממנו. רק מנהל פלטפורמה יכול לעשות זאת.",
    ug_this_person_also_uses_sites_you_don_t_manage_only_a_platform_admin_can_do_this_you_can_still_remove_their_roles_on_your_sites: "לאדם הזה יש גישה גם לאתרים שאינם בניהולך. רק מנהל פלטפורמה יכול לעשות זאת. עדיין אפשר להסיר את התפקידים שלו באתרים שלך.",
    ug_this_person_s_autodesk_sign_in_opens_models_on_sites_in_mabat_connect_autodesk_with_another_account_on_those_sites_first_or_deactivate_this_person_instead: "החיבור של האדם הזה ל-Autodesk פותח מודלים באתרים ב-Mabat. קודם חברו את Autodesk עם חשבון אחר באתרים האלה, או השביתו את האדם במקום למחוק.",
    ug_this_person_started_a_site_onboarding_so_they_cannot_be_deleted_deactivate_them_instead: "האדם הזה התחיל קליטה של אתר, ולכן אי אפשר למחוק אותו. אפשר להשבית אותו במקום.",
    ug_other_records_still_point_to_this_person_so_they_cannot_be_deleted_deactivate_them_instead: "רשומות אחרות עדיין מפנות לאדם הזה, ולכן אי אפשר למחוק אותו. אפשר להשבית אותו במקום.",
    ug_read_granted_on_value0: "הוענקה גישת קריאה אל {value0}",
    ug_read_only: "קריאה בלבד",
    ug_remove: "הסרה",
    ug_remove_assignment: "הסרת שיוך",
    ug_remove_the_assignment: "הסרת השיוך",
    ug_remove_value0_from_value1: "להסיר את {value0} מתוך {value1}?",
    ug_rename: "שינוי שם",
    ug_rename_group: "שינוי שם קבוצה",
    ug_reset_password: "איפוס סיסמה",
    ug_reset_the_password_for_value0_a_temporary_password_will_be_emailed_to_them: "לאפס את הסיסמה של {value0}? סיסמה זמנית תישלח אליו בדוא״ל.",
    ug_responder: "מטפל",
    ug_responsibility: "אחריות",
    ug_retry: "ניסיון חוזר",
    ug_revoke: "ביטול גישה",
    ug_revoke_access_to_value0: "לבטל את הגישה אל {value0}?",
    ug_role_for_this_assignment: "תפקיד עבור שיוך זה",
    ug_room: "חדר",
    ug_rooms_open: "חדרים (",
    ug_save: "שמירה",
    ug_saving_changes_ellipsis: "שומר שינויים…",
    ug_saving_ellipsis: "שומר…",
    ug_search_assets_by_name_ellipsis: "חיפוש נכסים לפי שם…",
    ug_search_groups: "חיפוש קבוצות",
    ug_search_groups_by_name_or_description_ellipsis: "חיפוש קבוצות לפי שם או תיאור…",
    ug_search_name_email_group_ellipsis: "חיפוש לפי שם, דוא״ל או קבוצה…",
    ug_search_people: "חיפוש אנשים",
    ug_search_people_ellipsis: "חיפוש אנשים…",
    ug_select_a_person: "בחירת אדם",
    ug_select_a_person_to_see_what_they_are_attached_to: "בחרו אדם להצגת הקבוצות, הגישה ותחומי האחריות שלו",
    ug_service_calls: "קריאות שירות",
    ug_sign_in: "כניסה",
    ug_site: "אתר",
    ug_site_membership_managed_on_the_group_s_sites: "חברות באתר — מנוהלת באתרי הקבוצה",
    ug_sites_open: "אתרים (",
    ug_some_people_could_not_be_added_retry_adds_only_the_remaining_people: "לא ניתן היה להוסיף חלק מהאנשים. ניסיון חוזר יוסיף רק את האנשים שנותרו.",
    ug_spatial_tree: "מיקומים",
    ug_system: "מערכת",
    ug_systems_open: "מערכות (",
    ug_these_changes_apply_only_to_this_assignment_access_granted_elsewhere_is_unchanged: "השינויים חלים רק על שיוך זה. גישה שהוענקה במקום אחר נשארת ללא שינוי.",
    ug_type_to_search_assets_ellipsis: "הקלידו לחיפוש נכסים…",
    ug_unknown: "לא ידוע",
    ug_users: "משתמשים",
    ug_via_group: "דרך {group}",
    ug_watcher: "עוקב",
    ug_what: "סוג השיוך",
    ug_withhold: "למנוע",
    ug_you: "אני",
    ug_you_manage_access_for_sites_people_outside_these_sites_are_read_only: "באפשרותך לנהל גישה עבור: {sites}. אנשים מחוץ לאתרים אלה מוצגים לקריאה בלבד.",
    ug_a_group: "קבוצה",
    ug_admin: "מנהל",
    ug_e_g_contractors: "לדוגמה: קבלנים",
    ug_inactive: "לא פעיל",
    ug_no_access: "ללא גישה",
    ug_other_site: "אתר אחר",
    ug_not_on_your_sites: "לא משויך לאתרים שבניהולך",
    ug_site_required: "אתר *",
    ug_no_site_yet: "עדיין בלי אתר",
    ug_they_start_on_this_site_as_a_user: "יתחילו באתר הזה בתפקיד משתמש.",
    ug_choose_the_site_this_person_starts_on: "בחרו את האתר שבו האדם יתחיל",
    ug_you_can_only_add_people_to_your_own_sites: "אפשר להוסיף אנשים רק לאתרים שבניהולך",
    ug_that_site_does_not_exist: "האתר הזה לא קיים",
    ug_site_member: "חבר באתר",
    ug_count_ability_changes: "{count} שינויי יכולות",
    ug_count_ability_changes_saved: "נשמרו {count} שינויי יכולות",
    ug_count_access_gaps: "{count} פערי גישה",
    ug_count_asset: "נכס אחד",
    ug_count_assets: "{count} נכסים",
    ug_count_group: "קבוצה אחת",
    ug_count_groups: "{count} קבוצות",
    ug_count_other_abilities_are_controlled_by_the_role_and_cannot_be_changed_here: "{count} יכולות נוספות נקבעות לפי התפקיד ולא ניתן לשנותן כאן.",
    ug_count_room: "חדר אחד",
    ug_count_rooms: "{count} חדרים",
    ug_count_site: "אתר אחד",
    ug_count_sites: "{count} אתרים",
    ug_count_system: "מערכת אחת",
    ug_count_systems: "{count} מערכות",
    ug_name_is_responsible_for_place_but_cannot_open_its_site_grant_site_access_or_remove_the_assignment: "{name} אחראי על {place}, אך אינו יכול לפתוח את האתר. יש להעניק גישה לאתר או להסיר את השיוך.",
    ug_people_people_assignments_assignments: "{people} אנשים · {assignments} שיוכים",
    ug_people_people_groups_groups: "{people} אנשים · {groups} קבוצות",
    ug_value0_added_to_value1: "{value0} נוספו אל {value1}",
    ug_value0_created: "{value0} נוצר",
    ug_value0_deleted: "{value0} נמחק",
    ug_value0_for_this_assignment: "{value0} עבור שיוך זה",
    ug_value0_removed_from_value1: "{value0} הוסר מתוך {value1}",
    ug_responsibility_gap: "⚠ פער גישה",
    ug_email_already_exists: "כתובת הדוא״ל כבר קיימת",
    ug_email_name_and_password_are_required: "יש להזין דוא״ל, שם וסיסמה",
    ug_enter_a_valid_email_address: "יש להזין כתובת דוא״ל תקינה",
    ug_password_must_contain_at_least_8_characters: "הסיסמה חייבת לכלול לפחות 8 תווים",
    ug_you_cannot_manage_a_user_with_equal_or_higher_privileges: "אין באפשרותך לנהל משתמש בעל הרשאות זהות או גבוהות יותר",
    ug_site_admin: "מנהל אתר",
    ug_data_creator: "עורך נתונים",
    ug_user: "משתמש",
    ug_no_people_available_to_add: "אין אנשים זמינים להוספה",
    dt_assets_unassignedSystem: "ללא מערכת",
    dt_tickets_matching: "קריאות תואמות",
    dt_tickets_filters: "סינון",
    dt_tickets_applyFilters: "הצג קריאות",
    dt_tickets_clearFilters: "נקה מסננים",
    dt_tickets_outsideQueue: "מחוץ לעמוד זה",
    dt_tickets_onPage: "בעמוד זה",
    dt_tickets_closeDetails: "סגור פרטי קריאה",
    dt_tickets_activity: "פעילות",
    dt_tickets_connections: "קישורים",
    dt_tickets_files: "קבצים",
    dt_tickets_moreActions: "עוד",
    dt_tickets_locate: "הצג בתלת־ממד",
    dt_tickets_editDetails: "עריכת פרטים",
    dt_tickets_details: "פרטי קריאה",
    dt_tickets_noDescription: "לא הוזן תיאור.",
    dt_tickets_action_in_progress: "התחל טיפול",
    dt_tickets_action_resolved: "סמן כנפתרה",
    dt_tickets_action_closed: "סגור קריאה",
    dt_tickets_action_cancelled: "בטל קריאה",
    dt_tickets_action_open: "פתח מחדש",

    headerSearch: 'חיפוש נכסים וחדרים',
    headerSelectSite: 'יש לבחור אתר לחיפוש',
    headerThisSite: 'באתר זה',
    headerSearching: 'מחפש…',
    headerNoResults: 'לא נמצאו תוצאות',
    headerSearchError: 'החיפוש נכשל. יש לשנות את החיפוש ולנסות שוב.',
    headerLanguage: 'שפה',
    headerAccount: 'חשבון',
    headerLogout: 'התנתקות',
    headerSwitchSite: 'החלפת אתר',
    headerSites: 'אתרים',
    headerFindSite: 'חיפוש אתר',
    headerManageSites: 'ניהול אתרים',
    navHintWalk: 'הליכה — גרירה להסתכלות · W A S D לתנועה · גלגלת לצעד · גרירה ימנית להחלקה הצידה',
    navHintOrbit: 'סיבוב — גרירה לסיבוב סביב הנקודה שתופסים · גלגלת לזום · גרירה ימנית להזזה',
    ctrlHelpTitle: 'שליטה',
    ctrlHelpWalk: 'הליכה',
    ctrlHelpOrbit: 'סיבוב',
    ctrlHelpCurrent: 'עכשיו',
    ctrlHelpSwitch: 'מחליפים עם הליכה / סיבוב בסרגל העליון. 🏠 מחזיר לתצוגת הבית שלך.',
    homeGo: 'בית',
    homeGoSaved: 'בית — התצוגה ששמרת',
    homeGoAuto: 'בית (אוטומטי) — אפשר לקבוע עם ▾',
    homeMenu: 'אפשרויות בית',
    homeSet: 'קבע את התצוגה הנוכחית כבית שלי',
    homeReset: 'איפוס לאוטומטי',
    homeSetDone: 'הבית נקבע לאתר הזה',
    homeResetDone: 'הבית אופס לאוטומטי',
    ctrlHelp_walkDrag_gesture: 'גרירה',
    ctrlHelp_walkDrag: 'הסתכלות מסביב',
    ctrlHelp_walkKeys_gesture: 'W A S D',
    ctrlHelp_walkKeys: 'הליכה',
    ctrlHelp_walkWheel_gesture: 'גלגלת',
    ctrlHelp_walkWheel: 'צעד קדימה / אחורה',
    ctrlHelp_walkRight_gesture: 'גרירה ימנית',
    ctrlHelp_walkRight: 'החלקה הצידה',
    ctrlHelp_walkQE_gesture: 'Q / E',
    ctrlHelp_walkQE: 'למטה / למעלה',
    ctrlHelp_walkShift_gesture: 'Shift',
    ctrlHelp_walkShift: 'מהר יותר',
    ctrlHelp_orbitDrag_gesture: 'גרירה',
    ctrlHelp_orbitDrag: 'סיבוב סביב הנקודה שלוחצים עליה',
    ctrlHelp_orbitWheel_gesture: 'גלגלת',
    ctrlHelp_orbitWheel: 'זום',
    ctrlHelp_orbitRight_gesture: 'גרירה ימנית',
    ctrlHelp_orbitRight: 'הזזה',
    ctrlHelpPanorama: 'פנורמה',
    ctrlHelp_panoDrag_gesture: 'גרירה',
    ctrlHelp_panoDrag: 'הסתכלות מסביב',
    ctrlHelp_panoWheel_gesture: 'גלגלת',
    ctrlHelp_panoWheel: 'זום',
    ctrlHelp_panoClick_gesture: 'לחיצה על 🌐',
    ctrlHelp_panoClick: 'מעבר לפנורמה הזו',
    ctrlHelp_panoFloor_gesture: 'Floor − / +',
    ctrlHelp_panoFloor: 'קומה אחרת',
    ctrlHelp_panoOverlay_gesture: 'כפתור 3D',
    ctrlHelp_panoOverlay: 'הצגת המודל מעל התמונה',
    ctrlHelpPanoFoot: 'חיפוש, מדידה ותכנון עובדים גם על התמונה.',
    ctrlHelpPlan: 'תוכנית קומה',
    ctrlHelp_planDrag_gesture: 'גרירה',
    ctrlHelp_planDrag: 'הזזת התוכנית',
    ctrlHelp_planWheel_gesture: 'גלגלת',
    ctrlHelp_planWheel: 'זום',
    ctrlHelp_planClick_gesture: 'לחיצה על 🌐',
    ctrlHelp_planClick: 'פתיחת הפנורמה הזו',
    ctrlHelp_planAsset_gesture: 'לחיצה על נכס',
    ctrlHelp_planAsset: 'פתיחת הפרטים שלו',
    ctrlHelp_planFloor_gesture: 'Floor − / +',
    ctrlHelp_planFloor: 'קומה אחרת',
    ctrlHelpPlanFoot: 'חיפוש, מדידה ותכנון עובדים גם על התוכנית.',
    measureTitle_distance: 'מרחק',
    measureTitle_angle: 'זווית',
    measureTitle_area: 'שטח',
    measureHow_distance: 'לחצו על שתי נקודות.',
    measureHow_angle: 'לחצו על שלוש נקודות.',
    measureHow_area: 'לחצו על נקודות.',
    measureDragKey: 'גרירה',
    measureDrag: 'סיבוב המבט',
    measureDelete: 'מחיקת המדידה המסומנת',
    measureEsc: 'סיום מדידה (מוחק הכול)',
    measureFromModel: 'המידות נלקחות מהמודל התלת־ממדי שמאחורי התמונה.',
    measureHowPlan_area: 'לחצו על נקודות; לסיום לחצו שוב על הנקודה הראשונה.',
    measureDragPlan: 'הזזת התוכנית',
    measureDeleteLast: 'מחיקת המדידה האחרונה',
    sitePlanReset: 'איפוס מיקום',
    sitePlanOnFloor: 'קומה',
    sitePlanKindFloor: 'תוכנית רצפה',
    sitePlanKindCeiling: 'תוכנית תקרה',
    sitePlanAdjust: 'כוונון גובה',
    sitePlanAdjustHint: 'למעלה (+) או למטה (−) מהגובה של הקומה',
    sitePlanPosition: 'מיקום',
    sitePlanMovedByHand: 'הוזז ידנית',
    sitePlanBringHere: 'הבאה לכאן',
    sitePlanAlignTwo: 'יישור לפי 2 נקודות',
    sitePlanRemoveConfirm: 'להסיר את התוכנית? הקובץ נשאר בקבצי האתר.',
    planningGoTo3D: 'הצגה בתלת־ממד',
    sitePlanMoveHint: 'הזזה על המבט: גררו בתוך המסגרת · הידית העגולה מסובבת · פינה משנה גודל',
    sitePlanAlignPlan1Flat: 'לחצו על נקודה מזוהה בשרטוט שלכם',
    sitePlanAlignModel1Flat: 'עכשיו לחצו על אותה נקודה בתוכנית הקומה שמתחת',
    sitePlanAlignPlan2Flat: 'לחצו על נקודה שנייה בשרטוט, רחוקה מהראשונה',
    sitePlanAlignModel2Flat: 'ואת הנקודה השנייה בתוכנית הקומה',
    headerNoSitesFound: 'לא נמצאו אתרים',
    headerUntitledAsset: 'נכס ללא שם',
    headerAssetFallback: 'נכס',
    headerRoleAdmin: 'מנהל מערכת',
    headerRoleSiteAdmin: 'מנהל אתר',
    headerRoleDataCreator: 'יוצר נתונים',
    headerRoleUser: 'משתמש',

    planningCm: 'ס״מ',
    planningTool: "תכנון",
    viewerBrowser: "דפדפן",
    planningWorkspace: "סביבת תכנון",
    planningMyObjects: "אובייקטי התכנון שלי",
    planningPrivate: "פרטי לך · צורות תכנון",
    planningSelected: "אובייקט תכנון נבחר",
    planningLayerHidden: "אובייקטי התכנון מוסתרים.",
    planningPlansHidden: "התוכניות מוסתרות.",
    planningShowLayer: "הצגת שכבה",
    planningCloseHint: "סגירת החלונית אינה מסתירה שכבות.",
    planningPlansVisibility: "הצגת תוכניות",
    planningOpenPlans: "פתיחת כלי התוכניות",

    dt_element_element: "אלמנט",
    dt_element_modelElement: "אלמנט מודל",
    dt_element_asset: "נכס",
    dt_element_noAsset: "ללא נכס",
    dt_element_location: "מיקום",
    dt_element_system: "מערכת",
    dt_element_unassigned: "לא משויך",
    dt_element_properties: "מאפייני המודל",
    dt_element_groups: "קבוצות",
    dt_element_values: "ערכים",
    dt_element_search: "חיפוש שם או ערך…",
    dt_element_noMatches: "לא נמצאו מאפיינים תואמים.",
    dt_element_noProperties: "אין מאפיינים זמינים בתצוגה זו.",
    dt_element_createHint: "יצירת נכס מאפשרת לקשר קריאות שירות, חדרים ומערכות.",
    dt_element_modelOnlyHint: "אלמנט המודל אינו מקושר לנכס.",
    dt_element_more: "פעולות נוספות",
    dt_element_assetActions: "פעולות נכס",
    dt_element_close: "סגירת החלונית",
    dt_element_drag: "גרירה להזזה",
    dt_element_canvasAvailable: "המודל נשאר זמין",
    dt_element_creating: "יוצר נכס…",
    dt_element_yes: "כן",
    dt_element_no: "לא",
    dt_element_action_focus: "הצגה בתלת־ממד",
    dt_element_action_copyId: "העתקת מזהה אלמנט",
    dt_element_action_createAsset: "יצירת נכס",
    dt_element_action_createAssetAndAddIot: "הוספת IoT (יוצר נכס)",
    dt_element_action_createAssetAndTicket: "יצירת קריאה (יוצר נכס)",
    dt_element_action_createTicket: "יצירת קריאה",
    dt_element_action_viewTickets: "קריאות לנכס",
    dt_element_action_addIot: "הוספת מכשיר IoT",
    dt_element_action_showInDiagram: "תרשים מערכת",
    dt_element_action_editAsset: "עריכת נכס",
    dt_element_action_deleteAsset: "מחיקת נכס",
    dt_tickets_issueType: "סוג קריאה",
    dt_tickets_unclassified: "ללא סיווג",
    dt_issue_incident: "תקלה",
    dt_issue_maintenance: "תחזוקה",
    dt_issue_inspection: "בדיקה",
    dt_issue_request: "בקשת שירות",
    dt_issue_other: "אחר",
    dt_tickets_tags: "תגיות",
    dt_tickets_tagsPlaceholder: "למשל: חוזר, בטיחות",
    dt_tickets_tagsHelp: "יש להפריד תגיות בפסיקים · עד 12 תגיות, 40 תווים לכל תגית.",
    dt_tickets_existingTags: "תגיות קיימות",
    dt_tickets_retryTags: "לא ניתן לטעון הצעות לתגיות · ניסיון נוסף",
    dt_tickets_related: "קריאות קשורות",
    dt_tickets_linkTicket: "קישור קריאה",
    dt_tickets_unlinkTicket: "ביטול קישור",
    dt_tickets_relatedHelp: "הקישור מופיע בשתי הקריאות. הסטטוס וההקצאה מנוהלים בנפרד.",
    dt_tickets_searchRelated: "חיפוש קריאה לפי כותרת או מספר",
    dt_tickets_relatedLoadFailed: "לא ניתן לטעון קריאות.",
    dt_tickets_relatedFailed: "לא ניתן לעדכן את קישור הקריאה.",
    dt_tickets_classification: "סיווג",
    dt_tickets_editClassification: "עריכת סיווג",
    dt_tickets_classificationFailed: "לא ניתן לשמור את הסיווג.",
    dt_tickets_removeTag: "הסרת תגית",
    dt_tickets_noTags: "אין תגיות",
    dt_tickets_allTypes: "כל סוגי הקריאות",
    dt_tickets_tagFilter: "סינון לפי תגית",
    dt_ev_ticketLinked: "קישר/ה קריאה",
    dt_ev_ticketUnlinked: "ביטל/ה קישור לקריאה",
    dt_tickets_moreFilters: 'מסננים נוספים',
    dt_tickets_equipmentError: 'לא ניתן לטעון ציוד.',
    dt_tickets_autoAssign: 'שיוך אוטומטי',
    dt_tickets_leaveUnassigned: 'ללא שיוך',
    dt_tickets_queue: 'משויך ל',
    dt_tickets_everyone: 'כולם',
    dt_tickets_mine: 'שלי',
    dt_tickets_myTeams: 'הצוותים שלי',
    dt_tickets_sort: 'מיון לפי',
    dt_tickets_newest: 'החדשות תחילה',
    dt_tickets_dueFirst: 'מועד יעד',
    dt_tickets_assetId: 'ציוד',
    dt_tickets_back: 'חזרה לקריאות',
    dt_tickets_previous: 'הקודם',
    dt_tickets_next: 'הבא',
    dt_tickets_retry: 'ניסיון חוזר',
    dt_tickets_scopeError: 'לא ניתן לטעון חדרים ומערכות.',
    dt_tickets_narrowSearch: 'קיימות תוצאות נוספות. יש למקד את החיפוש.',
    dt_tickets_contextHint: 'שיוכי הציוד מוצגים כהקשר. חדרים ומערכות שקושרו במפורש מגדירים במה הקריאה עוסקת.',
    dt_tickets_siteUnavailable: 'האתר של הקריאה אינו זמין.',
    dt_tickets_filterScope: 'חדר או מערכת',
    dt_tickets_filterEquipment: 'חיפוש ציוד…',
    dt_tickets_assignmentHint: 'שיוך אוטומטי מתבסס על ציוד המקור, או על הציוד הראשון שנבחר, או על החדר או המערכת שנבחרו.',
    // App - General
    mabat3dPortal: 'פורטל מבט תלת מימד',
    dashboard: 'לוח בקרה',
    tickets: 'קריאות שירות',
    viewer: 'צפייה',
    assets: 'נכסים',
    sites: 'אתרים',
    systems: 'מערכות',
    locations: 'מיקומים',
    users: 'משתמשים',
    settings: 'הגדרות',
    onboarding: 'קליטת פרויקטים',
    documentation: 'תיעוד',
    search: 'חיפוש',
    searchPlaceholder: 'חיפוש נכסים, חדרים, אתרים...',
    noAssetSelected: 'לא נבחר אתר',
    noViewerFilesAvailable: 'אין קבצי צפייה זמינים',
    noViewerFilesMessage: 'אין קבצים שניתן להציג בתצוגת 3D/2D/פנורמה.',
    upload: 'העלאה',
    delete: 'מחיקה',
    edit: 'עריכה',
    save: 'שמירה',
    cancel: 'ביטול',
    close: 'סגירה',
    loading: 'טוען...',
    error: 'שגיאה',
    success: 'הצלחה',

    // Sites page chrome (F-6)
    sitesLoadingAssets: 'טוען נכסים...',
    fileBadgeDefault: 'ברירת מחדל',
    fileBadgeLocked: 'נעול',
    fileBadgeLockedTitle: 'סנכרון אוטומטי כבוי - נעול לגרסה מסוימת',
    fileBadgePaused: 'מושהה',
    fileBadgePausedTitle: 'סנכרון אוטומטי מושהה',
    noIfcModelsUploaded: 'לא הועלו מודלי IFC',
    noModelsUploaded: 'לא הועלו מודלים',

    // POI Table chrome (F-6)
    poiSearchPlaceholder: 'חיפוש...',
    poiDeleteSelectedTitle: 'מחיקת נכסים שנבחרו',
    poiDeleteAllTitle: 'מחיקת כל הנכסים באתר זה',
    poiClosePanel: 'סגירת חלונית',
    poiSelectColumns: 'בחירת עמודות',
    poiSearchFields: 'חיפוש שדות...',
    poiSearchColumns: 'חיפוש עמודות...',
    poiValuePlaceholder: 'ערך...',
    poiActions: 'פעולות',
    poiDeleteAll: 'מחיקת הכל',
    poiDeleteAllConfirmTitle: 'למחוק את כל הנכסים?',
    poiDeleteSelectedConfirmTitle: 'למחוק את הנכסים שנבחרו?',
    poiDeleting: 'מוחק...',
    poiApply: 'החלה',
    poiApplying: 'מחיל...',
    poiSetSystems: 'שיוך מערכות',
    poiSetLocations: 'שיוך מיקומים',


    // Help Section
    hi: 'היי',
    needHelp: 'צריך עזרה?',
    pleaseCheckDocs: 'בדוק את המדריכים שלנו',

    // Login Page
    loginTitle: 'פורטל מבט תלת מימד',
    loginSubtitle: 'התחברות לחשבון שלך',
    emailAddress: 'כתובת אימייל',
    password: 'סיסמה',
    enterEmail: 'הזן את האימייל שלך',
    enterPassword: 'הזן את הסיסמה שלך',
    signingIn: 'מתחבר...',
    signIn: 'התחברות',
    pleaseEnterBoth: 'נא להזין אימייל וסיסמה',
    loginFailed: 'ההתחברות נכשלה. אנא בדוק את הפרטים.',
    // Forgot password (2026-09-28)
    forgotPassword: 'שכחת סיסמה?',
    forgotTitle: 'איפוס סיסמה',
    forgotSubtitle: 'הזן את האימייל של החשבון ונשלח לך קישור לבחירת סיסמה חדשה.',
    sendResetLink: 'שליחת קישור לאיפוס',
    sendingResetLink: 'שולח...',
    forgotSent: 'אם קיים חשבון עם האימייל הזה, שלחנו אליו קישור לאיפוס הסיסמה. הקישור תקף ל-30 דקות.',
    backToSignIn: 'חזרה להתחברות',
    enterValidEmail: 'נא להזין כתובת אימייל תקינה',
    resetUnavailable: 'איפוס סיסמה באימייל עדיין אינו זמין. נא לפנות למנהל המערכת.',
    tooManyRequests: 'יותר מדי בקשות. נסה שוב בעוד מספר דקות.',
    newPasswordTitle: 'בחירת סיסמה חדשה',
    savePassword: 'שמירת סיסמה',
    savingPassword: 'שומר...',
    resetLinkInvalid: 'הקישור אינו תקף או שפג תוקפו. בקש קישור חדש.',
    requestNewLink: 'בקשת קישור חדש',
    passwordUpdated: 'הסיסמה עודכנה. אפשר להתחבר עכשיו.',
    resetFailed: 'משהו השתבש. נסה שוב.',

    // Settings Panel - Profile
    profileInfo: 'פרטי פרופיל',
    editProfile: 'עריכת פרופיל',
    saving: 'שומר...',
    saveChanges: 'שמירת שינויים',
    profileUpdatedSuccess: 'הפרופיל עודכן בהצלחה!',
    profileUpdateFailed: 'עדכון הפרופיל נכשל',
    name: 'שם',
    fullName: 'שם מלא',
    email: 'אימייל',
    organization: 'ארגון',
    yourOrganizationName: 'שם הארגון שלך',
    role: 'תפקיד',

    // Settings Panel - Password
    passwordSecurity: 'אבטחת סיסמה',
    keepAccountSecure: 'שמור על החשבון שלך מאובטח באמצעות סיסמה חזקה וייחודית.',
    changePassword: 'שינוי סיסמה',
    currentPassword: 'סיסמה נוכחית',
    newPassword: 'סיסמה חדשה',
    confirmNewPassword: 'אישור סיסמה חדשה',
    enterNewPassword: 'הזן סיסמה חדשה (מינימום 8 תווים)',
    enterCurrentPassword: 'הזן סיסמה נוכחית',
    reenterNewPassword: 'הזן שוב את הסיסמה החדשה',
    confirmPassword: 'אשר סיסמה חדשה',
    passwordsDoNotMatch: 'הסיסמאות אינן תואמות',
    allFieldsRequired: 'כל שדות הסיסמה נדרשים',
    newPasswordsDoNotMatch: 'הסיסמאות החדשות אינן תואמות',
    passwordMinLength: 'הסיסמה החדשה חייבת להכיל לפחות 8 תווים',
    passwordTooWeak: 'הסיסמה חלשה מדי.',
    passwordChangedSuccess: 'הסיסמה שונתה בהצלחה!',
    passwordChangeFailed: 'שינוי הסיסמה נכשל',
    changing: 'משנה...',

    // Locations Page
    noAssetSelectedMessage: 'לא נבחר אתר. אנא בחר אתר מדף האתרים.',
    noSiteSelectedMessage: 'לא נבחר אתר. אנא בחר אתר מדף האתרים.',
    failedToFetchLocations: 'טעינת עץ המיקומים נכשלה',
    addLocation: 'הוספת מיקום',
    locationName: 'שם מיקום',
    locationType: 'סוג מיקום',
    building: 'בניין',
    buildingStorey: 'קומה',
    space: 'חלל',
    searchLocations: 'חיפוש במיקומים...',
    createLocation: 'יצירת מיקום',
    deleteAll: 'מחיקת הכל',
    retry: 'נסה שוב',
    searchPOIs: 'חיפוש נכסים...',

    // Systems Page - Categories
    categoryStructure: 'אלמנטים מבניים: קירות, קורות, עמודים, רצפות',
    categoryArchitectural: 'אלמנטים אדריכליים: דלתות, חלונות, מדרגות',
    categoryHVAC: 'מערכות מיזוג: תעלות, פתחי אוויר, דודים',
    categoryElectrical: 'מערכות חשמל: כבלים, תאורה, מתגים',
    categoryPlumbing: 'מערכות אינסטלציה: צנרת, אביזרים, שסתומים',
    categoryFireSafety: 'בטיחות אש: ספרינקלרים, אזעקות',
    categoryMechanical: 'ציוד מכני: חיישנים, בקרים',
    categoryFurniture: 'ריהוט ואלמנטי עיצוב',
    categorySpatial: 'אלמנטים מרחביים: חדרים, חללים',
    categoryOther: 'אלמנטים אחרים בבניין',

    // Revit Categories
    categoryAudioVisual: 'מכשירי אודיו וידאו',
    categoryCommunication: 'מכשירי תקשורת',
    categoryData: 'מכשירי נתונים',
    categoryElectricalEquipment: 'ציוד חשמלי',
    categoryElectricalFixtures: 'אביזרי חשמל',
    categoryLightingDevices: 'מכשירי תאורה',
    categoryLightingFixtures: 'גופי תאורה',
    categoryNurseCall: 'מערכת קריאה לאחות',
    categorySecurity: 'מכשירי אבטחה',
    categoryTelephone: 'מכשירי טלפון',
    categoryFireAlarm: 'מכשירי גילוי אש',
    categorySprinklers: 'ספרינקלרים',
    categoryPlumbingFixtures: 'אביזרי אינסטלציה',
    categoryAirTerminals: 'פתחי אוויר',
    categoryDuctAccessory: 'אביזרי תעלות',
    categoryDuctFitting: 'חיבורי תעלות',
    categoryFlexDuct: 'תעלות גמישות',
    categoryMechanicalEquipment: 'ציוד מכני',
    categoryPipeFitting: 'חיבורי צנרת',
    categoryPipeAccessory: 'אביזרי צנרת',
    categoryWalls: 'קירות',
    categoryFloors: 'רצפות',
    categoryCeilings: 'תקרות',
    categoryRoofs: 'גגות',
    categoryDoors: 'דלתות',
    categoryWindows: 'חלונות',
    categoryStairs: 'מדרגות',
    categoryRailings: 'מעקות',
    categoryColumns: 'עמודים',
    categoryBeams: 'שלד קורות',
    categoryFoundations: 'יסודות מבניים',
    categoryGenericModels: 'מודלים כלליים',
    categorySpecialtyEquipment: 'ציוד מיוחד',
    categoryCasework: 'נגרות',
    categoryRooms: 'חדרים',

    // View modes
    view3D: 'תלת מימד',
    view2D: 'דו מימד',
    viewPanorama: 'פנורמה',
    map2D: 'מפה דו מימדית',
    closeMinimap: 'סגור מפה',
    minimapOrbitNote: 'אין מפה מוקטנת במצב סיבוב',

    // User management
    userManagement: 'ניהול משתמשים',
    manageUsersRolesPermissions: 'ניהול משתמשים, תפקידים והרשאות',
    createUser: 'יצירת משתמש',
    addUser: 'הוספת משתמש',
    deleteUser: 'מחיקת משתמש',
    userRole: 'תפקיד משתמש',
    admin: 'מנהל',
    assetManager: 'מנהל אתרים',
    roleViewer: 'צפיין', // the RBAC role (a person). Was a second `viewer:` key, which
                    // silently overrode the nav tab's 'צפייה' — see the English block.
    searchUsers: 'חיפוש משתמשים לפי שם או אימייל...',
    noUsersFound: 'לא נמצאו משתמשים',
    active: 'פעיל',
    inactive: 'לא פעיל',
    manageGroups: 'ניהול קבוצות',
    resetPassword: 'איפוס סיסמה',
    deactivate: 'השבתה',
    activate: 'הפעלה',
    assetGroups: 'קבוצות אתרים',
    organizeAssetsGroups: 'ארגון אתרים לקבוצות לניהול הרשאות קל יותר',
    createGroup: 'יצירת קבוצה',
    noAssetGroupsYet: 'אין עדיין קבוצות אתרים. צור אחת כדי לארגן אתרים ולנהל הרשאות ביעילות.',
    manageAssets: 'ניהול אתרים',
    manageUsers: 'ניהול משתמשים',
    createNewUser: 'יצירת משתמש חדש',
    passwordResetSuccessful: 'איפוס סיסמה הצליח',
    passwordResetFor: 'הסיסמה אופסה עבור:',
    temporaryPassword: 'סיסמה זמנית:',
    importantSharePassword: 'אנא שתף סיסמה זו בצורה מאובטחת עם המשתמש. עליהם לשנות אותה מיד לאחר ההתחברות.',
    copyToClipboard: 'העתק ללוח',
    manageGroupsFor: 'ניהול קבוצות:',
    loadingPermissions: 'טוען הרשאות...',
    assetGroupPermissions: 'הרשאות קבוצות אתרים',
    groupName: 'שם קבוצה',
    permission: 'הרשאה',
    grantedBy: 'ניתנה על ידי',
    actions: 'פעולות',
    readOnly: 'קריאה בלבד',
    fullAccess: 'גישה מלאה',
    revoke: 'ביטול',
    noGroupPermissionsYet: 'עדיין לא הוקצו הרשאות קבוצה',
    grantNewPermission: 'הענקת הרשאה חדשה',
    grantPermissionTo: 'הענקת הרשאה ל',
    selectAssetGroup: 'בחר קבוצת אתרים',
    chooseGroup: 'בחר קבוצה...',
    permissionLevel: 'רמת הרשאה',
    userCanViewOnly: 'המשתמש יכול לצפות אך לא יכול לשנות או למחוק',
    userCanEdit: 'המשתמש יכול לצפות, לערוך ולמחוק',
    grantPermission: 'הענק הרשאה',
    assetsInGroup: 'אתרים בקבוצה',
    noAssetsInGroup: 'אין עדיין אתרים בקבוצה זו',
    addAssetsToGroup: 'הוספת אתרים לקבוצה',
    allAssetsInGroup: 'כל האתרים הזמינים כבר בקבוצה זו',
    remove: 'הסרה',
    add: 'הוספה',
    createAssetGroup: 'יצירת קבוצת אתרים',
    groupNameRequired: 'שם קבוצה',
    noDescription: 'אין תיאור',
    usersWithAccess: 'משתמשים עם גישה',
    noUsersHaveAccess: 'אין עדיין משתמשים עם גישה לקבוצה זו',
    addUsersToGroup: 'הוספת משתמשים לקבוצה',
    allUsersHaveAccess: 'לכל המשתמשים כבר יש גישה לקבוצה זו',
    minimumCharacters: 'מינימום 8 תווים',
    assignToGroup: 'הקצאה לקבוצה (אופציונלי)',
    noGroupAssignment: 'ללא הקצאה לקבוצה',

    // Language
    language: 'שפה',
    english: 'אנגלית',
    hebrew: 'עברית',

    // Theme
    theme: 'ערכת נושא',
    lightTheme: 'בהיר',
    darkTheme: 'כהה',

    // Sites page (formerly Assets)
    selectFiles: 'בחר קבצים',
    dragDrop: 'או גרור ושחרר קבצים כאן',
    supportedFormats: 'פורמטים נתמכים: IFC, PDF, E57, RCP, RCS, JPEG',
    noFiles: 'לא נבחרו קבצים',
    uploadedFiles: 'קבצים שהועלו',
    dataDirectoryFiles: 'קבצי ספריית נתונים',
    loadFromData: 'טען מהנתונים',
    sitesManagement: 'ניהול אתרים',
    searchSites: 'חיפוש אתרים...',
    createSite: 'יצירת אתר',
    currentSite: 'האתר הנוכחי',
    whatsNew_title: 'מה חדש בתאום הדיגיטלי',
    whatsNew_subtitle: 'עדכון ספטמבר 2026',
    whatsNew_look: 'מראה חדש',
    whatsNew_lookBody: 'כל המערכת עוצבה מחדש, בעברית ובאנגלית.',
    whatsNew_viewer: 'האתר שלך, בתוך הצפייה',
    whatsNew_viewerBody: 'קומות, מערכות ונכסים בחלונית "דפדפן" אחת.',
    whatsNew_assets: 'נכסים על המודל',
    whatsNew_assetsBody: 'דגלים לפי מערכת בתלת-ממד, במפה ובפנורמות.',
    whatsNew_tickets: 'קריאות שירות מהמודל',
    whatsNew_ticketsBody: 'פתחו קריאת שירות על כל רכיב, חדר או מערכת, עם תמונות.',
    whatsNew_systems: 'מערכות כתרשימים',
    whatsNew_systemsBody: 'כל מערכת נפתחת כתרשים שאפשר לחקור.',
    whatsNew_plans: 'תכניות ותכנון',
    whatsNew_plansBody: 'הניחו תכנית דו-ממדית על המודל ובדקו שפריטים חדשים נכנסים.',
    whatsNew_dashboard: 'לוח בקרה',
    whatsNew_dashboardBody: 'פעילות, קריאות שירות פתוחות ומצב החיישנים במקום אחד.',
    whatsNew_reopenHint: 'אפשר לפתוח את זה שוב מתפריט הפרופיל.',
    whatsNew_gotIt: 'הבנתי',
    whatsNew_menu: 'מה חדש',
    noSitesYet: 'עדיין אין אתרים',
    noSites: 'לא נמצאו אתרים',
    assetsManagement: 'ניהול נכסים',
    searchAssets: 'חיפוש נכסים...',
    createAsset: 'יצירת נכס',
    assetsCount: 'נכסים',
    noFilesUploaded: 'לא הועלו קבצים',
    created: 'נוצר',
    uploadFile: 'העלאת קובץ',
    description: 'תיאור',
    files: 'קבצים',
    file: 'קובץ',
    size: 'גודל',
    uploaded: 'הועלה',
    status: 'סטטוס',
    createNewSite: 'יצירת אתר חדש',
    siteName: 'שם האתר',
    siteDescription: 'תיאור האתר',
    editSite: 'עריכת אתר',
    createNewAsset: 'יצירת נכס חדש',
    assetName: 'שם הנכס',
    editAsset: 'עריכת נכס',
    uploadFileTo: 'העלאת קובץ ל',
    fileType: 'סוג קובץ',
    selectFile: 'בחירת קובץ',
    deleteConfirm: 'האם אתה בטוח שברצונך למחוק את הנכס?',
    deleteFileConfirm: 'האם אתה בטוח שברצונך למחוק את קובץ ה{fileType}?',
    enterAssetName: 'אנא הכנס שם נכס',
    uploadFailed: 'ההעלאה נכשלה',
    threeDModel: 'מודל תלת מימדי (IFC)',
    twoDPlans: 'תוכניות דו מימדיות',
    panoramas: 'פנורמות (E57)',
    documents: 'מסמכים (PDF)',
    noFileUploaded: 'לא הועלה {fileType}',
    panorama: 'פנורמה',

    // Settings/Appearance
    settingsTitle: 'הגדרות',
    appearance: 'מראה',
    themeMode: 'מצב עיצוב',
    currentlyUsing: 'כרגע משתמש ב',
    switchTo: 'החלף ל',
    dark: 'כהה',
    light: 'בהיר',
    interfaceLanguage: 'שפת ממשק',
    current: 'נוכחי',
    switchToLanguage: 'החלף ל',

    // Documentation Page
    docSubtitle: 'למדו כיצד להשתמש בפורטל מבט תלת מימד ביעילות',
    docOverview: 'סקירה',
    docFeatures: 'תכונות',
    docFAQ: 'שאלות נפוצות',
    docWelcome: 'ברוכים הבאים לפורטל מבט תלת מימד',
    docWelcomeDesc: 'פורטל מבט תלת מימד הוא פלטפורמה מקיפה לצפייה וניהול BIM (מידע בניין ממוחשב). היא מאפשרת לצפות, לנווט ולנהל מודלים תלת מימדיים של בניינים, תוכניות קומה דו מימדיות, תמונות פנורמיות ונכסים בסביבה משולבת.',
    docQuickStart3D: 'צפייה תלת מימדית',
    docQuickStart3DDesc: 'צפייה וניווט במודלי IFC',
    docQuickStartPOI: 'ניהול נכסים',
    docQuickStartPOIDesc: 'מעקב וניהול נכסים',
    docQuickStartPanorama: 'צפייה בפנורמה',
    docQuickStartPanoramaDesc: 'חקירת תמונות 360° מסריקות E57',
    docGettingStarted: 'צעדים ראשונים',
    docStep1: 'עברו לדף הנכסים ובחרו או צרו נכס',
    docStep2: 'העלו את הקבצים שלכם (IFC לתלת מימד, GEOTIFF לדו מימד, E57 לפנורמות)',
    docStep3: 'נווטו לדף הצפייה כדי לחקור את המודלים שלכם',
    docStep4: 'השתמשו בכפתורי מצב התצוגה (3D/2D/פנורמה) כדי לעבור בין תצוגות',
    docStep5: 'חפשו נכסים באמצעות סרגל החיפוש בכותרת',

    // Documentation Features
    docFeature3DViewer: 'צפייה תלת מימדית',
    docFeature3DViewerDesc: 'צפייה אינטראקטיבית בקבצי IFC עם ניווט עכבר ומקלדת. תומך במספר קבצי IFC לכל נכס.',
    docFeature2DViewer: 'תוכניות קומה דו מימדיות',
    docFeature2DViewerDesc: 'צפייה בתוכניות GEOTIFF עם תצוגת קואורדינטות. לחצו כדי לסנכרן את מיקום המצלמה עם הצפייה התלת מימדית.',
    docFeaturePanorama: 'צפייה בפנורמה',
    docFeaturePanoramaDesc: 'צפייה בתמונות פנורמיות 360° שחולצו מסריקות ענן נקודות E57. ניווט בין מיקומי פנורמה.',
    docFeaturePOI: 'נכסים',
    docFeaturePOIDesc: 'ייבוא, צפייה וניהול נכסים מקבצי CSV. חיפוש וניווט למיקומי נכסים בכל הצפיינים.',
    docFeatureAssets: 'ניהול אתרים',
    docFeatureAssetsDesc: 'יצירה, ארגון וניהול אתרי בניין עם סוגי קבצים מרובים. מעקב אחר ספירת קבצים וסטטיסטיקת נכסים.',
    docFeatureUsers: 'ניהול משתמשים',
    docFeatureUsersDesc: 'ניהול משתמשים, תפקידים (מנהל, מנהל אתרים, משתמש) והרשאות. יצירת קבוצות אתרים לבקרת גישה.',
    docFeatureI18n: 'תמיכה רב-לשונית',
    docFeatureI18nDesc: 'תמיכה מלאה באנגלית ועברית עם מעבר אוטומטי לפריסה מימין לשמאל.',
    docFeatureTheme: 'התאמת ערכת נושא',
    docFeatureThemeDesc: 'מעבר בין ערכות נושא בהירות וכהות בהתאם להעדפותיכם.',

    // Documentation FAQ
    docFAQCategoryGeneral: 'כללי',
    docFAQCategoryViewer: 'צפייה',
    docFAQCategoryAssets: 'אתרים',
    docFAQCategoryPOI: 'נכסים',
    docFAQCategoryUsers: 'משתמשים והרשאות',
    docFAQWhatIs: 'מהו פורטל מבט תלת מימד?',
    docFAQWhatIsAnswer: 'פורטל מבט תלת מימד הוא פלטפורמת צפייה וניהול BIM מבוססת אינטרנט המאפשרת לצוותים לצפות במודלי בניין תלת מימדיים, תוכניות קומה דו מימדיות ותמונות פנורמיות בסביבה משולבת אחת.',
    docFAQFileFormats: 'אילו פורמטים נתמכים?',
    docFAQFileFormatsAnswer: 'פורמטים נתמכים כוללים: IFC (מודלי BIM תלת מימדיים), GEOTIFF (תוכניות קומה דו מימדיות), E57 (עננות נקודות פנורמיות), CSV (נתוני נכסים), DWG/DXF (שרטוטי CAD).',
    docFAQHowToView3D: 'כיצד אני צופה במודל תלת מימדי?',
    docFAQHowToView3DAnswer: 'בחרו נכס עם קובץ IFC, עברו לדף הצפייה וודאו שמצב תלת מימד נבחר. המודל ייטען אוטומטית. השתמשו בעכבר לסיבוב, גלילה לזום ולחיצה ימנית לגרירה.',
    docFAQHowToNavigate: 'כיצד אני מנווט בצפייה התלת מימדית?',
    docFAQHowToNavigateAnswer: 'לחצו וגררו לסיבוב התצוגה. גלילה להתקרבות/התרחקות. לחיצה ימנית וגרירה להזזה. תוכלו גם להשתמש במפה המוקטנת בפינה כדי ללחוץ על מיקומים.',
    docFAQPanoramaSync: 'כיצד עובד סנכרון הפנורמה?',
    docFAQPanoramaSyncAnswer: 'בעת צפייה בפנורמות, המפה המוקטנת מציגה את מיקומכם בתוכנית הקומה הדו מימדית. לחצו על מיקומים שונים בלוח הניווט או במפה המוקטנת כדי לקפוץ למיקום פנורמה זה.',
    docFAQHowToUpload: 'כיצד אני מעלה קבצים לנכס?',
    docFAQHowToUploadAnswer: 'עברו לדף הנכסים, לחצו על נכס ואז השתמשו בכפתור העלאה כדי להוסיף קבצים. בחרו את סוג הקובץ (IFC, GEOTIFF, E57 וכו\') ובחרו את הקובץ שלכם.',
    docFAQMultipleIFC: 'האם אפשר לקבל מספר קבצי IFC בנכס אחד?',
    docFAQMultipleIFCAnswer: 'כן! תוכלו להעלות מספר קבצי IFC לנכס יחיד. השתמשו בכפתור "הגדר כברירת מחדל" כדי לבחור איזה קובץ IFC ייטען כברירת מחדל בצפייה.',
    docFAQWhatArePOIs: 'מהם נכסים?',
    docFAQWhatArePOIsAnswer: 'נכסים הם מיקומים ספציפיים בבניין שלכם שאתם רוצים לעקוב אחריהם, כגון ציוד, חיישנים או נקודות תחזוקה. הם מיובאים דרך קבצי CSV וניתן לצפות בהם בדף מערכות.',
    docFAQHowToSearchPOIs: 'כיצד אני מחפש נכסים?',
    docFAQHowToSearchPOIsAnswer: 'השתמשו בסרגל החיפוש בכותרת כדי לחפש נכסים לפי שם. לחצו על תוצאה כדי לנווט למיקום זה בצפייה. תוכלו גם לעיין בנכסים לפי קטגוריה בדף מערכות.',
    docFAQUserRoles: 'מהם תפקידי המשתמש השונים?',
    docFAQUserRolesAnswer: 'מנהל: גישה מלאה לכל התכונות וניהול משתמשים. מנהל נכסים: יכול לנהל נכסים ומשתמשים בתוך הקבוצות שלו. משתמש: יכול לצפות בנכסים שקיבל גישה אליהם.',
    docNeedHelp: 'צריכים עוד עזרה?',
    docContactSupport: 'צרו קשר עם התמיכה',

    // Misc
    logout: 'התנתקות',
    profile: 'פרופיל',
    notifications: 'התראות',
    help: 'עזרה',

    // ===== DT Alpha pages =====
    dt_loading: 'טוען…',
    app_newVersionReady: 'גרסה חדשה מוכנה',
    app_reload: 'טען מחדש',
    app_pageCouldNotOpen: 'לא ניתן לפתוח את הדף',
    dt_dismiss: 'סגירה',
    dt_cancel: 'ביטול',
    dt_save: 'שמירה',
    dt_delete: 'מחיקה',
    dt_edit: 'עריכה',
    dt_create: 'יצירה',
    dt_rotateToken: 'החלפת אסימון',
    dt_copyToken: 'העתקת אסימון',
    dt_hide: 'הסתרה',
    dt_reveal: 'חשיפה',
    dt_status_open: 'פתוחה',
    dt_status_assigned: 'שויכה',
    dt_status_in_progress: 'בטיפול',
    dt_status_resolved: 'טופלה',
    dt_status_closed: 'סגורה',
    dt_status_cancelled: 'בוטלה',
    dt_priority_low: 'נמוכה',
    dt_priority_normal: 'רגילה',
    dt_priority_high: 'גבוהה',
    dt_priority_critical: 'קריטית',
    dt_origin_manual: 'ידני',
    dt_origin_3d_element: 'אלמנט תלת-ממד',
    dt_origin_diagram: 'תרשים',
    dt_origin_iot: 'IoT',
    dt_devstatus_ok: 'תקין',
    dt_devstatus_alert: 'התראה',
    dt_devstatus_offline: 'לא מקוון',
    dt_devstatus_never: 'אין נתונים עדיין',
    dt_devstatus_disabled: 'מושבת',
    dt_dash_openTickets: 'קריאות פתוחות',
    dt_dash_updatedNow: 'עודכן כעת',
    dt_dash_unassigned: 'לא משויך',
    dt_dash_needAttentionNow: 'דורש טיפול',
    dt_dash_reportingNormally: 'מדווחים תקין',
    dt_dash_nodataShort: 'אין נתונים',
    dt_dash_highCritical: 'גבוהה / קריטית',
    dt_dash_devicesAttention: 'חיישנים הדורשים טיפול',
    dt_dash_iotDevices: 'חיישני IoT',
    dt_dash_recentActivity: 'פעילות אחרונה',
    dt_dash_deviceHealth: 'תקינות חיישנים',
    dt_dash_allTickets: 'כל הקריאות',
    dt_dash_allDevices: 'כל החיישנים',
    dt_dash_selectSite: 'בחרו אתר כדי לראות את לוח הבקרה שלו.',
    dt_dash_noActivity: 'אין עדיין פעילות — שינויים בקריאות והתראות IoT באתר זה יופיעו כאן.',
    dt_dash_nothingOpen: 'אין קריאות פתוחות.',
    dt_dash_noDevices: 'לא רשומים חיישנים.',
    dt_dash_boundLightUp: 'חיישנים המקושרים לאלמנט מוארים בתרשים הקשרים.',
    dt_dash_allReporting: 'כל החיישנים מדווחים כרגיל.',
    dt_tickets_search: 'חיפוש קריאות…',
    dt_tickets_new: '+ קריאה חדשה',
    dt_tickets_activeDefault: 'פעילות (ברירת מחדל)',
    dt_tickets_allStatuses: 'כל הסטטוסים',
    dt_tickets_allPriorities: 'כל העדיפויות',
    dt_tickets_unassigned: 'לא משויך',
    dt_tickets_delete: 'מחיקת קריאה',
    dt_tickets_deleteConfirm: 'למחוק את הקריאה? התגובות, המשויכים והקבצים המצורפים יימחקו איתה. לא ניתן לשחזר.',
    dt_tickets_post: 'שליחה',
    dt_tickets_addComment: 'הוספת תגובה…',
    dt_tickets_showIn3d: 'הצג בתלת-ממד',
    dt_tickets_timeline: 'ציר זמן',
    dt_tickets_loading: 'טוען קריאות…',
    dt_tickets_none: 'אין עדיין קריאות — פתחו קריאה באמצעות ״קריאה חדשה״, מאלמנט תלת-ממד, או מהדמיה בתרשים.',
    dt_tickets_noneMatch: 'אין קריאות התואמות לסינון.',
    dt_tickets_selectSite: 'בחרו אתר כדי לראות את הקריאות שלו.',
    dt_tickets_of: 'מתוך',
    dt_tickets_active: 'פעילות',
    dt_tickets_createdVia: 'נוצרה דרך',
    dt_tickets_selectTicket: 'בחרו קריאה כדי לראות את פרטיה.',
    dt_tickets_emptyTitle: 'אין עדיין קריאות',
    dt_tickets_emptyHint: 'פתחו קריאה בעזרת הכפתור מטה, מאלמנט תלת-ממד, או מהדמיה בתרשים.',
    dt_tickets_touches: 'מה הקריאה נוגעת בו',
    dt_tickets_linkAsset: '+ קישור נכס',
    dt_tickets_unlinkAsset: 'ניתוק הנכס מהקריאה',
    dt_tickets_link: 'קישור',
    dt_tickets_searchAssets: 'חיפוש נכסים…',
    dt_tickets_noAssets: 'אין עדיין נכסים מקושרים לקריאה.',
    dt_tickets_primary: 'ראשי',
    dt_tickets_linked: 'מקושר',
    dt_tickets_location: 'מיקום',
    dt_tickets_system: 'מערכת',
    dt_tickets_model: 'מודל',
    dt_tickets_element: 'אלמנט',
    dt_tickets_fromPrimary: 'מהנכס הראשי',
    dt_tickets_moreSystems: 'מערכות נוספות',
    dt_tickets_diagram: 'תרשים מערכת',
    dt_tickets_assetsHere: 'נכסים כאן',
    dt_tickets_openInDiagrams: 'פתיחה בתרשימים →',
    dt_tickets_thisTicket: 'הקריאה הזו',
    dt_tickets_legendTicket: 'נכס הקריאה',
    dt_tickets_legendLinked: 'מקושר',
    dt_tickets_isOn: 'נמצא ב־',
    dt_tickets_assignee: 'משויך ל',
    dt_tickets_aUser: 'משתמש',
    dt_tickets_aGroup: 'קבוצה',
    dt_tickets_addAssignee: 'הוספת משויך נוסף',
    dt_tickets_pickMember: '— בחרו משתמש או קבוצה —',
    dt_tickets_clear: 'ניקוי',
    dt_tickets_group: 'קבוצה',
    dt_tickets_people: 'אנשים',
    dt_tickets_sites: 'אתרים',
    dt_tickets_noAssignees: 'אף אחד עדיין לא משויך לקריאה.',
    dt_tickets_forThisAsset: 'קריאות עבור נכס זה',
    dt_tickets_noneForAsset: 'אין קריאות המפנות לנכס זה.',
    dt_ev_assetLinked: 'קישר/ה נכס',
    dt_ev_assetUnlinked: 'ניתק/ה נכס',
    dt_ev_opened: 'פתח/ה את הקריאה',
    dt_ev_status: 'שינה/תה סטטוס',
    dt_ev_priority: 'שינה/תה עדיפות',
    dt_ev_assigned: 'שייך/כה את הקריאה',
    dt_ev_unassigned: 'ביטל/ה שיוך',
    dt_ev_edited: 'ערך/ה את הקריאה',
    dt_ev_system: 'מערכת',
    dt_ct_title: 'קריאה חדשה',
    dt_ct_titleField: 'כותרת',
    dt_ct_desc: 'תיאור',
    dt_ct_priority: 'עדיפות',
    dt_ct_assignee: 'משויך ל',
    dt_ct_titlePh: 'מה דורש טיפול?',
    dt_ct_descPh: 'פרטים, תסמינים, מיקום…',
    dt_ct_create: 'יצירת קריאה',
    dt_ct_creating: 'יוצר…',
    dt_notif_title: 'התראות',
    dt_notif_markAll: 'סמן הכל כנקרא',
    dt_notif_new: 'חדשות',
    dt_notif_prefs: 'העדפות התראות',
    dt_notif_inApp: 'התראות באפליקציה',
    dt_notif_email: 'דוא״ל',
    dt_notif_empty: 'אין עדיין התראות — פעילות קריאות והתראות באתרים שלכם יופיעו כאן.',
    dt_notif_newTicket: 'קריאה חדשה',
    dt_notif_ticket: 'קריאה',
    dt_notif_commentOn: 'תגובה לקריאה',
    dt_notif_iotAlert: 'התראת IoT',
    dt_notif_resolved: 'הקריאה טופלה',
    dt_notif_overdue: 'קריאה באיחור',
    dt_notif_needsAttention: 'קריאה דורשת תשומת לב',
    dt_notif_dueAt: 'מועד יעד:',
    dt_notif_attentionSince: 'נדרשת תשומת לב מאז:',
    dt_notif_deviceSilent: 'המכשיר הפסיק לדווח',
    dt_notif_lastReading: 'קריאה אחרונה:',
    dt_notif_activityUpdate: 'עדכון פעילות',
    dt_int_delivery_pending: 'ממתין לשליחה',
    dt_int_delivery_delivered: 'נמסר',
    dt_int_delivery_failed: 'השליחה נכשלה',
    dt_int_delivery_unknown: 'מצב השליחה אינו זמין',
    dt_notif_updated: 'עודכנה',
    dt_notif_reassigned: 'שויכה מחדש',
    dt_notif_unassigned: 'בוטל שיוך',
    dt_dev_title: 'חיישני IoT',
    dt_dev_registered: 'רשומים',
    dt_dev_register: '+ רישום חיישן',
    dt_dev_loading: 'טוען חיישנים…',
    dt_dev_selectSite: 'בחרו אתר כדי לנהל את חיישני ה-IoT שלו.',
    dt_dev_none: 'אין עדיין חיישנים. רשמו חיישן, קשרו אותו לאלמנט, והפנו את החיישן ל-webhook הקליטה — חיישנים מקושרים מאירים את האלמנט שלהם בתרשים הקשרים.',
    dt_dev_boundTo: 'מקושר ל:',
    dt_dev_unbound: '— לא מקושר',
    dt_dev_lastReading: 'קריאה אחרונה:',
    dt_dev_never: 'מעולם',
    dt_dev_thresholds: 'ספי התראה:',
    dt_dev_showToken: 'הצגת אסימון קליטה',
    dt_dev_ext: 'ext:',
    dt_dev_name: 'שם',
    dt_dev_extKey: 'מזהה חיצוני (יצרן/מק״ט — אופציונלי)',
    dt_dev_boundAsset: 'אלמנט מקושר',
    dt_dev_locNote: 'הערת מיקום',
    dt_dev_alertThresholds: 'ספי התראה',
    dt_dev_metricPh: 'מדד (למשל טמפרטורה)',
    dt_dev_min: 'מינ׳',
    dt_dev_max: 'מקס׳',
    dt_dev_addThreshold: '+ סף התראה',
    dt_dev_enabled: 'מופעל (חיישנים מושבתים דוחים קליטה)',
    dt_dev_registerBtn: 'רישום',
    dt_dev_editTitle: 'עריכת',
    dt_dev_regTitle: 'רישום חיישן',
    dt_dev_confirmRotate: 'להחליף את אסימון הקליטה של חיישן זה? האסימון הנוכחי יפסיק לפעול מיידית.',
    dt_dev_confirmDelete: 'למחוק חיישן זה ואת כל קריאותיו?',
    dt_int_title: 'אינטגרציות',
    dt_int_connectors: 'מחברים',
    dt_int_connector: 'מחבר',
    dt_int_add: '+ הוספת מחבר',
    dt_int_intro: 'שיקוף פעילות קריאות למערכת CRM חיצונית (webhook יוצא) ואפשרות לעדכן קריאות בחזרה (נקודת קצה נכנסת). Mabat3D נשארת מקור האמת.',
    dt_int_loading: 'טוען אינטגרציות…',
    dt_int_none: 'אין עדיין מחברים.',
    dt_int_selectSite: 'בחרו אתר כדי לנהל את האינטגרציות שלו.',
    dt_int_enabled: 'מופעל',
    dt_int_disabled: 'מושבת',
    dt_int_outbound: 'יוצא:',
    dt_int_inbound: 'נכנס:',
    dt_int_notSet: '— לא הוגדר',
    dt_int_events: 'אירועים:',
    dt_int_lastDelivery: 'משלוח אחרון:',
    dt_int_showEndpoint: 'הצגת נקודת קצה נכנסת + אסימון',
    dt_int_addTitle: 'הוספת מחבר',
    dt_int_editTitle: 'עריכת',
    dt_int_name: 'שם',
    dt_int_url: 'כתובת webhook יוצא',
    dt_int_secret: 'מפתח חתימה (אופציונלי — נשלח כ-HMAC-SHA256 בכותרת X-Mabat-Signature',
    dt_int_secretKeep: '; שדה ריק משאיר את המפתח הקיים',
    dt_int_deliveredEvents: 'אירועים לשליחה',
    dt_int_noneSelected: 'לא נבחרו אירועים — דבר לא יישלח (המחבר למעשה מושהה).',
    dt_int_ev_all: 'כל אירועי הקריאות',
    dt_int_ev_created: 'קריאה נוצרה',
    dt_int_ev_updated: 'קריאה עודכנה',
    dt_int_ev_commented: 'תגובה לקריאה',
    dt_int_ev_iot: 'התראות IoT',
    dt_int_confirmRotate: 'להחליף את האסימון הנכנס של מחבר זה? האסימון הנוכחי יפסיק לפעול מיידית.',
    dt_int_confirmDelete: 'למחוק אינטגרציה זו?',
    dt_int_pausedNone: 'ללא (מושהה)',
    dt_dg_addAsset: '+ אלמנט',
    dt_dg_addSystem: '+ מערכת',
    dt_dg_addJunction: '+ צומת',
    dt_dg_seedFromSystem: 'יצירה ממערכת…',
    dt_dg_allSystems: 'כל המערכות',
    dt_dg_allKinds: 'כל הסוגים',
    dt_dg_lens: 'עדשה:',
    dt_dg_only: 'רק:',
    dt_dg_fullLabels: 'שמות מלאים',
    dt_dg_fullLabelsTip: 'גלישת שמות ארוכים במקום קיצוצם',
    dt_dg_panel: 'פאנל',
    dt_dg_newConnections: 'חיבורים חדשים:',
    dt_dg_save: 'שמירה',
    dt_dg_saving: 'שומר…',
    dt_dg_saved: 'נשמר',
    dt_dg_canonical: 'ראשי',
    dt_dg_edge_flow: 'זרימה',
    dt_dg_edge_dependency: 'תלות',
    dt_dg_edge_physical: 'פיזי',
    dt_dg_edge_condition: 'תנאי',
    dt_dg_kind_asset: 'אלמנט',
    dt_dg_kind_system: 'מערכת',
    dt_dg_kind_logical: 'צומת',
    dt_dg_tab_simulate: 'הדמיה',
    dt_dg_tab_lint: 'תקינות',
    dt_dg_fail: 'כשל',
    dt_dg_disconnect: 'ניתוק',
    dt_dg_reset: 'איפוס',
    dt_dg_impact: 'השפעה',
    dt_dg_affected: 'מושפעים',
    dt_dg_seeds: 'מקור/ות',
    dt_dg_highlight3d: 'הדגשת השפעה בתלת-ממד',
    dt_dg_isolated: 'דבר נוסף אינו מושפע — המקורות מבודדים או שקיים גיבוי.',
    dt_dg_reason_flow: 'אבדן כל האספקה',
    dt_dg_reason_physical: 'התפשטות פיזית',
    dt_dg_reason_dependency: 'תלות שנפלה',
    dt_dg_noSim: 'לא מתבצעת הדמיה.',
    dt_dg_noIssues: '✓ לא נמצאו בעיות מבניות.',
    dt_dg_checkLinks: 'בדיקת קישורי אלמנטים (טוען רשימת אלמנטים)',
    dt_dg_seedTitle: 'יצירה ממערכת',
    dt_dg_seedIntro: 'מוסיף צומת אלמנט לכל אלמנט במערכת שנבחרה (לא מנוחשים חיבורים).',
    dt_dg_seedLoading: 'טוען מערכות…',
    dt_dg_seedNone: 'לא נמצאו ערכי מערכת באלמנטים של אתר זה.',
    dt_dg_assets: 'אלמנטים',
    dt_dg_pickAsset: 'קישור אלמנט',
    dt_dg_searchAssets: 'חיפוש אלמנטים…',
    dt_dg_noMatchAssets: 'אין אלמנטים תואמים.',
    dt_dg_loadingAssets: 'טוען אלמנטים…',
    dt_dg_loadingDiagram: 'טוען תרשים…',
    dt_dg_noDiagram: 'לא קיים תרשים לאתר זה עדיין.',
    dt_dg_call: 'קריאה',
    dt_dg_callTip: 'פתיחת קריאה עבור השפעה זו',
    dt_dg_3d: 'תלת-ממד',
    dt_dg_3dTip: 'הצגת אלמנט זה במציג התלת-ממד',
    dt_dg_selectSite: 'בחרו אתר כדי לראות את תרשים הקשרים שלו.',
    dt_dg_diagrams: 'תרשימים',
    dt_dg_chooseDiagram: 'בחירת תרשים',
    dt_dg_unsavedSwitch: 'יש שינויים שלא נשמרו — לבטל אותם ולעבור לתרשים אחר?',
    dt_dg_createSiteDiagram: 'יצירת תרשים האתר',
    dt_dg_firstInfo: 'התרשים הראשון הופך לתרשים הקשרים הראשי של האתר — הבסיס להדמיית כשלים, ובהמשך ל-IoT וקריאות שירות.',
    dt_dg_saveChanges: 'שמירת שינויים',
    dt_dg_cannotSave: 'לא ניתן לשמור',
    dt_dg_saveFailed: 'השמירה נכשלה',
    dt_dg_saveConflict: 'התרשים שונה על ידי מישהו אחר מאז שטענתם אותו.\n\nאישור = דריסה בגרסה שלכם · ביטול = שמירת שלהם (טעינה מחדש)',
    dt_dg_nodeLabelPrompt: 'שם צומת:',
    dt_dg_markNodes: 'בחרו צמתים בקנבס, ואז סמנו אותם:',
    dt_dg_showFirst50: 'מוצגות 50 ההתאמות הראשונות — צמצמו את החיפוש.',
    dt_dg_closePanel: '\u05e1\u05d2\u05d9\u05e8\u05ea \u05e4\u05d0\u05e0\u05dc',

    // Responsibility editor (scope_grants, migration 042)
    dt_resp_title: '\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea',
    dt_resp_subtitle: '\u05d7\u05d1\u05e8\u05d5 \u05de\u05e9\u05ea\u05de\u05e9 \u05d0\u05d5 \u05e7\u05d1\u05d5\u05e6\u05d4 \u05dc\u05de\u05e2\u05e8\u05db\u05ea \u05d0\u05d5 \u05dc\u05d7\u05d3\u05e8, \u05e2\u05dd \u05ea\u05e4\u05e7\u05d9\u05d3 \u05d5\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea',
    dt_resp_selectSite: '\u05d1\u05d7\u05e8\u05d5 \u05d0\u05ea\u05e8 \u05db\u05d3\u05d9 \u05dc\u05e0\u05d4\u05dc \u05de\u05d9 \u05d0\u05d7\u05e8\u05d0\u05d9 \u05e2\u05dc \u05de\u05d4.',
    dt_resp_loading: '\u05d8\u05d5\u05e2\u05df \u05de\u05e2\u05e8\u05db\u05d5\u05ea \u05d5\u05d0\u05e0\u05e9\u05d9\u05dd\u2026',
    dt_resp_loadFailed: '\u05d8\u05e2\u05d9\u05e0\u05ea \u05e0\u05ea\u05d5\u05e0\u05d9 \u05d4\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u05e0\u05db\u05e9\u05dc\u05d4',
    dt_resp_attachFailed: '\u05d4\u05d7\u05d9\u05d1\u05d5\u05e8 \u05e0\u05db\u05e9\u05dc',
    dt_resp_revokeFailed: '\u05d4\u05d1\u05d9\u05d8\u05d5\u05dc \u05e0\u05db\u05e9\u05dc',
    dt_resp_newAttachment: '\u05d7\u05d9\u05d1\u05d5\u05e8 \u05d7\u05d3\u05e9',
    dt_resp_step1: '\u05dc\u05de\u05d4 \u05d6\u05d4 \u05de\u05d7\u05d5\u05d1\u05e8?',
    dt_resp_step1Sub: '\u05d4\u05de\u05d5\u05e7\u05d3',
    dt_resp_step2: '\u05de\u05d9?',
    dt_resp_step2Sub: '\u05d4\u05d7\u05d1\u05e8',
    dt_resp_step3: '\u05e2\u05dd \u05d0\u05d9\u05d6\u05d4 \u05db\u05d5\u05d7 \u05d5\u05d0\u05d9\u05d6\u05d5 \u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea?',
    dt_resp_aSystem: '\u05de\u05e2\u05e8\u05db\u05ea',
    dt_resp_aRoom: '\u05d7\u05d3\u05e8',
    dt_resp_aSite: '\u05d0\u05ea\u05e8',
    dt_resp_oneAsset: '\u05e0\u05db\u05e1 \u05d1\u05d5\u05d3\u05d3',
    dt_resp_otherScopes: '\u05d2\u05dd \u05d0\u05dc\u05d5 \u05de\u05d5\u05e7\u05d3\u05d9\u05dd \u05d7\u05d5\u05e7\u05d9\u05d9\u05dd \u2014 \u05d0\u05d5\u05ea\u05d4 \u05e9\u05d5\u05e8\u05d4, \u05d4\u05e4\u05e0\u05d9\u05d4 \u05d0\u05d7\u05e8\u05ea',
    dt_resp_subtree: '\u05ea\u05ea-\u05e2\u05e5',
    dt_resp_wholeStorey: '\u05e7\u05d5\u05de\u05d4 \u05e9\u05dc\u05de\u05d4',
    dt_resp_noOwner: '\u05d0\u05d9\u05df \u05d1\u05e2\u05dc\u05d9\u05dd',
    dt_resp_noSystems: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05de\u05e2\u05e8\u05db\u05d5\u05ea \u05d1\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4.',
    dt_resp_noRooms: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05d7\u05d3\u05e8\u05d9\u05dd \u05d1\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4.',
    dt_resp_aUser: '\u05de\u05e9\u05ea\u05de\u05e9',
    dt_resp_aGroup: '\u05e7\u05d1\u05d5\u05e6\u05d4',
    dt_resp_noUsers: '\u05dc\u05d0\u05e3 \u05d0\u05d7\u05d3 \u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05d2\u05d9\u05e9\u05d4 \u05dc\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4.',
    dt_resp_noGroups: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05e7\u05d1\u05d5\u05e6\u05d4 \u05d1\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4. \u05d4\u05d5\u05e1\u05d9\u05e4\u05d5 \u05e7\u05d1\u05d5\u05e6\u05d4 \u05dc\u05d0\u05ea\u05e8 \u05e7\u05d5\u05d3\u05dd, \u05d1\u05e2\u05de\u05d5\u05d3 \u05d4\u05de\u05e9\u05ea\u05de\u05e9\u05d9\u05dd.',
    dt_resp_here: '\u05db\u05d0\u05df',
    dt_resp_people: '\u05d0\u05e0\u05e9\u05d9\u05dd',
    dt_resp_sites: '\u05d0\u05ea\u05e8\u05d9\u05dd',
    dt_resp_alreadyOn: '\u05db\u05d1\u05e8 \u05e2\u05dc',
    dt_resp_group: '\u05e7\u05d1\u05d5\u05e6\u05d4',
    dt_resp_groupNote: '\u05d7\u05d9\u05d1\u05d5\u05e8 \u05e7\u05d1\u05d5\u05e6\u05d4 \u05d4\u05d5\u05d0 \u05e9\u05d5\u05e8\u05d4 \u05d0\u05d7\u05ea \u05dc\u05ea\u05de\u05d9\u05d3 \u2014 \u05de\u05d9 \u05e9\u05de\u05e6\u05d8\u05e8\u05e3 \u05d0\u05d5 \u05e2\u05d5\u05d6\u05d1 \u05d9\u05d5\u05e8\u05e9 \u05d6\u05d0\u05ea \u05de\u05d9\u05d3, \u05d5\u05d0\u05d9\u05df \u05e6\u05d5\u05e8\u05da \u05d1\u05e0\u05d9\u05e7\u05d5\u05d9 \u05e4\u05e8\u05d8\u05e0\u05d9.',
    dt_resp_roleLabel: '\u05ea\u05e4\u05e7\u05d9\u05d3 \u2014 \u05de\u05d4 \u05de\u05d5\u05ea\u05e8 \u05dc\u05d4\u05dd \u05dc\u05e2\u05e9\u05d5\u05ea',
    dt_resp_roleNone: '\u2014 \u05dc\u05dc\u05d0 \u05ea\u05e4\u05e7\u05d9\u05d3 (\u05d4\u05d0\u05d7\u05e8\u05d9\u05d5\u05ea \u05d1\u05dc\u05d1\u05d3) \u2014',
    dt_resp_roleHint: '\u05d4\u05ea\u05e4\u05e7\u05d9\u05d3 \u05e0\u05e4\u05e8\u05d3 \u05de\u05d4\u05de\u05d5\u05e7\u05d3. \u05d4\u05ea\u05e4\u05e7\u05d9\u05d3 \u05d0\u05d5\u05de\u05e8 \u05de\u05d4; \u05d4\u05de\u05d5\u05e7\u05d3 \u05d0\u05d5\u05de\u05e8 \u05e2\u05dc \u05de\u05d4.',
    dt_resp_dutyLabel: '\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u2014 \u05e2\u05dc \u05de\u05d4 \u05d4\u05dd \u05e2\u05d5\u05e0\u05d9\u05dd',
    dt_resp_duty: '\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea',
    dt_resp_duty_owner: '\u05d1\u05e2\u05dc\u05d9\u05dd',
    dt_resp_duty_responder: '\u05de\u05d2\u05d9\u05d1',
    dt_resp_duty_watcher: '\u05e6\u05d5\u05e4\u05d4',
    dt_resp_dutyHint: '\u05d1\u05e2\u05dc\u05d9\u05dd \u05e2\u05d5\u05e0\u05d4 \u05e2\u05dc \u05db\u05da \u00b7 \u05de\u05d2\u05d9\u05d1 \u05de\u05e7\u05d1\u05dc \u05d0\u05ea \u05d4\u05e7\u05e8\u05d9\u05d0\u05d5\u05ea \u00b7 \u05e6\u05d5\u05e4\u05d4 \u05e8\u05e7 \u05de\u05e7\u05d1\u05dc \u05d4\u05ea\u05e8\u05d0\u05d5\u05ea.',
    dt_resp_systemSubtree: '\u05ea\u05ea-\u05e2\u05e5 \u05de\u05e2\u05e8\u05db\u05ea',
    dt_resp_roomScope: '\u05d7\u05d3\u05e8',
    dt_resp_pickBoth: '\u05d1\u05d7\u05e8\u05d5 \u05de\u05d5\u05e7\u05d3 \u05d5\u05d7\u05d1\u05e8 \u05db\u05d3\u05d9 \u05dc\u05e8\u05d0\u05d5\u05ea \u05de\u05d4 \u05d9\u05d7\u05d5\u05d1\u05e8.',
    dt_resp_impliesReadA: '\u05d7\u05dc \u05e2\u05dc',
    dt_resp_impliesReadAssets: '\u05e0\u05db\u05e1\u05d9\u05dd \u05ea\u05d7\u05ea \u05d4\u05de\u05d5\u05e7\u05d3 \u05d4\u05d6\u05d4',
    dt_resp_impliesReadB: '\u2014 \u05d0\u05e4\u05e9\u05e8 \u05dc\u05d1\u05d7\u05d5\u05e8 \u05e8\u05e7 \u05d0\u05e0\u05e9\u05d9\u05dd \u05d5\u05e7\u05d1\u05d5\u05e6\u05d5\u05ea \u05e9\u05db\u05d1\u05e8 \u05d1\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4, \u05db\u05da \u05e9\u05db\u05dc \u05d0\u05d7\u05e8\u05d0\u05d9 \u05d9\u05db\u05d5\u05dc \u05dc\u05e4\u05ea\u05d5\u05d7 \u05d0\u05ea \u05de\u05d4 \u05e9\u05d4\u05d5\u05d0 \u05d0\u05d7\u05e8\u05d0\u05d9 \u05e2\u05dc\u05d9\u05d5.',
    dt_resp_cancel: '\u05d1\u05d9\u05d8\u05d5\u05dc',
    dt_resp_attach: '\u05d7\u05d9\u05d1\u05d5\u05e8',
    dt_resp_siteAdminOnly: '\u05de\u05e0\u05d4\u05dc\u05d9 \u05d0\u05ea\u05e8 \u05de\u05e0\u05d4\u05dc\u05d9\u05dd \u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u05d1\u05d0\u05ea\u05e8 \u05e9\u05dc\u05d4\u05dd \u05d1\u05dc\u05d1\u05d3.',
    dt_resp_alreadyOnScope: '\u05db\u05d1\u05e8 \u05e2\u05dc \u05d4\u05de\u05d5\u05e7\u05d3 \u05d4\u05d6\u05d4',
    dt_resp_pickScopeFirst: '\u05d1\u05d7\u05e8\u05d5 \u05de\u05d5\u05e7\u05d3 \u05db\u05d3\u05d9 \u05dc\u05e8\u05d0\u05d5\u05ea \u05de\u05d9 \u05db\u05d1\u05e8 \u05e2\u05dc\u05d9\u05d5.',
    dt_resp_nobodyHere: '\u05d0\u05d9\u05e9 \u05d0\u05d9\u05e0\u05d5 \u05de\u05d7\u05d5\u05d1\u05e8 \u05dc\u05de\u05d5\u05e7\u05d3 \u05d4\u05d6\u05d4.',
    dt_resp_membership: '\u05d7\u05d1\u05e8\u05d5\u05ea',
    dt_resp_member_user: '\u05de\u05e9\u05ea\u05de\u05e9',
    dt_resp_member_group: '\u05e7\u05d1\u05d5\u05e6\u05d4',
    dt_resp_noRole: '\u05dc\u05dc\u05d0 \u05ea\u05e4\u05e7\u05d9\u05d3',
    dt_resp_by: '\u05e2\u05dc \u05d9\u05d3\u05d9',
    dt_resp_revoke: '\u05d1\u05d9\u05d8\u05d5\u05dc',
    dt_resp_membershipNote: '\u05d7\u05d1\u05e8\u05d5\u05ea \u05e4\u05e9\u05d5\u05d8\u05d4 \u05db\u05d1\u05e8 \u05de\u05e9\u05de\u05e2\u05d4 \u05e8\u05d5\u05d0\u05d9\u05dd \u05d5\u05de\u05e7\u05d1\u05dc\u05d9\u05dd \u05d4\u05ea\u05e8\u05d0\u05d5\u05ea. \u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u05d4\u05d9\u05d0 \u05d4\u05de\u05e9\u05e4\u05d8 \u05d4\u05e0\u05d5\u05e1\u05e3: \u05de\u05d9\u05e9\u05d4\u05d5 \u05e2\u05d5\u05e0\u05d4 \u05e2\u05dc \u05db\u05da.',
    dt_resp_threeRules: '\u05e9\u05dc\u05d5\u05e9\u05ea \u05d4\u05db\u05dc\u05dc\u05d9\u05dd',
    dt_resp_rule1: '\u05e7\u05d1\u05d5\u05e6\u05d4 \u05d4\u05d9\u05d0 \u05d0\u05e0\u05e9\u05d9\u05dd + \u05d3\u05d1\u05e8\u05d9\u05dd',
    dt_resp_rule1d: '\u05e8\u05e9\u05d9\u05de\u05d4 \u05d0\u05d7\u05ea \u05e9\u05dc \u05d0\u05e0\u05e9\u05d9\u05dd, \u05e8\u05e9\u05d9\u05de\u05d4 \u05d0\u05d7\u05ea \u05e9\u05dc \u05de\u05d4 \u05e9\u05d4\u05dd \u05de\u05d7\u05d5\u05d1\u05e8\u05d9\u05dd \u05d0\u05dc\u05d9\u05d5. \u05dc\u05dc\u05d0 \u05e7\u05d1\u05d5\u05e6\u05d5\u05ea \u05de\u05e7\u05d5\u05e0\u05e0\u05d5\u05ea.',
    dt_resp_rule2: '\u05ea\u05e4\u05e7\u05d9\u05d3 \u2260 \u05de\u05d5\u05e7\u05d3',
    dt_resp_rule2d: '\u05d4\u05ea\u05e4\u05e7\u05d9\u05d3 \u05d0\u05d5\u05de\u05e8 \u05de\u05d4 \u05de\u05d5\u05ea\u05e8 \u05dc\u05e2\u05e9\u05d5\u05ea; \u05d4\u05de\u05d5\u05e7\u05d3 \u05d0\u05d5\u05de\u05e8 \u05e2\u05dc \u05de\u05d4. \u05d4\u05d4\u05e8\u05e9\u05d0\u05d4 \u05d4\u05d9\u05d0 \u05d4\u05d7\u05d9\u05ea\u05d5\u05da.',
    dt_resp_rule3: '\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u05de\u05e7\u05e0\u05d4 \u05e7\u05e8\u05d9\u05d0\u05d4',
    dt_resp_rule3d: '\u05d0\u05d7\u05e8\u05d0\u05d9\u05d5\u05ea \u05e2\u05dc \u05de\u05e9\u05d4\u05d5 \u05de\u05e7\u05e0\u05d4 \u05e7\u05e8\u05d9\u05d0\u05d4 \u05e2\u05dc\u05d9\u05d5 \u2014 \u05db\u05da \u05e9\u05f4\u05d0\u05d7\u05e8\u05d0\u05d9 \u05d0\u05d1\u05dc \u05dc\u05dc\u05d0 \u05d2\u05d9\u05e9\u05d4\u05f4 \u05d4\u05d5\u05e4\u05da \u05dc\u05d1\u05dc\u05ea\u05d9 \u05d0\u05e4\u05e9\u05e8\u05d9.',
    dt_resp_rowNote: '\u05db\u05dc \u05d7\u05d9\u05d1\u05d5\u05e8 \u05db\u05d0\u05df \u05d4\u05d5\u05d0 \u05e9\u05d5\u05e8\u05d4 \u05d0\u05d7\u05ea \u05e2\u05dd \u05de\u05e7\u05d5\u05e8 \u05de\u05d5\u05d2\u05d3\u05e8. \u05d1\u05d9\u05d8\u05d5\u05dc \u05de\u05d5\u05d7\u05e7 \u05d0\u05ea \u05d4\u05e9\u05d5\u05e8\u05d4; \u05d4\u05d5\u05d0 \u05dc\u05e2\u05d5\u05dc\u05dd \u05d0\u05d9\u05e0\u05d5 \u05db\u05d5\u05ea\u05d1 \u05de\u05e6\u05d1\u05d4 \u05d0\u05d5 \u05d4\u05e2\u05e8\u05d4.',

    // Planning objects (source request §7)
    planningObjectsTitle: '\u05d0\u05d5\u05d1\u05d9\u05d9\u05e7\u05d8\u05d9 \u05ea\u05db\u05e0\u05d5\u05df',
    planningObjectsLayer: '\u05d0\u05d5\u05d1\u05d9\u05d9\u05e7\u05d8\u05d9 \u05ea\u05db\u05e0\u05d5\u05df',
    planningObjectAdd: '+ \u05d4\u05d5\u05e1\u05e4\u05ea \u05d0\u05d5\u05d1\u05d9\u05d9\u05e7\u05d8',
    planningObjectDefaultName: '\u05d0\u05d5\u05d1\u05d9\u05d9\u05e7\u05d8 \u05d7\u05d3\u05e9',
    planningObjectName: '\u05e9\u05dd',
    planningObjectNamePlaceholder: '\u05de\u05db\u05d5\u05e0\u05ea \u05db\u05d1\u05d9\u05e1\u05d4',
    planningObjectShape: '\u05e6\u05d5\u05e8\u05d4',
    planningObjectShapeBox: '\u05ea\u05d9\u05d1\u05d4',
    planningObjectShapeCylinder: '\u05d2\u05dc\u05d9\u05dc',
    planningObjectShapeExtrusion: '\u05e6\u05d9\u05d5\u05e8 \u05e6\u05d5\u05e8\u05d4',
    planningObjectDraw: '\u05e6\u05d9\u05d5\u05e8',
    planningObjectDrawHint: '\u05dc\u05d7\u05e5 \u05e2\u05dc \u05e4\u05d9\u05e0\u05d5\u05ea \u05d4\u05e6\u05d5\u05e8\u05d4 \u05e2\u05dc \u05d4\u05e8\u05e6\u05e4\u05d4, \u05d5\u05dc\u05e1\u05d9\u05d5\u05dd \u05dc\u05d7\u05e5 \u05e9\u05d5\u05d1 \u05e2\u05dc \u05d4\u05e4\u05d9\u05e0\u05d4 \u05d4\u05e8\u05d0\u05e9\u05d5\u05e0\u05d4',
    planningObjectSize: '\u05de\u05d9\u05d3\u05d5\u05ea (\u05e1\u05f4\u05de)',
    planningObjectWidth: '\u05e8\u05d5\u05d7\u05d1',
    planningObjectDepth: '\u05e2\u05d5\u05de\u05e7',
    planningObjectDiameter: '\u05e7\u05d5\u05d8\u05e8',
    planningObjectHeight: '\u05d2\u05d5\u05d1\u05d4',
    planningObjectRotation: '\u05e1\u05d9\u05d1\u05d5\u05d1 (\u00b0)',
    planningObjectFloor: '\u05e7\u05d5\u05de\u05d4',
    planningObjectAboveFloor: '\u05d2\u05d5\u05d1\u05d4 \u05de\u05e2\u05dc \u05d4\u05e8\u05e6\u05e4\u05d4 (\u05e1\u05f4\u05de)',
    planningObjectPlace: '\u05de\u05e7\u05dd',
    planningObjectPlaceHint: '\u05dc\u05d7\u05e5 \u05e2\u05dc \u05e8\u05e6\u05e4\u05d4 \u05d1\u05de\u05d5\u05d3\u05dc \u05db\u05d3\u05d9 \u05dc\u05de\u05e7\u05dd \u05d0\u05d5\u05ea\u05d5',
    planningObjectsEmpty: '\u05e2\u05d3\u05d9\u05d9\u05df \u05dc\u05d0 \u05de\u05d5\u05e7\u05dd \u05d3\u05d1\u05e8. \u05d4\u05d5\u05e1\u05e3 \u05d0\u05d5\u05d1\u05d9\u05d9\u05e7\u05d8 \u05d1\u05de\u05d9\u05d3\u05d5\u05ea \u05e9\u05dc \u05d4\u05e6\u05d9\u05d5\u05d3 \u05e9\u05d0\u05ea\u05d4 \u05de\u05ea\u05db\u05e0\u05df, \u05de\u05e7\u05dd \u05d0\u05d5\u05ea\u05d5 \u05d1\u05de\u05d5\u05d3\u05dc, \u05d5\u05d4\u05e4\u05d0\u05e0\u05dc \u05d9\u05d5\u05d3\u05d9\u05e2 \u05db\u05de\u05d4 \u05de\u05e7\u05d5\u05dd \u05e0\u05e9\u05d0\u05e8 \u05e1\u05d1\u05d9\u05d1\u05d5.',
    planningObjectOverlaps: '\u05d7\u05d5\u05e4\u05e3 \u05d0\u05ea \u05d4\u05de\u05d5\u05d3\u05dc \u2014 \u05d4\u05d5\u05d0 \u05dc\u05d0 \u05e0\u05db\u05e0\u05e1 \u05db\u05d0\u05df',
    planningObjectClearance: '\u05de\u05e8\u05d5\u05d5\u05d7\u05d9\u05dd',
    planningObjectSide_right: '\u05d9\u05de\u05d9\u05df',
    planningObjectSide_left: '\u05e9\u05de\u05d0\u05dc',
    planningObjectSide_front: '\u05d7\u05d6\u05d9\u05ea',
    planningObjectSide_back: '\u05d0\u05d7\u05d5\u05e8',
    planningObjectSide_above: '\u05de\u05e2\u05dc',

    // Site plans (source request §5)
    sitePlansTitle: '\u05ea\u05db\u05e0\u05d9\u05d5\u05ea',
    sitePlansLayer: '\u05ea\u05db\u05e0\u05d9\u05d5\u05ea',
    sitePlanUpload: "העלאת תכנית",
    sitePlanChooseExisting: "בחירת קובץ קיים באתר",
    sitePlanUploading: "מעלה תכנית…",
    sitePlanAdding: "מוסיף תכנית…",
    sitePlanRetryAdd: "ניסיון חוזר להוספת התכנית שהועלתה",
    sitePlanKeepOpen: "יש להשאיר את החלונית פתוחה עד להוספת התכנית.",
    sitePlanLoadError: "לא ניתן לטעון את הקבצים. נסו שוב.",
    sitePlanWaitModel: "יש להמתין לטעינת המודל לפני הוספת תכנית כתמונה.",
    sitePlanCreateError: "לא ניתן להוסיף את התכנית. הקובץ נשמר בקובצי האתר; נסו שוב.",
    sitePlanUnsupported: "יש לבחור קובץ שרטוט או תמונה בפורמט נתמך.",
    sitePlanUploadError: "לא ניתן להשלים את הוספת התכנית. נסו שוב.",
    sitePlanNoExistingFiles: "אין עדיין קבצים מתאימים באתר. ניתן להעלות תכנית למעלה.",
    sitePlanQueued: "בתור",
    sitePlanStatusFailed: "נכשל",
    sitePlanAdd: '+ \u05d4\u05d5\u05e1\u05e4\u05ea \u05ea\u05db\u05e0\u05d9\u05ea',
    sitePlanPickFile: '\u05d1\u05d7\u05e8 \u05e9\u05e8\u05d8\u05d5\u05d8',
    sitePlanNoCandidates: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05e9\u05e8\u05d8\u05d5\u05d8\u05d9\u05dd \u05de\u05ea\u05d0\u05d9\u05de\u05d9\u05dd \u05d1\u05d0\u05ea\u05e8 \u05d4\u05d6\u05d4. \u05d9\u05e9 \u05dc\u05d4\u05e2\u05dc\u05d5\u05ea \u05ea\u05d7\u05d9\u05dc\u05d4 DWG, DXF, PDF \u05d0\u05d5 \u05ea\u05de\u05d5\u05e0\u05d4.',
    sitePlansEmpty: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05ea\u05db\u05e0\u05d9\u05d5\u05ea. \u05d4\u05d5\u05e1\u05e3 \u05e9\u05e8\u05d8\u05d5\u05d8 \u05d5\u05d4\u05d5\u05d0 \u05d9\u05d5\u05d8\u05dc \u05e2\u05dc \u05d4\u05de\u05d5\u05d3\u05dc \u2014 \u05e7\u05d5\u05d1\u05e5 DWG \u05e2\u05dd \u05e7\u05d5\u05d0\u05d5\u05e8\u05d3\u05d9\u05e0\u05d8\u05d5\u05ea \u05e0\u05d7\u05ea \u05d1\u05e2\u05e6\u05de\u05d5.',
    sitePlanKind: '\u05de\u05e6\u05d9\u05d2',
    sitePlanElevation: '\u05d2\u05d5\u05d1\u05d4 \u05d1\u05de\u05d5\u05d3\u05dc',
    sitePlanOpacity: '\u05e9\u05e7\u05d9\u05e4\u05d5\u05ea',
    sitePlanVisible: '\u05d4\u05e6\u05d2 \u05ea\u05db\u05e0\u05d9\u05ea \u05d6\u05d5',
    sitePlanRetry: '\u05e0\u05e1\u05d4 \u05e9\u05d5\u05d1',
    sitePlanRemove: '\u05d4\u05e1\u05e8\u05d4',
    sitePlanFailed: '\u05dc\u05d0 \u05e0\u05d9\u05ea\u05df \u05d4\u05d9\u05d4 \u05dc\u05ea\u05e8\u05d2\u05dd \u05d0\u05ea \u05d4\u05e9\u05e8\u05d8\u05d5\u05d8',
    sitePlanClipped: '\u05d9\u05d9\u05ea\u05db\u05df \u05e9\u05d4\u05e9\u05e8\u05d8\u05d5\u05d8 \u05e0\u05d7\u05ea\u05da \u05dc\u05e4\u05d9 \u05d4\u05ea\u05e6\u05d5\u05d2\u05d4 \u05e9\u05e0\u05e9\u05de\u05e8\u05d4 \u05dc\u05d0\u05d7\u05e8\u05d5\u05e0\u05d4. \u05d0\u05dd \u05d7\u05dc\u05e7 \u05de\u05de\u05e0\u05d5 \u05d7\u05e1\u05e8, \u05e9\u05de\u05d5\u05e8 \u05d0\u05d5\u05ea\u05d5 \u05de\u05d7\u05d3\u05e9 \u05d1-CAD \u05e2\u05dd \u05db\u05dc \u05d4\u05ea\u05db\u05e0\u05d9\u05ea \u05d5\u05d4\u05e2\u05dc\u05d4 \u05e9\u05d5\u05d1.',
    sitePlanAlignPlan1: '\u05dc\u05d7\u05e5 \u05e2\u05dc \u05e0\u05e7\u05d5\u05d3\u05d4 \u05d1\u05d5\u05dc\u05d8\u05ea \u05e2\u05dc \u05d4\u05ea\u05db\u05e0\u05d9\u05ea',
    sitePlanAlignModel1: '\u05e2\u05db\u05e9\u05d9\u05d5 \u05dc\u05d7\u05e5 \u05e2\u05dc \u05d0\u05d5\u05ea\u05d4 \u05e0\u05e7\u05d5\u05d3\u05d4 \u05d1\u05de\u05d5\u05d3\u05dc',
    sitePlanAlignPlan2: '\u05dc\u05d7\u05e5 \u05e2\u05dc \u05e0\u05e7\u05d5\u05d3\u05d4 \u05e9\u05e0\u05d9\u05d9\u05d4 \u05d1\u05ea\u05db\u05e0\u05d9\u05ea, \u05e8\u05d7\u05d5\u05e7\u05d4 \u05de\u05d4\u05e8\u05d0\u05e9\u05d5\u05e0\u05d4',
    sitePlanAlignModel2: '\u05d5\u05d0\u05ea \u05d0\u05d5\u05ea\u05d4 \u05e0\u05e7\u05d5\u05d3\u05d4 \u05d1\u05de\u05d5\u05d3\u05dc',
    sitePlanAlignedByPoints: '\u05de\u05d9\u05d5\u05e9\u05e8 \u05dc\u05e4\u05d9 \u05e9\u05ea\u05d9 \u05e0\u05e7\u05d5\u05d3\u05d5\u05ea',
    sitePlanAlignedByFile: '\u05dc\u05e4\u05d9 \u05d4\u05e7\u05d5\u05d0\u05d5\u05e8\u05d3\u05d9\u05e0\u05d8\u05d5\u05ea \u05e9\u05dc \u05d4\u05e9\u05e8\u05d8\u05d5\u05d8',
    sitePlanUseFileCoords: '\u05d4\u05e9\u05ea\u05de\u05e9 \u05d1\u05e7\u05d5\u05d0\u05d5\u05e8\u05d3\u05d9\u05e0\u05d8\u05d5\u05ea \u05e9\u05dc \u05d4\u05e9\u05e8\u05d8\u05d5\u05d8',
    dt_int_kind: '\u05e1\u05d5\u05d2',
    dt_int_kind_rest: '\u05de\u05d7\u05d1\u05e8 CRM / ERP',
    dt_int_kind_lorawan: '\u05e9\u05e8\u05ea LoRaWAN',
    dt_int_kind_bms: '\u05e9\u05e2\u05e8 BMS',
    dt_int_hint_lorawan: '\u05d4\u05e4\u05e0\u05d4 \u05d0\u05ea \u05d4-webhook \u05e9\u05dc \u05e9\u05e8\u05ea \u05d4\u05e8\u05e9\u05ea \u05dc\u05d0\u05d9\u05e0\u05d8\u05d2\u05e8\u05e6\u05d9\u05d4 \u05d4\u05d6\u05d0\u05ea. \u05d4\u05ea\u05e7\u05e0\u05d9\u05dd \u05de\u05d6\u05d5\u05d4\u05d9\u05dd \u05dc\u05e4\u05d9 DevEUI, \u05d5\u05dc\u05db\u05df \u05dc\u05db\u05dc \u05d4\u05ea\u05e7\u05df \u05d9\u05e9 \u05dc\u05d4\u05d2\u05d3\u05d9\u05e8 \u05d0\u05ea \u05d4-DevEUI \u05db\u05de\u05e4\u05ea\u05d7 \u05d7\u05d9\u05e6\u05d5\u05e0\u05d9. \u05e4\u05e2\u05e0\u05d5\u05d7 \u05d4-payload \u05d7\u05d9\u05d9\u05d1 \u05dc\u05d4\u05d9\u05e2\u05e9\u05d5\u05ea \u05d1\u05e9\u05e8\u05ea \u05d4\u05e8\u05e9\u05ea.',
    dt_int_hint_bms: '\u05d4\u05e4\u05e0\u05d4 \u05d0\u05ea \u05e9\u05e2\u05e8 \u05d4-BMS \u05dc\u05d0\u05d9\u05e0\u05d8\u05d2\u05e8\u05e6\u05d9\u05d4 \u05d4\u05d6\u05d0\u05ea. \u05e2\u05dc\u05d9\u05d5 \u05dc\u05e9\u05dc\u05d5\u05d7 POST \u05e2\u05dd { points: [{ id, value }] }, \u05db\u05d0\u05e9\u05e8 id \u05ea\u05d5\u05d0\u05dd \u05dc\u05de\u05e4\u05ea\u05d7 \u05d4\u05d7\u05d9\u05e6\u05d5\u05e0\u05d9 \u05e9\u05dc \u05d4\u05d4\u05ea\u05e7\u05df.',
    dt_int_deliveries: '\u05de\u05e9\u05dc\u05d5\u05d7\u05d9\u05dd',
    dt_int_noDeliveries: '\u05e2\u05d3\u05d9\u05d9\u05df \u05dc\u05d0 \u05e0\u05e9\u05dc\u05d7 \u05d3\u05d1\u05e8.',
    dt_int_attempts: '\u05e0\u05d9\u05e1\u05d9\u05d5\u05e0\u05d5\u05ea',
    dt_int_resend: '\u05e9\u05dc\u05d7 \u05e9\u05d5\u05d1',
    dt_dev_adapter: '\u05de\u05d3\u05d5\u05d5\u05d7 \u05d3\u05e8\u05da',
    dt_dev_adapter_generic: '\u05d9\u05e9\u05d9\u05e8 (\u05d8\u05d5\u05e7\u05df \u05de\u05e9\u05dc\u05d5)',
    dt_dev_adapter_lorawan: '\u05e9\u05e8\u05ea LoRaWAN',
    dt_dev_adapter_bms: '\u05e9\u05e2\u05e8 BMS',
    dt_dev_devEui: 'DevEUI',
    dt_dev_pointId: '\u05de\u05d6\u05d4\u05d4 \u05e0\u05e7\u05d5\u05d3\u05ea BMS',
    dt_dev_extKeyRequired: '\u05d1\u05dc\u05e2\u05d3\u05d9\u05d5, \u05e9\u05d9\u05d3\u05d5\u05e8 \u05dc\u05d0 \u05d9\u05de\u05e6\u05d0 \u05d0\u05ea \u05d4\u05d4\u05ea\u05e7\u05df \u05d4\u05d6\u05d4 \u2014 \u05d4\u05d5\u05d0 \u05e0\u05e9\u05d0\u05e8 \u05d1\u05de\u05e6\u05d1 \u05d9\u05e9\u05d9\u05e8.',
    systemsTreeTitle: '\u05e2\u05e5 \u05d4\u05de\u05e2\u05e8\u05db\u05d5\u05ea',
    systemsTreeHint: '\u05e7\u05d1\u05e5 \u05de\u05e2\u05e8\u05db\u05d5\u05ea \u05ea\u05d7\u05ea \u05d0\u05d1 \u2014 \u05e0\u05db\u05e1 \u05d1\u05de\u05e2\u05e8\u05db\u05ea \u05d1\u05ea \u05e0\u05de\u05e6\u05d0 \u05d2\u05dd \u05d1\u05db\u05dc \u05de\u05d4 \u05e9\u05de\u05e2\u05dc\u05d9\u05d4, \u05d5\u05d4\u05d0\u05d7\u05e8\u05d0\u05d9 \u05e2\u05dc \u05d0\u05d1 \u05d0\u05d7\u05e8\u05d0\u05d9 \u05e2\u05dc \u05de\u05d4 \u05e9\u05de\u05ea\u05d7\u05ea\u05d9\u05d5.',
    systemsTreeEmpty: '\u05d0\u05d9\u05df \u05e2\u05d3\u05d9\u05d9\u05df \u05de\u05e2\u05e8\u05db\u05d5\u05ea. \u05d4\u05d5\u05e1\u05e3 \u05de\u05e2\u05dc\u05d4, \u05d0\u05d5 \u05d2\u05d6\u05d5\u05e8 \u05d0\u05d5\u05ea\u05df \u05de\u05de\u05d0\u05e4\u05d9\u05d9\u05e0\u05d9 \u05d4\u05de\u05d5\u05d3\u05dc \u05dc\u05de\u05d8\u05d4.',
    systemsNewName: '\u05e9\u05dd \u05de\u05e2\u05e8\u05db\u05ea \u05d7\u05d3\u05e9\u05d4',
    systemsNoParent: '\u05e8\u05de\u05d4 \u05e2\u05dc\u05d9\u05d5\u05e0\u05d4',
    systemsAdd: '\u05d4\u05d5\u05e1\u05e3',
    systemsRename: '\u05e9\u05e0\u05d4 \u05e9\u05dd',
    systemsParent: '\u05d0\u05d1',
    systemsDerived: '\u05e0\u05d2\u05d6\u05e8',
    systemsDerivedHint: '\u05e0\u05d5\u05e6\u05e8 \u05d1\u05d2\u05d6\u05d9\u05e8\u05d4 \u05de\u05de\u05d0\u05e4\u05d9\u05d9\u05e0\u05d9\u05dd \u2014 \u05d4\u05e8\u05e6\u05d4 \u05d7\u05d5\u05d6\u05e8\u05ea \u05e2\u05e9\u05d5\u05d9\u05d4 \u05dc\u05d4\u05d7\u05dc\u05d9\u05e3 \u05d0\u05d5\u05ea\u05d5',
    systemsCountHint: '\u05e0\u05db\u05e1\u05d9\u05dd \u05d1\u05de\u05e2\u05e8\u05db\u05ea \u05d4\u05d6\u05d0\u05ea \u05d0\u05d5 \u05de\u05ea\u05d7\u05ea\u05d9\u05d4',
    systemsDeleteBlocked: '\u05e2\u05d3\u05d9\u05d9\u05df \u05d9\u05e9 \u05e0\u05db\u05e1\u05d9\u05dd \u05d1\u05de\u05e2\u05e8\u05db\u05ea \u05d4\u05d6\u05d0\u05ea',
    systemsPreviewTitle: '\u05d2\u05d6\u05d5\u05e8 \u05de\u05de\u05d0\u05e4\u05d9\u05d9\u05e0\u05d9 \u05d4\u05de\u05d5\u05d3\u05dc',
    systemsPreview: '\u05ea\u05e6\u05d5\u05d2\u05d4 \u05de\u05e7\u05d3\u05d9\u05de\u05d4',
    systemsPreviewing: '\u05d1\u05d5\u05d3\u05e7\u2026',
    systemsPickPropertyFirst: '\u05d1\u05d7\u05e8 \u05ea\u05d7\u05d9\u05dc\u05d4 \u05de\u05d0\u05e4\u05d9\u05d9\u05df \u05de\u05e2\u05e8\u05db\u05ea',
    systemsWouldCreate: '\u05de\u05e2\u05e8\u05db\u05d5\u05ea',
    systemsMaxDepth: '\u05e2\u05d5\u05de\u05e7',
    systemsNoSystem: '\u05e0\u05db\u05e1\u05d9\u05dd \u05dc\u05dc\u05d0 \u05de\u05e2\u05e8\u05db\u05ea',
    systemsTooManyWarning: '\u05d6\u05d4 \u05d4\u05e8\u05d1\u05d4 \u05de\u05e2\u05e8\u05db\u05d5\u05ea \u05dc\u05d0\u05ea\u05e8 \u05d0\u05d7\u05d3. \u05e2\u05e8\u05db\u05d9\u05dd \u05db\u05de\u05d5 \u05e9\u05de\u05d5\u05ea \u05de\u05e9\u05e4\u05d7\u05d5\u05ea Revit \u05de\u05d9\u05d9\u05e6\u05e8\u05d9\u05dd \u05de\u05e2\u05e8\u05db\u05ea \u05dc\u05db\u05dc \u05e1\u05d5\u05d2 \u05d0\u05dc\u05de\u05e0\u05d8 \u05d5\u05dc\u05d0 \u05e7\u05d1\u05d5\u05e6\u05d5\u05ea \u05d0\u05de\u05d9\u05ea\u05d9\u05d5\u05ea \u2014 \u05d1\u05d3\u05d5\u05e7 \u05d0\u05ea \u05d4\u05e9\u05de\u05d5\u05ea \u05dc\u05de\u05d8\u05d4 \u05dc\u05e4\u05e0\u05d9 \u05d4\u05d7\u05dc\u05d4.',
    systemsApplyDerivation: '\u05d4\u05d7\u05dc \u05d0\u05ea \u05d6\u05d4',
    dt_ct_findAsset: '\u05d7\u05e4\u05e9 \u05e0\u05db\u05e1\u05d9\u05dd \u05dc\u05e6\u05e8\u05d5\u05e3\u2026',
    dt_ct_noAssetMatch: '\u05d0\u05d9\u05df \u05e0\u05db\u05e1 \u05ea\u05d5\u05d0\u05dd',
    dt_ct_noAssetsYet: 'לא צורף נכס. קריאה יכולה לעסוק גם בחדר או במערכת שלמה — הוסף אחת למטה.',
    // שיוך קריאה, קבצים ו-SLA
    dt_ct_scope: 'או חדר / מערכת שלמה',
    dt_ct_findScope: 'חיפוש חדרים ומערכות…',
    dt_ct_noScopeMatch: 'לא נמצא חדר או מערכת תואמים',
    dt_ct_removeScope: 'הסרה',
    dt_tickets_aboutScope: 'הקריאה עוסקת ב',
    dt_tickets_detachScope: 'ביטול שיוך',
    dt_ev_scopeLinked: 'שויך',
    dt_ev_scopeUnlinked: 'בוטל שיוך',
    dt_tickets_attachments: 'תמונות וקבצים',
    dt_tickets_uploading: 'מעלה…',
    dt_tickets_addPhoto: '+ הוספת תמונה או קובץ',
    dt_tickets_noAttachments: 'אין עדיין תמונות או קבצים — תמונה של התקלה היא לרוב הדיווח המהיר ביותר.',
    dt_tickets_removeAttachment: 'הסרה',
    dt_tickets_download: 'הורדה',
    dt_ev_attachmentAdded: 'צורף קובץ',
    dt_ev_attachmentRemoved: 'קובץ הוסר',
    dt_tickets_overdue: 'באיחור',
    dt_tickets_slaDue: 'דורש הסלמה',
    // קריאות — התאמה לשפת העיצוב (2026-09-25)
    dt_tickets_newTicket: 'קריאה חדשה',
    dt_tickets_removeFilter: 'הסרת מסנן',
    // The range is isolated left-to-right (U+2066…U+2069) so "1–8" never reads as "8–1".
    dt_tickets_range: '\u2066{from}–{to}\u2069 מתוך {total}',
    dt_tickets_previousPage: 'העמוד הקודם',
    dt_tickets_nextPage: 'העמוד הבא',
    dt_tickets_previousTicket: 'הקריאה הקודמת',
    dt_tickets_nextTicket: 'הקריאה הבאה',
    dt_tickets_created: 'נוצרה',
    dt_tickets_doneEditing: 'סיום',
    // לוח כיסוי אחריות
    dt_cov_title: 'כיסוי אחריות',
    dt_cov_subtitle: 'מה שאין לו אחראי',
    dt_cov_selectSite: 'בחר אתר כדי לראות כיסוי.',
    dt_cov_loadFailed: 'לא ניתן לטעון את נתוני הכיסוי לאתר זה.',
    dt_cov_wholeSubtree: 'כל תת-העץ',
    dt_cov_assets: 'נכסים',
    dt_cov_openTicket: 'קריאה פתוחה',
    dt_cov_systemsWithOwner: 'מערכות עם אחראי',
    dt_cov_subtreesUncovered: 'תת-עצים ללא כיסוי',
    dt_cov_roomsWithOwner: 'חדרים עם אחראי',
    dt_cov_roomsUncovered: 'חדרים ללא כיסוי',
    dt_cov_uncoveredWithTickets: 'ללא כיסוי עם קריאות פתוחות',
    dt_cov_nobodyOnHook: 'אין אף אחד אחראי לאלה',
    dt_cov_peopleWithDuty: 'אנשים וקבוצות עם אחריות',
    dt_cov_users: 'משתמשים',
    dt_cov_groups: 'קבוצות',
    dt_cov_systemsNoOwner: 'מערכות ללא אחראי',
    dt_cov_allSystemsCovered: 'לכל מערכת יש אחראי',
    dt_cov_roomsNoOwner: 'חדרים ללא אחראי',
    dt_cov_allRoomsCovered: 'לכל חדר יש אחראי',
    dt_cov_byTopLevelSystem: 'כיסוי לפי מערכת ראשית',
    dt_cov_owner: 'אחראי',
    dt_cov_noOwner: 'ללא אחראי',
    dt_cov_howComputed: 'איך זה מחושב',
    dt_cov_howComputedBody: 'שאילתה אחת על',
    dt_cov_fullyCovered: 'כיסוי מלא',
    dt_cov_everythingCovered: 'לכל מערכת וחדר יש אחראי',
    dt_cov_systems: 'מערכות',
    dt_cov_rooms: 'חדרים',
    dt_cov_nothingUncovered: 'אין פריטים ללא כיסוי. בדוק שוב אחרי סנכרון המודל הבא — חדרים חדשים מגיעים ללא כיסוי.',
    dt_cov_notFullyCovered: 'הכיסוי עדיין אינו מלא — ראה את הרשימות למעלה.',
    // חסרו עוד לפני השינויים האלה
    assignLocations: 'שיוך מיקומים',
    assetNotAssigned: 'לא משויך',
    assetLocate: 'איתור בתלת־ממד',
    assetLoadModel: 'טעינת מודל',
    assetManage: 'ניהול',
    assetSelectResults: 'בחירת כל התוצאות',
    assetFind: 'איתור נכסים',
    assetFindHint: 'בחרו היכן ומה לחפש. אפשר לבחור כמה אפשרויות.',
    assetWhere: 'איפה?',
    assetWhat: 'מה?',
    assetSearchPlaces: 'חיפוש בניינים, קומות או חדרים',
    assetSearchEquipment: 'חיפוש ציוד או מערכות',
    assetChooseRooms: 'בחירת חדרים',
    assetMoreOptions: 'אפשרויות נוספות',
    assetChooseProperty: 'בחירת מאפיין',
    assetCustomRules: 'כללים מותאמים',
    assetCustomRulesHint: 'לחיפוש מפורט לפי מאפיינים. הכללים מצמצמים גם את האפשרויות למעלה.',
    assetNoChoices: 'אין אפשרויות מתאימות',
    assetMatches: 'נכסים מתאימים',
    assetAll: 'כל הנכסים',
    assetOr: 'או',
    assetResetChoices: 'איפוס בחירות',
    assetShowResults: 'הצגת תוצאות',
    assetFacet_building: 'בניין',
    assetFacet_level: 'קומה',
    assetFacet_room_name: 'חדר',
    assetFacet_category: 'סוג ציוד',
    assetFacet_system_category: 'מערכת',
    assetFacet_source_file: 'מודל',
    assetExport: 'ייצוא',
    assetExportResults: 'נכסים מתאימים',
    assetExportSelected: 'נכסים שנבחרו',
    assetExpand: 'הרחבה',
    assetRestore: 'שחזור גודל',
    assetWithoutRoom: 'נכסים ללא שיוך לחדר',
    assetInScene: 'בבחירת התצוגה',
    assetFollowScene: 'מעקב אחר הבחירה בתצוגה',
    assetNotAssetYet: 'עדיין לא נכס',
    assetSceneElement: 'אלמנט',
    assetCreating: 'יוצר…',
    assetSceneMore: '+{count} נוספים',
    assetSceneFewer: 'הצגת פחות',
    assetClearFilters: 'ניקוי מסננים',
    assetRemoveFilter: 'הסרת מסנן',
    assetOutsideResults: 'נבחרו מחוץ לתוצאות אלו',
    assetClearSelection: 'ניקוי בחירה',
    assetSelectPage: 'בחירת עמוד זה',
    assetSelect: 'בחירת נכס',
    assetDetails: 'פרטי נכס',
    assetNoResults: 'אין נכסים שמתאימים לבחירה.',
    assetNoAssets: 'עדיין אין נכסים באתר זה.',
    assetResetSearch: 'איפוס חיפוש',
    assetPage: 'עמוד',
    assetResize: 'שינוי גודל חלונית הנכסים',
    assetDevices: 'מכשירים מקושרים',
    assetNoDevices: 'אין מכשירים מקושרים',
    assetDevicesError: 'לא ניתן לטעון מכשירים מקושרים',
    assetAddDevice: 'הוספת מכשיר',
    assetNewTicket: 'קריאה חדשה',
    assetViewTickets: 'הצגת קריאות',
    poiAssetsTitle: 'נכסים',
    poiElements: 'אלמנטים',
    poiColumns: 'עמודות',
    poiRoomGapWarning: 'נכסים משויכים לקומות אך לא לחדרים — הדגשת חדר, מסננים וספירות לא יכללו אותם.',
    noLocationsHint: 'מיקומים נוספים כשמייבאים נכסים ממודל במסך הצפייה.',
    noSystemAssetsHint: 'נכסים נוספים כשמייבאים אותם ממודל במסך הצפייה.',
    poiRoomAssignmentWarning: '{count} מתוך {total} נכסים אינם משויכים לחדרים — תכונות חדר (הדגשה, מסננים, ספירות) לא יכללו אותם. הפעל \"שיוך מיקומים מחדש\" מעמוד המיקומים.',
    dt_cov_howComputedBody2: 'שבה duty = \'owner\', עם מעבר על שרשראות ההורים של המערכות והמיקומים, בדיוק כפי ש-accessSql.js · RESPONSIBLE_GRANTS_SQL עושה עבור נכס בודד. דבר אינו נשמר במטמון — הרשאה חדשה משנה את העמוד בטעינה הבאה.',
  }
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

/** THE direction lever (see the effect in TranslationProvider): which way the page reads. */
function applyDocumentLanguage(lang: Language) {
  document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
}

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('mabat3d-language') as Language;
    return savedLanguage || 'en';
  });

  const setLanguage = useCallback((lang: Language) => {
    // Flip the page NOW, before React re-renders for the new language: the 3D viewer measures
    // where its picture and minimap sit while it re-renders, and with the flip left to the
    // effect below it measured the old side — the minimap landed one app-menu width from its
    // corner (Idan 2026-10-06: "on language change, the minimap sit in the wrong place").
    applyDocumentLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('mabat3d-language', lang);
  }, []);

  // THE direction lever (RTL audit 2026-07-22): direction/lang must live on
  // <html>, not only as inline styles inside the App tree. This single effect
  // is what makes (a) every [dir="rtl"]/[lang="he"] CSS rule in theme.css
  // actually match, (b) runtime checks that read documentElement.dir/lang
  // (e.g. AutodeskViewer's viewer-chrome RTL branches) return the truth, and
  // (c) body-mounted surfaces OUTSIDE the App tree (toasts, portals like
  // ElementInfoPanel) inherit the correct direction. Applies on boot and on
  // every language switch; also covers the embed surface, whose locale is
  // seeded into localStorage before render (src/index.tsx).
  React.useEffect(() => {
    applyDocumentLanguage(currentLanguage);
  }, [currentLanguage]);

  const t = useCallback((key: string, fallback?: string): string => {
    // Fallback support (F-3): callers were already passing a second argument, which a
    // 1-arg signature silently DISCARDED — missing keys rendered as raw key names.
    return translations[currentLanguage][key] || fallback || key;
  }, [currentLanguage]);

  const isRTL = currentLanguage === 'he';

  const value = useMemo(() => ({
    t,
    currentLanguage,
    setLanguage,
    isRTL
  }), [t, currentLanguage, setLanguage, isRTL]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export default TranslationContext;
